import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import { X, TrendingUp, ChevronRight, Loader2, AlertCircle, Calendar } from 'lucide-react';
import {
  getRevenueContext,
  getRevenueInputs,
  saveRevenueInputs,
  saveOverrideV3,
  saveBulkOverridesV3,
  saveRoomStatOverride,
  updateStatAccountMapping,
  getDailyValues,
  saveDailyValues,
  getDailyReference,
  getStatAccountHistory,
  getStatAccountDailyActuals,
} from '../../api';
import type { RevenueContext, RevenueInputsResponse, RevenueContextAccount, ForecastMode, StatAccountInput, StatAccountMapping, DailyValue, DailyReferenceOtb, DailyReferenceRms } from '../../api';
import { DailyEntryModal } from './DailyEntryModal';
import { StatAccountMappingPanel } from './StatAccountMappingPanel';
import { ownerDocumentOf, ownerWindowOf } from '../../utils/ownerWindow';
import { useForecastChartPalette } from '../../utils/chartPalette';
import { RowsSkeleton } from '../shared/Skeleton';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function fmt(n: number): string {
  return n.toLocaleString();
}
function fmtDollar(n: number): string {
  return `$${n.toLocaleString()}`;
}

interface RevenueForecastModalProps {
  isOpen: boolean;
  onClose: () => void;
  department: string;
  departmentId?: number;
  versionId: number;
  locationId: number;
  orgId: number;
  year: number;
  initialMonth?: number;
  isReadOnly?: boolean;
  /** Budget versions swap OTB/RMS references for historical actuals + seeding */
  isBudget?: boolean;
  onApplied: () => void;
  /** Available rooms per month from the PnLGrid occupancy row (keyed by short month name, e.g. 'jan') */
  gridAvailableRooms?: Record<string, number>;
  currency?: string | null;
  /** Open straight on a view — 'accounts' lands on the stat-account mapping (forces stat mode). */
  initialTab?: 'overview' | 'accounts';
}

interface InputState {
  groupRN: number;
  groupADR: number;
  transRN: number;
  transADR: number;
  groupPct: number;
  transPct: number;
}

interface DayData {
  groupRN: number;
  transRN: number;
  groupADR: number;
  transADR: number;
  edited: boolean;
}

type ActiveTab = 'overview' | 'inputs' | 'daily' | 'accounts';
type InputMode = 'rn' | 'pct';
type DailyRef = 'none' | 'ly' | 'otb';
type AcctMethod = 'ly_pct' | 'budget_pct' | 'manual';

interface LyDailyEntry {
  day: number;
  grpRN: number;
  transRN: number;
  grpRevenue: number;
  transRevenue: number;
}

/**
 * Build a day-of-week aligned mapping from forecast month days to LY daily data.
 * "3rd Friday of March 2026" maps to "3rd Friday of March 2025" instead of same calendar date.
 */
function buildDowAlignedLyMap(
  year: number, month: number, lyDaily: LyDailyEntry[]
): Record<number, LyDailyEntry | undefined> {
  const lyYear = year - 1;
  const dim = new Date(year, month, 0).getDate();

  // Build LY lookup by (dow, nthOccurrence)
  const lyByDowNth: Record<string, LyDailyEntry> = {};
  const lyByDow: Record<number, LyDailyEntry[]> = {};
  for (const ld of lyDaily) {
    const lyDate = new Date(lyYear, month - 1, ld.day);
    const dow = lyDate.getDay();
    const nth = Math.ceil(ld.day / 7);
    lyByDowNth[`${dow}_${nth}`] = ld;
    if (!lyByDow[dow]) lyByDow[dow] = [];
    lyByDow[dow].push(ld);
  }

  const mapping: Record<number, LyDailyEntry | undefined> = {};
  for (let d = 1; d <= dim; d++) {
    const date = new Date(year, month - 1, d);
    const dow = date.getDay();
    const nth = Math.ceil(d / 7);
    // Try exact nth match, fallback to last available occurrence of that DOW
    mapping[d] = lyByDowNth[`${dow}_${nth}`]
      ?? (lyByDow[dow] && lyByDow[dow].length > 0 ? lyByDow[dow][lyByDow[dow].length - 1] : undefined);
  }
  return mapping;
}

export function RevenueForecastModal({
  isOpen,
  onClose,
  department,
  departmentId,
  versionId,
  locationId,
  orgId,
  year,
  initialMonth,
  isReadOnly = false,
  isBudget = false,
  onApplied,
  gridAvailableRooms,
  currency = 'USD',
  initialTab = 'overview',
}: RevenueForecastModalProps) {
  // New look: the modal's mini charts follow the chart palette (CSS vars on
  // the root); Classic leaves them unset so the fallbacks render as before.
  const chartPalette = useForecastChartPalette();
  const chartVars = chartPalette.inset
    ? ({
        '--fv3-chart-primary': chartPalette.colors.primary,
        '--fv3-chart-secondary': chartPalette.colors.secondary,
        '--fv3-chart-tertiary': chartPalette.colors.tertiary,
      } as React.CSSProperties)
    : undefined;
  const defaultMonth = initialMonth ?? new Date().getMonth() + 1;

  const [activeTab, setActiveTab] = useState<ActiveTab>('overview');
  const modalRootRef = useRef<HTMLDivElement>(null);
  const [activeMonth, setActiveMonth] = useState<number>(defaultMonth);
  const [inputMode, setInputMode] = useState<InputMode>('rn');
  const [inputs, setInputs] = useState<InputState>({
    groupRN: 0, groupADR: 0, transRN: 0, transADR: 0, groupPct: 0, transPct: 0,
  });
  const [spreadMethod, setSpreadMethod] = useState<string | null>(null);
  const [dailyData, setDailyData] = useState<Record<number, DayData>>({});
  const [dailyRef, setDailyRef] = useState<DailyRef>('none');
  const [acctMethod, setAcctMethod] = useState<AcctMethod>('ly_pct');
  const [manualPcts, setManualPcts] = useState<Record<number, number>>({});

  const [context, setContext] = useState<RevenueContext | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [availableRNOverride, setAvailableRNOverride] = useState<number | null>(null);
  const [forecastMode, setForecastMode] = useState<ForecastMode>('basic');
  const [statAccountInputs, setStatAccountInputs] = useState<Record<number, StatAccountInput>>({});

  // Patch to push updated per-day room nights + ADR into OverviewTab after a daily save
  const [overviewDailyPatch, setOverviewDailyPatch] = useState<{ month: number; saId: number; dailyRN: Record<number, number>; dailyADR: Record<number, number> } | null>(null);

  // Daily entry modal state (stat account mode)
  const [dailyEntryOpen, setDailyEntryOpen] = useState<{
    revenueChartOfAccountId: number;
    revenueAccountName: string;
    revenueAccountNumber: string;
    linkedStatAccountName: string;
    month: number;
    driverValue: number;
    initialMonthlyRN: number;
    initialMonthlyADR: number;
  } | null>(null);

  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputsRef = useRef<InputState>(inputs);
  inputsRef.current = inputs;

  // ── Per-month cache for multi-month editing ─────────────────
  interface MonthSnapshot {
    inputs: InputState;
    dailyData: Record<number, DayData>;
    spreadMethod: string | null;
    acctMethod: AcctMethod;
    manualPcts: Record<number, number>;
    context: RevenueContext | null;
    availableRNOverride: number | null;
    dirty: boolean;
    forecastMode: ForecastMode;
    statAccountInputs: Record<number, StatAccountInput>;
  }
  const monthCacheRef = useRef<Record<number, MonthSnapshot>>({});
  const prevMonthRef = useRef<number>(defaultMonth);

  // ── Overview: all-months quick-entry data ────────────────────
  interface OverviewRow {
    groupRN: number; transRN: number; groupADR: number; transADR: number;
    avgRooms: number; days: number;
    mode: ForecastMode;
    statAccountInputs?: Record<number, StatAccountInput>;
  }
  const [overviewData, setOverviewData] = useState<Record<number, OverviewRow>>({});
  const [overviewLoaded, setOverviewLoaded] = useState(false);
  const [modalStatAccounts, setModalStatAccounts] = useState<StatAccountMapping[]>([]);

  // Segments were added/removed in the mapping panel — refresh both copies.
  const reloadStatAccounts = useCallback(async () => {
    try {
      const ctx = await getRevenueContext({
        versionId, locationId, orgId, year, month: activeMonth || 1,
        departmentName: department, chartOfAccountDepartmentId: departmentId,
      });
      const next = ctx.statAccounts ?? [];
      setModalStatAccounts(next);
      setContext(prev => (prev ? { ...prev, statAccounts: next } : prev));
    } catch (err) {
      console.error('[RevenueForecastModal] reload stat accounts failed', err);
    }
  }, [versionId, locationId, orgId, year, activeMonth, department, departmentId]);

  // Fetch stat accounts once on modal open (needed for header toggle before month detail is loaded)
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const ctx = await getRevenueContext({
          versionId, locationId, orgId, year, month: 1,
          departmentName: department, chartOfAccountDepartmentId: departmentId,
        });
        setModalStatAccounts(ctx.statAccounts ?? []);
      } catch {
        // Stat accounts will just not be available
      }
    })();
  }, [isOpen, versionId, locationId, orgId, year, department, departmentId]);

  // Load overview data for all 12 months on open
  useEffect(() => {
    if (!isOpen || overviewLoaded) return;
    (async () => {
      const rows: Record<number, OverviewRow> = {};
      const promises = Array.from({ length: 12 }, (_, i) => i + 1).map(async (mo) => {
        try {
          const saved = await getRevenueInputs({ versionId, locationId, departmentName: department, chartOfAccountDepartmentId: departmentId, year, month: mo });
          const dim = new Date(year, mo, 0).getDate();
          const monthKey = SHORT_MONTHS[mo - 1].toLowerCase();
          const gridAvail = gridAvailableRooms?.[monthKey] ?? 0;
          const saInputs: Record<number, StatAccountInput> = {};
          if (saved.statAccountInputs) {
            for (const [id, val] of Object.entries(saved.statAccountInputs)) {
              saInputs[Number(id)] = { rn: val.rn || 0, adr: val.adr || 0 };
            }
          }
          rows[mo] = {
            groupRN: saved.groupRN ?? 0,
            transRN: saved.transRN ?? 0,
            groupADR: saved.groupADR ?? 0,
            transADR: saved.transADR ?? 0,
            avgRooms: gridAvail > 0 ? Math.round(gridAvail / dim * 10) / 10 : 0,
            days: dim,
            mode: saved.mode ?? 'basic',
            statAccountInputs: Object.keys(saInputs).length > 0 ? saInputs : undefined,
          };
        } catch {
          const dim = new Date(year, mo, 0).getDate();
          rows[mo] = { groupRN: 0, transRN: 0, groupADR: 0, transADR: 0, avgRooms: 0, days: dim, mode: 'basic' };
        }
      });
      await Promise.all(promises);
      setOverviewData(rows);
      // Restore the persisted mode for the month that opened the modal. Without
      // this, stat-account forecasts hydrate correctly but are hidden behind the
      // Basic view until the user manually toggles modes.
      setForecastMode(rows[defaultMonth]?.mode ?? 'basic');
      setOverviewLoaded(true);
    })();
  }, [isOpen, overviewLoaded, versionId, locationId, department, departmentId, year, gridAvailableRooms, defaultMonth]);

  // Reset overview on close
  useEffect(() => {
    if (!isOpen) setOverviewLoaded(false);
  }, [isOpen]);

  // Auto-save overview edits (debounced per month)
  const overviewSaveTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});
  const saveOverviewMonth = useCallback((mo: number, row: { groupRN: number; transRN: number; groupADR: number; transADR: number }) => {
    if (isReadOnly) return;
    if (overviewSaveTimers.current[mo]) clearTimeout(overviewSaveTimers.current[mo]);
    overviewSaveTimers.current[mo] = setTimeout(async () => {
      try {
        await saveRevenueInputs({
          versionId, locationId, departmentName: department, chartOfAccountDepartmentId: departmentId,
          year, month: mo,
          groupRN: row.groupRN, groupADR: row.groupADR,
          transRN: row.transRN, transADR: row.transADR,
        });
      } catch (err) {
        console.error(`Auto-save failed for month ${mo}:`, err);
      }
    }, 600);
  }, [isReadOnly, versionId, locationId, department, departmentId, year]);

  // Update overview field and trigger auto-save
  const updateOverviewField = useCallback((mo: number, field: string, val: number) => {
    setOverviewData(prev => {
      const updated = { ...prev, [mo]: { ...prev[mo], [field]: val } };
      const row = updated[mo];
      saveOverviewMonth(mo, row);
      // Also update cache so apply picks it up
      const dim = row.days;
      const avail = Math.round(row.avgRooms * dim);
      monthCacheRef.current[mo] = {
        inputs: {
          groupRN: row.groupRN, groupADR: row.groupADR,
          transRN: row.transRN, transADR: row.transADR,
          groupPct: 0, transPct: 0,
        },
        dailyData: {},
        spreadMethod: null,
        acctMethod: 'ly_pct',
        manualPcts: {},
        context: monthCacheRef.current[mo]?.context ?? null,
        availableRNOverride: avail > 0 ? avail : null,
        dirty: true,
        forecastMode: monthCacheRef.current[mo]?.forecastMode ?? row.mode ?? 'basic',
        statAccountInputs: monthCacheRef.current[mo]?.statAccountInputs ?? {},
      };
      return updated;
    });
  }, [saveOverviewMonth]);

  // ── Computed values ──────────────────────────────────────────
  const daysInMonth = context ? context.daysInMonth : new Date(year, activeMonth, 0).getDate();
  const totalRooms = context?.totalRooms ?? 0;
  const SHORT_MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  const gridAvailForMonth = gridAvailableRooms?.[SHORT_MONTH_KEYS[activeMonth - 1]] ?? 0;
  const defaultAvail = gridAvailForMonth > 0 ? gridAvailForMonth : totalRooms * daysInMonth;
  const totalAvail = availableRNOverride ?? defaultAvail;
  // Use modal-level stat accounts (loaded once on open) for toggle visibility,
  // fall back to context stat accounts when in month detail
  const statAccounts = context?.statAccounts ?? modalStatAccounts;
  const hasStatAccounts = modalStatAccounts.length > 0 || (context?.statAccounts?.length ?? 0) > 0;

  // Mode-aware computed values
  const saOccRN = useMemo(() => {
    if (forecastMode !== 'stat_account') return 0;
    let add = 0, sub = 0;
    for (const sa of statAccounts) {
      const inp = statAccountInputs[sa.id];
      if (!inp) continue;
      if (sa.operation === 'subtract') sub += inp.rn;
      else add += inp.rn;
    }
    return add - sub;
  }, [forecastMode, statAccounts, statAccountInputs]);

  const saTotalRevenue = useMemo(() => {
    if (forecastMode !== 'stat_account') return 0;
    return Object.values(statAccountInputs).reduce((sum, sa) => sum + sa.rn * sa.adr, 0);
  }, [forecastMode, statAccountInputs]);

  const occRN = forecastMode === 'stat_account' ? saOccRN : inputs.groupRN + inputs.transRN;
  const totalOccPct = totalAvail > 0 ? Math.round(occRN / totalAvail * 1000) / 10 : 0;
  const groupRevenue = inputs.groupRN * inputs.groupADR;
  const transRevenue = inputs.transRN * inputs.transADR;
  const totalRevenue = forecastMode === 'stat_account' ? saTotalRevenue : groupRevenue + transRevenue;
  const blendedADR = occRN > 0 ? Math.round(totalRevenue / occRN) : 0;

  // ── setField ────────────────────────────────────────────────
  const applySpreadRef = useRef<(method: string, overrideInputs?: InputState) => void>(() => {});
  const setField = useCallback((key: string, val: number) => {
    setInputs(prev => {
      const next = { ...prev, [key]: val };
      if (key === 'groupRN') next.groupPct = totalAvail > 0 ? Math.round(val / totalAvail * 1000) / 10 : 0;
      if (key === 'transRN') next.transPct = totalAvail > 0 ? Math.round(val / totalAvail * 1000) / 10 : 0;
      if (key === 'groupPct') next.groupRN = Math.round(totalAvail * (val / 100));
      if (key === 'transPct') next.transRN = Math.round(totalAvail * (val / 100));
      needsRespreadRef.current = true;
      queueMicrotask(() => {
        if (needsRespreadRef.current && spreadMethod) {
          needsRespreadRef.current = false;
          applySpreadRef.current(spreadMethod, next);
        }
      });
      return next;
    });
  }, [totalAvail, spreadMethod]);

  // ── Snapshot current month into cache ─────────────────────────
  const snapshotCurrentMonth = useCallback(() => {
    const m = prevMonthRef.current;
    // Only mark dirty if user actually entered data
    const hasBasicData = inputs.groupRN > 0 || inputs.transRN > 0 || inputs.groupADR > 0 || inputs.transADR > 0;
    const hasStatData = forecastMode === 'stat_account' && Object.values(statAccountInputs).some(sa => sa.rn > 0 || sa.adr > 0);
    const hasData = hasBasicData || hasStatData;
    const wasDirty = monthCacheRef.current[m]?.dirty ?? false;
    monthCacheRef.current[m] = {
      inputs: { ...inputs },
      dailyData: { ...dailyData },
      spreadMethod,
      acctMethod,
      manualPcts: { ...manualPcts },
      context,
      availableRNOverride,
      dirty: wasDirty || hasData,
      forecastMode,
      statAccountInputs: { ...statAccountInputs },
    };
  }, [inputs, dailyData, spreadMethod, acctMethod, manualPcts, context, availableRNOverride, forecastMode, statAccountInputs]);

  // ── Restore month from cache ────────────────────────────────
  const restoreFromCache = useCallback((cached: MonthSnapshot) => {
    setInputs(cached.inputs);
    setDailyData(cached.dailyData);
    setSpreadMethod(cached.spreadMethod);
    setAcctMethod(cached.acctMethod);
    setManualPcts(cached.manualPcts);
    setContext(cached.context);
    setAvailableRNOverride(cached.availableRNOverride);
    setForecastMode(cached.forecastMode ?? 'basic');
    setStatAccountInputs(cached.statAccountInputs ?? {});
  }, []);

  // ── Load data ────────────────────────────────────────────────
  const loadData = useCallback(async (month: number) => {
    // Check cache first — if user already edited this month in this session, restore it
    // Use cache only if it has full context (from a prior detail view visit, not overview-only edits)
    const cached = monthCacheRef.current[month];
    if (cached && cached.context) {
      restoreFromCache(cached);
      return;
    }

    setIsLoading(true);
    setError(null);
    setAvailableRNOverride(null);
    try {
      const [ctx, savedInputs] = await Promise.all([
        getRevenueContext({ versionId, locationId, orgId, year, month, departmentName: department, chartOfAccountDepartmentId: departmentId }),
        getRevenueInputs({ versionId, locationId, departmentName: department, chartOfAccountDepartmentId: departmentId, year, month }),
      ]);
      setContext(ctx);

      // Initialize inputs from saved data, falling back to overview edits if server has zeros
      const dim = ctx.daysInMonth;
      const avail = ctx.totalRooms * dim;
      const hasSavedInputs = (savedInputs.groupRN ?? 0) > 0 || (savedInputs.transRN ?? 0) > 0 ||
                             (savedInputs.groupADR ?? 0) > 0 || (savedInputs.transADR ?? 0) > 0;
      const ov = overviewData[month];
      const hasOverviewInputs = ov && (ov.groupRN > 0 || ov.transRN > 0 || ov.groupADR > 0 || ov.transADR > 0);
      const source = hasSavedInputs ? savedInputs : (hasOverviewInputs ? ov : savedInputs);
      const gRN = source.groupRN ?? 0;
      const tRN = source.transRN ?? 0;
      const gADR = source.groupADR ?? 0;
      const tADR = source.transADR ?? 0;
      setInputs({
        groupRN: gRN,
        groupADR: gADR,
        transRN: tRN,
        transADR: tADR,
        groupPct: avail > 0 ? Math.round(gRN / avail * 1000) / 10 : 0,
        transPct: avail > 0 ? Math.round(tRN / avail * 1000) / 10 : 0,
      });

      // Restore saved distribution method
      if (savedInputs.distributionMethod) {
        setAcctMethod(savedInputs.distributionMethod as AcctMethod);
      }
      // Restore saved manual pcts
      if (savedInputs.distributions && savedInputs.distributions.length > 0) {
        const pctMap: Record<number, number> = {};
        savedInputs.distributions.forEach(d => { pctMap[d.chartOfAccountId] = d.pct; });
        setManualPcts(pctMap);
      }

      // Restore saved daily data if available, otherwise reset to zeros
      const newDaily: Record<number, DayData> = {};
      if (savedInputs.savedDailyData && savedInputs.savedDailyData.length > 0) {
        for (let i = 1; i <= dim; i++) newDaily[i] = { groupRN: 0, transRN: 0, groupADR: 0, transADR: 0, edited: false };
        for (const d of savedInputs.savedDailyData) {
          if (d.day >= 1 && d.day <= dim) {
            newDaily[d.day] = { groupRN: d.groupRN, groupADR: d.groupADR, transRN: d.transRN, transADR: d.transADR, edited: false };
          }
        }
        setSpreadMethod(savedInputs.spreadMethod || null);
      } else {
        for (let i = 1; i <= dim; i++) newDaily[i] = { groupRN: 0, transRN: 0, groupADR: 0, transADR: 0, edited: false };
        setSpreadMethod(null);
      }
      setDailyData(newDaily);

      // Restore forecast mode and stat account inputs
      const savedMode = savedInputs.mode ?? 'basic';
      // Stat mode needs segments to forecast — but the mapping view is where
      // segments get created, so a caller landing there keeps stat mode.
      setForecastMode(
        initialTab === 'accounts' || (savedMode === 'stat_account' && (ctx.statAccounts?.length ?? 0) > 0) ? 'stat_account' : 'basic'
      );
      if (savedInputs.statAccountInputs && Object.keys(savedInputs.statAccountInputs).length > 0) {
        // Convert string keys to numbers and enrich with mapping metadata
        const saInputs: Record<number, StatAccountInput> = {};
        for (const [id, val] of Object.entries(savedInputs.statAccountInputs)) {
          const saId = Number(id);
          const mapping = ctx.statAccounts?.find(s => s.id === saId);
          saInputs[saId] = {
            rn: val.rn || 0,
            adr: val.adr || 0,
            chartOfAccountId: mapping?.chartOfAccountId,
            revenueChartOfAccountId: mapping?.revenueChartOfAccountId ?? undefined,
          };
        }
        setStatAccountInputs(saInputs);
      } else {
        // Initialize empty stat account inputs from available stat accounts
        const defaultSa: Record<number, StatAccountInput> = {};
        (ctx.statAccounts ?? []).forEach(sa => {
          defaultSa[sa.id] = {
            rn: 0, adr: 0,
            chartOfAccountId: sa.chartOfAccountId,
            revenueChartOfAccountId: sa.revenueChartOfAccountId ?? undefined,
          };
        });
        setStatAccountInputs(defaultSa);
      }
    } catch (err) {
      console.error('Failed to load revenue context:', err);
      setError('Failed to load revenue data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [versionId, locationId, orgId, year, department, departmentId, restoreFromCache, overviewData]);

  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
      if (initialTab === 'accounts') setForecastMode('stat_account');
      setActiveMonth(initialMonth ?? new Date().getMonth() + 1);
      monthCacheRef.current = {};
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && activeTab !== 'overview') {
      // Snapshot the previous month before loading the new one
      if (prevMonthRef.current !== activeMonth && context) {
        snapshotCurrentMonth();
      }
      prevMonthRef.current = activeMonth;
      loadData(activeMonth);
    }
  }, [isOpen, activeMonth, activeTab]);

  // ── Auto-save debounce ────────────────────────────────────────
  useEffect(() => {
    // The overview owns its own month-level persistence. Running the detail
    // auto-save while overview data is merely hydrating can race the reads and
    // replace a persisted stat-account mode with an empty Basic payload.
    if (isReadOnly || !context || activeTab === 'overview') return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        setIsSaving(true);
        if (forecastMode === 'stat_account') {
          await saveRevenueInputs({
            versionId, locationId, departmentName: department, chartOfAccountDepartmentId: departmentId,
            year, month: activeMonth,
            groupRN: 0, groupADR: 0, transRN: 0, transADR: 0,
            mode: 'stat_account',
            statAccountInputs,
          });
        } else {
          await saveRevenueInputs({
            versionId, locationId, departmentName: department, chartOfAccountDepartmentId: departmentId,
            year, month: activeMonth,
            groupRN: inputs.groupRN, groupADR: inputs.groupADR,
            transRN: inputs.transRN, transADR: inputs.transADR,
            mode: 'basic',
          });
        }
      } catch (err) {
        console.error('Auto-save failed:', err);
      } finally {
        setIsSaving(false);
      }
    }, 500);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [inputs, statAccountInputs, forecastMode, isReadOnly, context, activeTab]);

  // ── Escape key ───────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    const doc = ownerDocumentOf(modalRootRef.current);
    doc.addEventListener('keydown', handleKey);
    return () => doc.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  // ── Spread algorithms ─────────────────────────────────────────
  const applySpread = useCallback((method: string, overrideInputs?: InputState) => {
    if (!context) return;
    setSpreadMethod(method);
    let inp = overrideInputs ?? inputsRef.current;
    // In stat account mode, synthesize basic inputs from stat account totals for spread
    if (forecastMode === 'stat_account') {
      const totalRN = Object.values(statAccountInputs).reduce((s, sa) => s + sa.rn, 0);
      const totalRev = Object.values(statAccountInputs).reduce((s, sa) => s + sa.rn * sa.adr, 0);
      const blended = totalRN > 0 ? Math.round(totalRev / totalRN * 100) / 100 : 0;
      inp = { groupRN: 0, groupADR: 0, transRN: totalRN, transADR: blended, groupPct: 0, transPct: 0 };
    }
    const dim = context.daysInMonth;
    const lyDaily = context.ly.daily;
    const otbDaily = context.otb.daily;

    const newDaily: Record<number, DayData> = {};

    const lyMap: Record<number, typeof lyDaily[0]> = {};
    lyDaily.forEach(d => { lyMap[d.day] = d; });

    // DOW-aligned LY map: matches same day-of-week position (e.g. 3rd Friday) instead of calendar date
    const dowLyMap = buildDowAlignedLyMap(year, activeMonth, lyDaily);

    const otbMap: Record<number, typeof otbDaily[0]> = {};
    otbDaily.forEach(d => { otbMap[d.day] = d; });

    // Compute LY totals from DOW-aligned entries (for proportional spread)
    let lyTotalGrp = 0, lyTotalTrans = 0;
    for (let d = 1; d <= dim; d++) {
      const ly = dowLyMap[d];
      if (ly) { lyTotalGrp += ly.grpRN; lyTotalTrans += ly.transRN; }
    }
    const otbTotalGrp = otbDaily.reduce((s, d) => s + d.grpRN, 0);
    const otbTotalTrans = otbDaily.reduce((s, d) => s + d.transRN, 0);

    // For lm_trend: use LY occ% shape
    const lyTotalRN = lyTotalGrp + lyTotalTrans;
    const lyTotalAvail = totalRooms * dim;
    const lyAvgOccPct = lyTotalAvail > 0 ? lyTotalRN / lyTotalAvail * 100 : 0;

    // For otb ADR: compute avg OTB ADR from revenue/RN
    const otbTotalRevenue = otbDaily.reduce((s, d) => s + d.grpRevenue + d.transRevenue, 0);
    const otbTotalRN = otbTotalGrp + otbTotalTrans;
    const otbAvgADR = otbTotalRN > 0 ? otbTotalRevenue / otbTotalRN : inp.transADR;

    // Weekend: count weekend vs weekday days
    let wkeDays = 0;
    for (let d = 1; d <= dim; d++) {
      const dow = new Date(year, activeMonth - 1, d).getDay();
      if (dow === 5 || dow === 6 || dow === 0) wkeDays++;
    }
    const wkdDays = dim - wkeDays;
    const wkeMultiplier = 1.35;
    const wkdMultiplier = wkdDays > 0 ? 1 - (wkeMultiplier * wkeDays - wkeDays) / wkdDays : 1;

    // LM Trend: compute last month's weekend vs weeknight pattern from revenue data
    // Uses revenue as primary signal (more reliable than RN which may be empty)
    const lmDaily = context.lm?.daily ?? [];
    const lmMonth2 = activeMonth === 1 ? 12 : activeMonth - 1;
    const lmYear2 = activeMonth === 1 ? year - 1 : year;
    let lmWkeVal = 0, lmWkeCnt = 0, lmWkdVal = 0, lmWkdCnt = 0;
    for (const ld of lmDaily) {
      const ldDow = new Date(lmYear2, lmMonth2 - 1, ld.day).getDay();
      // Use revenue if available, otherwise fall back to room nights
      const dayVal = (ld.grpRevenue + ld.transRevenue) || (ld.grpRN + ld.transRN);
      if (dayVal === 0) continue; // skip days with no data
      if (ldDow === 0 || ldDow === 5 || ldDow === 6) { lmWkeVal += dayVal; lmWkeCnt++; }
      else { lmWkdVal += dayVal; lmWkdCnt++; }
    }
    const lmWkeAvg = lmWkeCnt > 0 ? lmWkeVal / lmWkeCnt : 0;
    const lmWkdAvg = lmWkdCnt > 0 ? lmWkdVal / lmWkdCnt : 0;
    const lmOverallAvg = (lmWkeCnt + lmWkdCnt) > 0 ? (lmWkeVal + lmWkdVal) / (lmWkeCnt + lmWkdCnt) : 0;
    const lmWkeRatio = lmOverallAvg > 0 ? lmWkeAvg / lmOverallAvg : 1;
    const lmWkdRatio = lmOverallAvg > 0 ? lmWkdAvg / lmOverallAvg : 1;

    const inpOccRN = inp.groupRN + inp.transRN;

    for (let d = 1; d <= dim; d++) {
      const dow = new Date(year, activeMonth - 1, d).getDay();
      const isWk = dow === 5 || dow === 6 || dow === 0;
      let grn = 0, trn = 0, gadr = inp.groupADR, tadr = inp.transADR;

      if (method === 'ly_pattern') {
        const ly = dowLyMap[d];
        if (ly && lyTotalGrp > 0) grn = Math.round(inp.groupRN * (ly.grpRN / lyTotalGrp));
        if (ly && lyTotalTrans > 0) trn = Math.round(inp.transRN * (ly.transRN / lyTotalTrans));
        gadr = inp.groupADR;
        tadr = inp.transADR;
      } else if (method === 'lm_trend') {
        // Use last month's weekend vs weeknight pattern to weight allocation
        const ratio = isWk ? lmWkeRatio : lmWkdRatio;
        const dayRN = Math.round((inpOccRN / dim) * ratio);
        const grpRatio = inpOccRN > 0 ? inp.groupRN / inpOccRN : 0;
        grn = Math.round(dayRN * grpRatio);
        trn = dayRN - grn;
        gadr = inp.groupADR;
        tadr = inp.transADR;
      } else if (method === 'even') {
        grn = Math.floor(inp.groupRN / dim);
        trn = Math.floor(inp.transRN / dim);
        gadr = inp.groupADR;
        tadr = inp.transADR;
      } else if (method === 'weekend') {
        const mult = isWk ? wkeMultiplier : wkdMultiplier;
        grn = Math.round(inp.groupRN / dim * mult);
        trn = Math.round(inp.transRN / dim * mult);
        gadr = inp.groupADR;
        tadr = isWk ? Math.round(inp.transADR * 1.08) : Math.round(inp.transADR * 0.96);
      } else if (method === 'otb') {
        // First pass: assign raw OTB values
        const otb = otbMap[d];
        grn = otb?.grpRN ?? 0;
        trn = otb?.transRN ?? 0;
        gadr = inp.groupADR;
        const otbDayRev = otb ? otb.grpRevenue + otb.transRevenue : 0;
        const otbDayRN = otb ? otb.grpRN + otb.transRN : 0;
        const otbDayADR = otbDayRN > 0 ? otbDayRev / otbDayRN : otbAvgADR;
        tadr = otbAvgADR > 0 ? Math.round(inp.transADR * (otbDayADR / otbAvgADR)) : inp.transADR;
      }

      newDaily[d] = { groupRN: grn, transRN: trn, groupADR: gadr, transADR: tadr, edited: false };
    }

    // OTB second pass: distribute remaining RN across future days only
    if (method === 'otb') {
      const otbBookedRN = Object.values(newDaily).reduce((s, d) => s + d.groupRN + d.transRN, 0);
      const remainingRN = inpOccRN - otbBookedRN;
      if (remainingRN > 0) {
        const today = new Date();
        const currentMonth = today.getMonth() + 1;
        const currentDay = today.getDate();
        const currentYear = today.getFullYear();
        const isFutureMonth = year > currentYear || (year === currentYear && activeMonth > currentMonth);
        const futureDays: number[] = [];
        for (let d = 1; d <= dim; d++) {
          if (isFutureMonth || (year === currentYear && activeMonth === currentMonth && d > currentDay)) {
            futureDays.push(d);
          }
        }
        const targetDays = futureDays.length > 0 ? futureDays : Array.from({ length: dim }, (_, i) => i + 1);
        const extraPerDay = remainingRN / targetDays.length;
        const grpShare = inpOccRN > 0 ? inp.groupRN / inpOccRN : 0.5;
        for (const d of targetDays) {
          newDaily[d] = {
            ...newDaily[d],
            groupRN: newDaily[d].groupRN + Math.round(extraPerDay * grpShare),
            transRN: newDaily[d].transRN + Math.round(extraPerDay * (1 - grpShare)),
          };
        }
      }
    }

    // Rounding correction: distribute leftover RN across first N days so totals match exactly
    const sumGrp = Object.values(newDaily).reduce((s, d) => s + d.groupRN, 0);
    const sumTrans = Object.values(newDaily).reduce((s, d) => s + d.transRN, 0);
    let grpRemainder = inp.groupRN - sumGrp;
    let transRemainder = inp.transRN - sumTrans;
    for (let d = 1; d <= dim && (grpRemainder !== 0 || transRemainder !== 0); d++) {
      if (grpRemainder > 0) { newDaily[d].groupRN++; grpRemainder--; }
      else if (grpRemainder < 0) { newDaily[d].groupRN--; grpRemainder++; }
      if (transRemainder > 0) { newDaily[d].transRN++; transRemainder--; }
      else if (transRemainder < 0) { newDaily[d].transRN--; transRemainder++; }
    }

    setDailyData(newDaily);
  }, [context, totalRooms, year, activeMonth, forecastMode, statAccountInputs]);
  applySpreadRef.current = applySpread;

  // ── Auto-reapply spread when inputs change ────────────────────
  // When user changes inputs on the Monthly Inputs tab, mark that daily data
  // needs recalculation. Apply it when switching to Daily tab or at apply time.
  const needsRespreadRef = useRef(false);
  useEffect(() => {
    if (!needsRespreadRef.current || !context) return;
    if (activeTab === 'daily' || activeTab === 'accounts') {
      needsRespreadRef.current = false;
      applySpread(spreadMethod || 'even', inputs);
    }
  }, [activeTab, spreadMethod, context, applySpread, inputs]);

  // ── Account distribution ──────────────────────────────────────
  const accounts = context?.accounts ?? [];

  const distribution = useMemo(() => {
    return accounts.map(a => {
      let pct: number;
      if (acctMethod === 'ly_pct') pct = a.lyPct;
      else if (acctMethod === 'budget_pct') pct = a.budgetPct;
      else pct = manualPcts[a.chartOfAccountId] ?? a.lyPct;
      return { chartOfAccountId: a.chartOfAccountId, pct };
    });
  }, [accounts, acctMethod, manualPcts]);

  const normalizedDistribution = useMemo(() => {
    const total = distribution.reduce((s, d) => s + d.pct, 0);
    if (total === 0) return distribution;
    return distribution.map(d => ({ ...d, pct: Math.round(d.pct / total * 1000) / 10 }));
  }, [distribution]);

  // Initialize manualPcts from lyPct when switching to manual (normalized to 100%)
  const handleAcctMethodChange = (method: AcctMethod) => {
    if (method === 'manual' && Object.keys(manualPcts).length === 0) {
      const init: Record<number, number> = {};
      const total = accounts.reduce((s, a) => s + a.lyPct, 0);
      accounts.forEach(a => {
        init[a.chartOfAccountId] = total > 0 ? Math.round(a.lyPct / total * 10000) / 100 : 0;
      });
      setManualPcts(init);
    }
    setAcctMethod(method);
  };

  // Auto-adjust manual percentages so they always sum to 100%
  const handleManualPctChange = useCallback((changedId: number, newPct: number) => {
    setManualPcts(prev => {
      const clamped = Math.max(0, Math.min(100, newPct));
      const otherTotal = Object.entries(prev)
        .filter(([id]) => Number(id) !== changedId)
        .reduce((s, [, v]) => s + v, 0);
      const remaining = 100 - clamped;
      const next: Record<number, number> = { ...prev, [changedId]: clamped };
      const otherIds = Object.keys(prev).filter(id => Number(id) !== changedId).map(Number);
      if (otherTotal > 0) {
        const scale = remaining / otherTotal;
        for (const id of otherIds) {
          next[id] = Math.round((prev[id] * scale) * 100) / 100;
        }
      } else if (otherIds.length > 0) {
        const each = Math.round((remaining / otherIds.length) * 100) / 100;
        for (const id of otherIds) {
          next[id] = each;
        }
      }
      return next;
    });
  }, []);

  // ── Revenue account mapping for stat accounts ─────────────────
  const handleRevenueAccountChange = useCallback(async (statAccountId: number, revenueChartOfAccountId: number | null) => {
    const revAcct = revenueChartOfAccountId
      ? (context?.accounts ?? []).find(a => a.chartOfAccountId === revenueChartOfAccountId)
      : null;

    const patchSa = (sa: StatAccountMapping): StatAccountMapping => {
      if (sa.id !== statAccountId) return sa;
      return {
        ...sa,
        revenueChartOfAccountId: revenueChartOfAccountId ?? undefined,
        revenueAccountName: revAcct?.accountName ?? null,
        revenueAccountNumber: revAcct?.accountNumber ?? null,
      };
    };

    // Optimistic update — apply immediately before API confirms
    setModalStatAccounts(prev => prev.map(patchSa));
    setContext(prev => prev ? { ...prev, statAccounts: (prev.statAccounts ?? []).map(patchSa) } : prev);
    setStatAccountInputs(prev => {
      const entry = prev[statAccountId];
      if (!entry) return prev;
      return { ...prev, [statAccountId]: { ...entry, revenueChartOfAccountId: revenueChartOfAccountId ?? undefined } };
    });

    try {
      await updateStatAccountMapping(statAccountId, { revenueChartOfAccountId });
    } catch (err) {
      // Revert on failure
      console.error('Failed to update revenue account mapping:', err);
      toast.error('Failed to save revenue account mapping');
      const revertSa = (sa: StatAccountMapping): StatAccountMapping => {
        if (sa.id !== statAccountId) return sa;
        return { ...sa, revenueChartOfAccountId: undefined, revenueAccountName: null, revenueAccountNumber: null };
      };
      setModalStatAccounts(prev => prev.map(revertSa));
      setContext(prev => prev ? { ...prev, statAccounts: (prev.statAccounts ?? []).map(revertSa) } : prev);
    }
  }, [context]);

  // ── Helper: build a month payload from a snapshot ──────────────
  // ── Apply forecast ────────────────────────────────────────────
  const handleApply = async () => {
    if (isReadOnly || isApplying) return;
    setIsApplying(true);
    try {
      // Snapshot current month so it's included in the cache
      if (activeTab !== 'overview') {
        snapshotCurrentMonth();
      }
      const cache = monthCacheRef.current;

      // Collect all dirty months
      const dirtyMonths = Object.entries(cache)
        .filter(([, snap]) => snap.dirty)
        .map(([m, snap]) => ({ month: Number(m), snap }));

      // If no dirty months in cache, fall back to applying just the active month
      if (dirtyMonths.length === 0 && activeTab !== 'overview' && accounts.length > 0) {
        dirtyMonths.push({
          month: activeMonth,
          snap: { inputs, dailyData, spreadMethod, acctMethod, manualPcts, context, availableRNOverride, dirty: true, forecastMode, statAccountInputs },
        });
      }

      // For months missing context (overview-only edits), fetch context so we can build distribution
      const needsContext = dirtyMonths.filter(({ snap }) => !snap.context);
      if (needsContext.length > 0) {
        await Promise.all(
          needsContext.map(async ({ month, snap }) => {
            try {
              const ctx = await getRevenueContext({
                versionId, locationId, orgId, year, month,
                departmentName: department, chartOfAccountDepartmentId: departmentId,
              });
              snap.context = ctx;
            } catch (err) {
              console.error(`Failed to fetch context for month ${month}:`, err);
            }
          })
        );
      }

      // Build override calls: for each dirty month, compute total revenue and allocate to accounts
      const overrideCalls: { chartOfAccountId: number; month: number; amount: number }[] = [];

      for (const { month, snap } of dirtyMonths) {
        const isStatMode = snap.forecastMode === 'stat_account' && snap.statAccountInputs && Object.keys(snap.statAccountInputs).length > 0;
        const totalRevenue = isStatMode
          ? Object.values(snap.statAccountInputs).reduce((sum, sa) => sum + sa.rn * sa.adr, 0)
          : (snap.inputs.groupRN * snap.inputs.groupADR) + (snap.inputs.transRN * snap.inputs.transADR);

        // Skip months with zero total forecast
        if (totalRevenue === 0) continue;

        if (isStatMode) {
          // Direct routing: never use percentage distribution in stat_account mode.
          // Use revenueChartOfAccountId if explicitly mapped, otherwise use the stat account's
          // own chartOfAccountId (which is the correlated revenue account for this hotel).
          const snapStatAccounts = snap.context?.statAccounts ?? modalStatAccounts;
          const snapAccounts = snap.context?.accounts ?? [];

          // Build target → amount map from stat inputs.
          // Resolution order: explicit revenueChartOfAccountId → name match against revenue accounts → skip
          const directAmounts: Record<number, number> = {};
          for (const [saId, sa] of Object.entries(snap.statAccountInputs)) {
            const mapping = snapStatAccounts.find(s => s.id === parseInt(saId));
            if (!mapping) continue;
            let targetCoaId: number | null = mapping.revenueChartOfAccountId ?? null;
            if (!targetCoaId) {
              const nameMatch = snapAccounts.find(a =>
                a.accountName.toLowerCase().trim() === mapping.accountName.toLowerCase().trim()
              );
              targetCoaId = nameMatch?.chartOfAccountId ?? null;
            }
            if (!targetCoaId) continue; // no revenue account match — skip
            directAmounts[targetCoaId] = (directAmounts[targetCoaId] ?? 0) + Math.round(sa.rn * sa.adr * 100) / 100;
          }

          // Push override for every revenue account — $0 clears any stale percentage allocations
          const pushed: Record<number, true> = {};
          for (const a of snapAccounts) {
            overrideCalls.push({ chartOfAccountId: a.chartOfAccountId, month, amount: directAmounts[a.chartOfAccountId] ?? 0 });
            pushed[a.chartOfAccountId] = true;
          }
          // Push any mapped targets not in the standard revenue accounts list
          for (const targetCoaId of Object.keys(directAmounts)) {
            const id = Number(targetCoaId);
            if (!pushed[id]) {
              overrideCalls.push({ chartOfAccountId: id, month, amount: directAmounts[id] });
            }
          }
          continue;
        }

        const snapAccounts = snap.context?.accounts ?? [];
        if (snapAccounts.length === 0) continue;

        // Build distribution percentages (same logic as buildMonthPayload)
        let dist = snapAccounts.map(a => {
          let pct: number;
          if (snap.acctMethod === 'ly_pct') pct = a.lyPct;
          else if (snap.acctMethod === 'budget_pct') pct = a.budgetPct;
          else pct = snap.manualPcts[a.chartOfAccountId] ?? a.lyPct;
          return { chartOfAccountId: a.chartOfAccountId, pct };
        });
        let distTotal = dist.reduce((s, d) => s + d.pct, 0);

        // Fallback: if selected method yields all zeros, try budget_pct
        if (distTotal === 0 && snap.acctMethod !== 'budget_pct') {
          dist = snapAccounts.map(a => ({ chartOfAccountId: a.chartOfAccountId, pct: a.budgetPct }));
          distTotal = dist.reduce((s, d) => s + d.pct, 0);
        }

        // Final fallback: even split
        if (distTotal === 0) {
          const evenPct = Math.round(100 / snapAccounts.length * 10) / 10;
          dist = snapAccounts.map(a => ({ chartOfAccountId: a.chartOfAccountId, pct: evenPct }));
          distTotal = dist.reduce((s, d) => s + d.pct, 0);
        }

        // Allocate revenue to each account by percentage; zero out accounts with no allocation
        for (const d of dist) {
          const amount = d.pct === 0 ? 0 : Math.round(totalRevenue * (d.pct / distTotal) * 100) / 100;
          overrideCalls.push({ chartOfAccountId: d.chartOfAccountId, month, amount });
        }
      }

      if (overrideCalls.length === 0) {
        toast.error('No months with data to apply');
        return;
      }

      // Save all overrides in a single bulk request
      await saveBulkOverridesV3(versionId, overrideCalls.map(({ chartOfAccountId, month, amount }) => ({
        chartOfAccountId,
        year,
        month,
        overrideAmount: amount,
      })));

      // Save room stat overrides (occupied + available room nights) for each dirty month
      const roomStatCalls: Promise<unknown>[] = [];
      for (const { month, snap } of dirtyMonths) {
        const isStatMode = snap.forecastMode === 'stat_account' && snap.statAccountInputs && Object.keys(snap.statAccountInputs).length > 0;
        const occupiedRN = isStatMode
          ? Object.values(snap.statAccountInputs).reduce((sum, sa) => sum + sa.rn, 0)
          : snap.inputs.groupRN + snap.inputs.transRN;
        if (occupiedRN > 0) {
          roomStatCalls.push(
            saveRoomStatOverride({
              versionId, year, month,
              roomStatType: 'occupiedRooms',
              overrideValue: Math.round(occupiedRN),
            })
          );
        }

        // Available room nights: use override if set, otherwise use grid/default
        const snapAvail = snap.availableRNOverride;
        if (snapAvail != null && snapAvail > 0) {
          roomStatCalls.push(
            saveRoomStatOverride({
              versionId, year, month,
              roomStatType: 'availableRooms',
              overrideValue: Math.round(snapAvail),
            })
          );
        }
      }
      if (roomStatCalls.length > 0) {
        await Promise.all(roomStatCalls);
      }

      const monthsApplied = new Set(overrideCalls.map(c => c.month)).size;
      const monthLabel = monthsApplied === 1 ? '1 month' : `${monthsApplied} months`;
      toast.success(`Revenue forecast applied to ${monthLabel}`);
      monthCacheRef.current = {};
      onApplied();
    } catch (err) {
      console.error('Failed to apply revenue forecast:', err);
      toast.error('Failed to apply revenue forecast');
    } finally {
      setIsApplying(false);
    }
  };

  // ── Daily totals ──────────────────────────────────────────────
  const dailyTotals = useMemo(() => {
    let gRN = 0, tRN = 0, gRev = 0, tRev = 0;
    Object.values(dailyData).forEach(d => {
      gRN += d.groupRN;
      tRN += d.transRN;
      gRev += d.groupRN * d.groupADR;
      tRev += d.transRN * d.transADR;
    });
    return { gRN, tRN, totalRN: gRN + tRN, gRev, tRev, totalRev: gRev + tRev };
  }, [dailyData]);

  // ── Inline per-day RN edit from OverviewTab → persist to forecastDailyValue ──
  const inlineDailySaveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const handleInlineDailyRNEdit = useCallback((mo: number, sa: StatAccountMapping, dailyRN: Record<number, number>, dailyADR: Record<number, number>) => {
    if (!sa.revenueChartOfAccountId || isReadOnly) return;
    const key = `${mo}-${sa.id}`;
    if (inlineDailySaveTimers.current[key]) clearTimeout(inlineDailySaveTimers.current[key]);
    inlineDailySaveTimers.current[key] = setTimeout(async () => {
      const daysInMonth = new Date(year, mo, 0).getDate();
      const values: Array<{ day: number; adr: number; roomNights: number }> = [];
      for (let d = 1; d <= daysInMonth; d++) {
        const rn = dailyRN[d] ?? 0;
        const adr = dailyADR[d] ?? 0;
        if (rn > 0 && adr > 0) values.push({ day: d, adr, roomNights: rn });
      }
      try {
        await saveDailyValues({
          versionId,
          chartOfAccountId: sa.revenueChartOfAccountId!,
          locationId,
          year,
          month: mo,
          values,
          spreadPattern: 'manual',
        });
      } catch (err: any) {
        // Month already holds PINS on a live driver (e.g. AI rooms commit) —
        // day-level edits there ARE pins, so retry in pin mode.
        if (err?.response?.data?.code === 'PINNED_DAILY_EXISTS') {
          try {
            await saveDailyValues({
              versionId,
              chartOfAccountId: sa.revenueChartOfAccountId!,
              locationId,
              year,
              month: mo,
              values,
              spreadPattern: 'manual',
              pinMode: true,
            });
            return;
          } catch (retryErr) {
            console.error('Failed to save inline daily stat values (pin retry):', retryErr);
            return;
          }
        }
        console.error('Failed to save inline daily stat values:', err);
      }
    }, 800);
  }, [versionId, locationId, year, isReadOnly]);

  // ── Handle daily entry save (stat account mode) ───────────────
  const handleDailyEntrySaved = useCallback((savedValues?: DailyValue[]) => {
    if (!dailyEntryOpen) return;
    const { revenueChartOfAccountId, month: entryMonth } = dailyEntryOpen;

    console.log('[DailyEntrySaved] revenueChartOfAccountId:', revenueChartOfAccountId, 'month:', entryMonth);
    console.log('[DailyEntrySaved] statAccounts:', statAccounts.map(sa => ({ id: sa.id, name: sa.accountName, revenueChartOfAccountId: sa.revenueChartOfAccountId })));
    console.log('[DailyEntrySaved] savedValues count:', savedValues?.length, 'sample:', savedValues?.slice(0,3));

    const linkedSa = statAccounts.find(sa => sa.revenueChartOfAccountId === revenueChartOfAccountId);
    console.log('[DailyEntrySaved] linkedSa:', linkedSa);
    if (linkedSa && savedValues && savedValues.length > 0) {
      // Derive monthly totals from the saved values (no re-fetch needed)
      const monthlyRN = savedValues.reduce((s, v) => s + (v.roomNights || 0), 0);
      const monthlyRevenue = savedValues.reduce((s, v) => s + (v.amount || 0), 0);
      const derivedADR = monthlyRN > 0 ? monthlyRevenue / monthlyRN : 0;

      // Update the overview monthly totals
      setOverviewData(prev => {
        const row = prev[entryMonth];
        if (!row) return prev;
        const saInputs = { ...(row.statAccountInputs ?? {}), [linkedSa.id]: { rn: monthlyRN, adr: derivedADR } };
        return { ...prev, [entryMonth]: { ...row, statAccountInputs: saInputs } };
      });

      // Push per-day room nights + ADR directly into the OverviewTab inline daily view
      const dailyRN: Record<number, number> = {};
      const dailyADR: Record<number, number> = {};
      for (const v of savedValues) {
        dailyRN[v.day] = v.roomNights ?? 0;
        dailyADR[v.day] = v.adr ?? 0;
      }
      setOverviewDailyPatch({ month: entryMonth, saId: linkedSa.id, dailyRN, dailyADR });

      // Persist the derived monthly totals
      saveRevenueInputs({
        versionId, locationId, departmentName: department, chartOfAccountDepartmentId: departmentId,
        year, month: entryMonth,
        groupRN: 0, groupADR: 0, transRN: 0, transADR: 0,
        mode: 'stat_account',
        statAccountInputs: { ...(overviewData[entryMonth]?.statAccountInputs ?? {}), [linkedSa.id]: { rn: monthlyRN, adr: derivedADR } },
      }).catch(err => console.error('Auto-save after daily entry failed:', err));
    }

    setDailyEntryOpen(null);
  }, [dailyEntryOpen, versionId, year, statAccounts, overviewData, locationId, department, departmentId]);

  // ── Budget seeding: batch-apply per-month stat account inputs ──
  const handleSeedMonths = useCallback((updates: Record<number, Record<number, StatAccountInput>>) => {
    setOverviewData(prev => {
      const next = { ...prev };
      for (const [moStr, saUpdates] of Object.entries(updates)) {
        const mo = Number(moStr);
        const row = next[mo];
        if (!row) continue;
        const merged = { ...(row.statAccountInputs ?? {}), ...saUpdates };
        next[mo] = { ...row, mode: 'stat_account', statAccountInputs: merged };
        const avail = Math.round(row.avgRooms * row.days);
        monthCacheRef.current[mo] = {
          ...(monthCacheRef.current[mo] ?? { inputs: { groupRN: 0, groupADR: 0, transRN: 0, transADR: 0, groupPct: 0, transPct: 0 }, dailyData: {}, spreadMethod: null, acctMethod: 'ly_pct' as AcctMethod, manualPcts: {} }),
          context: monthCacheRef.current[mo]?.context ?? null,
          availableRNOverride: avail > 0 ? avail : null,
          dirty: true,
          forecastMode: 'stat_account',
          statAccountInputs: merged,
        };
        saveRevenueInputs({
          versionId, locationId, departmentName: department, chartOfAccountDepartmentId: departmentId,
          year, month: mo,
          groupRN: 0, groupADR: 0, transRN: 0, transADR: 0,
          mode: 'stat_account',
          statAccountInputs: merged,
        }).catch(err => console.error(`Seed save failed for month ${mo}:`, err));
      }
      return next;
    });
  }, [versionId, locationId, department, departmentId, year]);

  // ── Navigate to month detail ──────────────────────────────────
  const goToMonthDetail = useCallback((mo: number, tab: 'inputs' | 'daily' | 'accounts' = 'inputs') => {
    // For same-month navigation (e.g. current month already set on open),
    // the loadData useEffect won't re-fire since activeMonth doesn't change.
    // Force a reload so overview edits and context are picked up.
    if (mo === activeMonth) {
      loadData(mo);
    }
    setActiveMonth(mo);
    setActiveTab(tab);
  }, [activeMonth, loadData]);

  if (!isOpen) return null;

  // Stat mode keys RN × ADR per segment on the overview grid (and in the P&L
  // grid's ORN/ADR drill-downs); its only detail view is the mapping.
  const DETAIL_TABS: { id: 'inputs' | 'daily' | 'accounts'; label: string }[] = forecastMode === 'stat_account'
    ? [{ id: 'accounts', label: 'Stat account mapping' }]
    : [
        { id: 'inputs', label: 'Month Detail' },
        { id: 'daily', label: 'Daily Breakdown' },
        { id: 'accounts', label: 'Account Distribution' },
      ];

  const isDetailView = activeTab !== 'overview';

  return (
    <div ref={modalRootRef} className="fv3-rfm fixed inset-0 z-[9999] flex items-center justify-center" style={chartVars}>
      {/* Backdrop */}
      <div className="fv3-scrim absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="fv3-dialog relative bg-white rounded-[14px] shadow-[0_25px_60px_rgba(0,0,0,0.25)] w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden mx-4">

        {/* Header */}
        <div className="fv3-dialog-head flex items-center justify-between px-5 py-3.5 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            {isDetailView && (
              <button
                onClick={() => { snapshotCurrentMonth(); setActiveTab('overview'); }}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg mr-1"
                title="Back to overview"
              >
                <ChevronRight size={16} className="rotate-180" />
              </button>
            )}
            <div className="w-10 h-10 rounded-[10px] flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, var(--fv3-accent-wash, #FFF7ED), var(--fv3-accent-wash, #FEF3C7))' }}>
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><rect x="2" y="6" width="18" height="13" rx="2" fill="var(--fv3-accent, #F28C38)" opacity="0.15"/><rect x="2" y="6" width="18" height="13" rx="2" stroke="var(--fv3-accent, #F28C38)" strokeWidth="1.4"/><path d="M6 3v5M16 3v5M2 10h18" stroke="var(--fv3-accent, #F28C38)" strokeWidth="1.4" strokeLinecap="round"/></svg>
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 tracking-tight">
                {department} Revenue {isBudget ? 'Budget' : 'Forecast'}
              </h2>
              <p className="text-sm text-slate-400 mt-0.5 flex items-center gap-2">
                <span>
                  {isDetailView ? `${MONTH_NAMES[activeMonth - 1]} ${year}` : String(year)}
                  {isDetailView && totalRooms > 0 && <> · {fmt(totalRooms)} rooms</>}
                </span>
                {isBudget && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wide" style={{ background: 'var(--fv3-accent-wash, #FEF3E7)', border: '1px solid var(--fv3-accent-line, #F5CBA0)', color: 'var(--fv3-accent-ink, #B45309)' }}>
                    BUDGET
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasStatAccounts && (
              <div className="flex items-center gap-2">
                <div className="flex bg-slate-50 border border-slate-200 rounded-lg p-[3px] gap-0.5">
                  <button
                    onClick={() => setForecastMode('basic')}
                    disabled={isReadOnly}
                    className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                      forecastMode === 'basic' ? 'bg-white text-slate-900 shadow-sm font-semibold' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    Basic
                  </button>
                  <button
                    onClick={() => setForecastMode('stat_account')}
                    disabled={isReadOnly}
                    className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                      forecastMode === 'stat_account' ? 'bg-white text-slate-900 shadow-sm font-semibold' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    By Stat Account
                  </button>
                </div>
                {forecastMode === 'stat_account' && (
                  <button
                    onClick={() => goToMonthDetail(activeMonth || 1, 'accounts')}
                    className="text-xs text-[var(--fv3-accent-hover, #E8722A)] hover:text-[var(--fv3-accent, #F28C38)] font-medium"
                  >
                    Stat account mapping →
                  </button>
                )}
              </div>
            )}
            {isSaving && (
              <span className="text-xs text-slate-400 flex items-center gap-1">
                <Loader2 size={12} className="animate-spin" /> Saving…
              </span>
            )}
            {isReadOnly && (
              <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-500">
                Read Only
              </span>
            )}
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 border border-slate-200 bg-white rounded-lg transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Month selector + detail tabs — only in detail view */}
        {isDetailView && (
          <>
            <div className="flex items-center gap-1 px-5 py-2 border-b border-slate-100 bg-slate-50 flex-shrink-0 overflow-x-auto">
              {SHORT_MONTHS.map((m, i) => {
                const mo = i + 1;
                const isDirty = monthCacheRef.current[mo]?.dirty || (mo === activeMonth && (inputs.groupRN > 0 || inputs.transRN > 0 || (forecastMode === 'stat_account' && Object.values(statAccountInputs).some(sa => sa.rn > 0))));
                return (
                  <button
                    key={mo}
                    onClick={() => setActiveMonth(mo)}
                    className={`relative px-3 py-1 rounded text-xs font-medium whitespace-nowrap transition-colors ${
                      activeMonth === mo
                        ? 'bg-orange-500 text-white'
                        : 'text-slate-500 hover:bg-slate-200'
                    }`}
                  >
                    {m}
                    {isDirty && activeMonth !== mo && (
                      <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-orange-400" />
                    )}
                  </button>
                );
              })}
            </div>
            <div className="flex border-b border-slate-200 bg-slate-50 flex-shrink-0 px-5">
              {DETAIL_TABS.map(t => (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  className={`px-4 py-2 -mb-px text-xs font-medium transition-colors ${
                    activeTab === t.id
                      ? 'border-b-2 border-orange-500 bg-white text-orange-600 font-bold'
                      : 'border-b-2 border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </>
        )}

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
          {activeTab === 'overview' ? (
            <OverviewTab
                overviewData={overviewData}
                updateField={updateOverviewField}
                overviewLoaded={overviewLoaded}
                year={year}
                versionId={versionId}
                locationId={locationId}
                orgId={orgId}
                isBudget={isBudget}
                departmentName={department}
                chartOfAccountDepartmentId={departmentId}
                onSeedMonths={handleSeedMonths}
                isReadOnly={isReadOnly}
                onMonthClick={(mo) => goToMonthDetail(mo)}
                onDailyClick={(mo) => goToMonthDetail(mo, 'daily')}
                onAccountsClick={(mo) => goToMonthDetail(mo, 'accounts')}
                forecastMode={forecastMode}
                statAccounts={statAccounts}
                onCalendarClick={(mo, sa) => {
                  if (!sa.revenueChartOfAccountId) return;
                  const revAcct = context?.accounts?.find(a => a.chartOfAccountId === sa.revenueChartOfAccountId);
                  const ovRow = overviewData[mo];
                  const saInp = ovRow?.statAccountInputs?.[sa.id] ?? { rn: 0, adr: 0 };
                  setDailyEntryOpen({
                    revenueChartOfAccountId: sa.revenueChartOfAccountId,
                    revenueAccountName: revAcct?.accountName ?? sa.revenueAccountName ?? sa.accountName,
                    revenueAccountNumber: revAcct?.accountNumber ?? sa.revenueAccountNumber ?? '',
                    linkedStatAccountName: sa.accountName,
                    month: mo,
                    driverValue: saInp.rn * saInp.adr,
                    initialMonthlyRN: saInp.rn,
                    initialMonthlyADR: saInp.adr,
                  });
                }}
                onStatAccountFieldUpdate={(mo, saId, field, val) => {
                  setOverviewData(prev => {
                    const row = prev[mo];
                    if (!row) return prev;
                    const saInputs = { ...(row.statAccountInputs ?? {}) };
                    saInputs[saId] = { ...(saInputs[saId] ?? { rn: 0, adr: 0 }), [field]: val };
                    const updated = { ...prev, [mo]: { ...row, statAccountInputs: saInputs } };
                    // Update cache for apply
                    const dim = row.days;
                    const avail = Math.round(row.avgRooms * dim);
                    monthCacheRef.current[mo] = {
                      ...(monthCacheRef.current[mo] ?? { inputs: { groupRN: 0, groupADR: 0, transRN: 0, transADR: 0, groupPct: 0, transPct: 0 }, dailyData: {}, spreadMethod: null, acctMethod: 'ly_pct' as AcctMethod, manualPcts: {} }),
                      context: monthCacheRef.current[mo]?.context ?? null,
                      availableRNOverride: avail > 0 ? avail : null,
                      dirty: true,
                      forecastMode: 'stat_account',
                      statAccountInputs: saInputs,
                    };
                    // Auto-save stat account inputs
                    saveRevenueInputs({
                      versionId, locationId, departmentName: department, chartOfAccountDepartmentId: departmentId,
                      year, month: mo,
                      groupRN: 0, groupADR: 0, transRN: 0, transADR: 0,
                      mode: 'stat_account',
                      statAccountInputs: saInputs,
                    }).catch(err => console.error(`Auto-save stat account failed for month ${mo}:`, err));
                    return updated;
                  });
                }}
                savedDailyPatch={overviewDailyPatch}
                onInlineDailyRNEdit={handleInlineDailyRNEdit}
              />
          ) : isLoading ? (
            <>
              <div className="rn-sk-classic-only flex items-center justify-center h-64">
                <Loader2 size={28} className="animate-spin text-slate-400" />
              </div>
              <div className="rn-sk-inset-only p-4"><RowsSkeleton rows={8} cols={6} label="Loading month" /></div>
            </>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-64 text-red-600 gap-2">
              <AlertCircle size={28} />
              <p className="text-sm">{error}</p>
              <button
                onClick={() => loadData(activeMonth)}
                className="text-xs text-blue-600 underline"
              >
                Try again
              </button>
            </div>
          ) : (
            <div className={`h-full ${activeTab === 'daily' ? 'overflow-hidden' : 'overflow-y-auto'}`}>
              {activeTab === 'inputs' && (
                <InputsTab
                  inputs={inputs}
                  setField={setField}
                  inputMode={inputMode}
                  setInputMode={setInputMode}
                  totalAvail={totalAvail}
                  onAvailableRNChange={(val) => setAvailableRNOverride(val > 0 ? val : null)}
                  occRN={occRN}
                  totalOccPct={totalOccPct}
                  blendedADR={blendedADR}
                  groupRevenue={groupRevenue}
                  transRevenue={transRevenue}
                  totalRevenue={totalRevenue}
                  context={context}
                  activeMonth={activeMonth}
                  year={year}
                  isReadOnly={isReadOnly}
                  forecastMode={forecastMode}
                  onForecastModeChange={setForecastMode}
                  statAccountInputs={statAccountInputs}
                  onStatAccountInputChange={(saId, field, value) => {
                    setStatAccountInputs(prev => {
                      const existing = prev[saId];
                      // If this is a new saId not yet in state, enrich with mapping metadata
                      if (!existing) {
                        const mapping = statAccounts.find(s => s.id === saId);
                        return { ...prev, [saId]: { rn: 0, adr: 0, chartOfAccountId: mapping?.chartOfAccountId, revenueChartOfAccountId: mapping?.revenueChartOfAccountId ?? undefined, [field]: value } };
                      }
                      return { ...prev, [saId]: { ...existing, [field]: value } };
                    });
                  }}
                />
              )}
              {activeTab === 'daily' && (
                <DailyTab
                  dailyData={dailyData}
                  setDailyData={setDailyData}
                  spreadMethod={spreadMethod}
                  applySpread={applySpread}
                  dailyRef={dailyRef}
                  setDailyRef={setDailyRef}
                  daysInMonth={daysInMonth}
                  totalRooms={totalRooms}
                  totalAvail={totalAvail}
                  occRN={occRN}
                  totalRevenue={totalRevenue}
                  dailyTotals={dailyTotals}
                  context={context}
                  activeMonth={activeMonth}
                  year={year}
                  isReadOnly={isReadOnly}
                />
              )}
              {activeTab === 'accounts' && forecastMode === 'stat_account' && (
                <StatAccountMappingPanel
                  orgId={orgId}
                  locationId={locationId}
                  statAccounts={statAccounts}
                  accounts={accounts}
                  isReadOnly={isReadOnly}
                  onRevenueAccountChange={handleRevenueAccountChange}
                  onMappingsChanged={reloadStatAccounts}
                />
              )}
              {activeTab === 'accounts' && forecastMode !== 'stat_account' && (
                <AccountsTab
                  accounts={accounts}
                  acctMethod={acctMethod}
                  setAcctMethod={handleAcctMethodChange}
                  manualPcts={manualPcts}
                  setManualPcts={setManualPcts}
                  onManualPctChange={handleManualPctChange}
                  totalRevenue={totalRevenue}
                  occRN={occRN}
                  activeMonth={activeMonth}
                  year={year}
                  isReadOnly={isReadOnly}
                  forecastMode={forecastMode}
                  statAccounts={statAccounts}
                  statAccountInputs={statAccountInputs}
                  availableRoomNights={totalAvail}
                  onRevenueAccountChange={handleRevenueAccountChange}
                />
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="fv3-dialog-foot flex items-center justify-between px-6 py-3.5 border-t border-slate-100 bg-slate-50 flex-shrink-0">
          <button
            onClick={isDetailView ? () => { snapshotCurrentMonth(); setActiveTab('overview'); } : onClose}
            className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg border border-slate-300"
          >
            {isDetailView ? '← Back to Overview' : 'Cancel'}
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={handleApply}
              disabled={isReadOnly || isApplying}
              className={`flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-lg transition-all ${
                isReadOnly || isApplying
                  ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  : 'fv3-btn-primary text-white shadow-[0_2px_8px_rgba(232,114,42,0.35)] hover:shadow-[0_4px_12px_rgba(232,114,42,0.4)]'
              }`}
              style={isReadOnly || isApplying ? undefined : { background: 'linear-gradient(135deg, var(--fv3-accent, #F28C38), var(--fv3-accent-hover, #E8722A))' }}
            >
              {isApplying ? (
                <><Loader2 size={14} className="animate-spin" /> Applying…</>
              ) : (() => {
                const cachedDirty = Object.values(monthCacheRef.current).filter(s => s.dirty).length;
                const overviewDirty = Object.values(overviewData).filter(r => r.groupRN > 0 || r.transRN > 0).length;
                const count = Math.max(cachedDirty, overviewDirty);
                return count > 1 ? `Apply ${count} Months` : `Apply Revenue ${isBudget ? 'Budget' : 'Forecast'}`;
              })()}
            </button>
          </div>
        </div>
      </div>

      {/* Daily Entry Modal (stat account mode) */}
      {dailyEntryOpen && (
        <DailyEntryModal
          isOpen={true}
          onClose={() => setDailyEntryOpen(null)}
          versionId={versionId}
          chartOfAccountId={dailyEntryOpen.revenueChartOfAccountId}
          locationId={locationId}
          accountName={dailyEntryOpen.revenueAccountName}
          accountNumber={dailyEntryOpen.revenueAccountNumber}
          driverType="Stat Account"
          driverValue={dailyEntryOpen.driverValue}
          year={year}
          month={dailyEntryOpen.month}
          currency={currency}
          onSave={handleDailyEntrySaved}
          showStatMode={true}
          linkedStatAccountName={dailyEntryOpen.linkedStatAccountName}
          initialMonthlyRN={dailyEntryOpen.initialMonthlyRN}
          initialMonthlyADR={dailyEntryOpen.initialMonthlyADR}
        />
      )}
    </div>
  );
}

// ── Inputs Tab ────────────────────────────────────────────────

interface InputsTabProps {
  inputs: InputState;
  setField: (key: string, val: number) => void;
  inputMode: InputMode;
  setInputMode: (m: InputMode) => void;
  totalAvail: number;
  onAvailableRNChange: (val: number) => void;
  occRN: number;
  totalOccPct: number;
  blendedADR: number;
  groupRevenue: number;
  transRevenue: number;
  totalRevenue: number;
  context: RevenueContext | null;
  activeMonth: number;
  year: number;
  isReadOnly: boolean;
  forecastMode: ForecastMode;
  onForecastModeChange: (mode: ForecastMode) => void;
  statAccountInputs: Record<number, StatAccountInput>;
  onStatAccountInputChange: (saId: number, field: 'rn' | 'adr', value: number) => void;
}

function InputsTab({
  inputs, setField, inputMode, setInputMode,
  totalAvail, onAvailableRNChange, occRN, totalOccPct, blendedADR,
  groupRevenue, transRevenue, totalRevenue,
  context, activeMonth, year, isReadOnly,
  forecastMode, onForecastModeChange, statAccountInputs, onStatAccountInputChange,
}: InputsTabProps) {
  // Compute pct dynamically from current totalAvail (handles editable available RN)
  const groupPctDynamic = totalAvail > 0 ? Math.round(inputs.groupRN / totalAvail * 1000) / 10 : 0;
  const transPctDynamic = totalAvail > 0 ? Math.round(inputs.transRN / totalAvail * 1000) / 10 : 0;

  const lyMonthly = context?.ly.monthly;
  const lyOcc = lyMonthly && context
    ? Math.round((lyMonthly.grpRN + lyMonthly.transRN) / (context.totalRooms * context.daysInMonth) * 1000) / 10
    : null;
  const lyRN = lyMonthly ? lyMonthly.grpRN + lyMonthly.transRN : null;
  const lyADR = lyMonthly && lyRN && lyRN > 0
    ? Math.round(lyMonthly.totalRevenue / lyRN)
    : null;
  const lyRevenue = lyMonthly?.totalRevenue ?? null;

  // OTB and Budget context
  const otbMonthly = context?.otb.monthly;
  const otbRN = otbMonthly ? otbMonthly.grpRN + otbMonthly.transRN : 0;
  const otbRevenue = otbMonthly?.totalRevenue ?? 0;
  const otbADR = otbRN > 0 ? Math.round(otbRevenue / otbRN) : 0;
  const budgetCtx = context?.budget;
  const budgetRN = budgetCtx ? budgetCtx.grpRN + budgetCtx.transRN : 0;

  const statAccounts = context?.statAccounts ?? [];
  const hasStatAccounts = statAccounts.length > 0;
  const SA_COLORS = ['blue', 'orange', 'emerald', 'violet', 'amber', 'cyan', 'rose', 'teal'];
  const SA_BG = ['bg-blue-500', 'bg-orange-500', 'bg-emerald-500', 'bg-violet-500', 'bg-amber-500', 'bg-cyan-500', 'bg-rose-500', 'bg-teal-500'];
  const SA_TEXT = ['text-blue-600', 'text-orange-500', 'text-emerald-600', 'text-violet-600', 'text-amber-600', 'text-cyan-600', 'text-rose-600', 'text-teal-600'];

  return (
    <div className="p-5 grid grid-cols-[1fr,260px] gap-5">
      {/* Left: inputs */}
      <div>
        {/* Forecast mode toggle + input mode toggle */}
        <div className="flex items-center gap-3 mb-4">
          {hasStatAccounts && (
            <div className="flex rounded overflow-hidden border border-slate-200">
              <button
                onClick={() => onForecastModeChange('basic')}
                disabled={isReadOnly}
                className={`px-3 py-1 text-xs font-semibold transition-colors ${
                  forecastMode === 'basic' ? 'bg-slate-800 text-white' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
                }`}
              >
                Basic
              </button>
              <button
                onClick={() => onForecastModeChange('stat_account')}
                disabled={isReadOnly}
                className={`px-3 py-1 text-xs font-semibold transition-colors ${
                  forecastMode === 'stat_account' ? 'bg-slate-800 text-white' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
                }`}
              >
                By Stat Account
              </button>
            </div>
          )}
          {forecastMode === 'basic' && (
            <div className="flex rounded overflow-hidden border border-slate-200 w-48">
              {([{ id: 'rn', label: 'Room Nights' }, { id: 'pct', label: 'Percentage' }] as { id: InputMode; label: string }[]).map(m => (
                <button
                  key={m.id}
                  onClick={() => setInputMode(m.id)}
                  disabled={isReadOnly}
                  className={`flex-1 py-1 text-xs font-semibold transition-colors ${
                    inputMode === m.id ? 'bg-slate-800 text-white' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {forecastMode === 'basic' ? (
          <>
            {/* Group */}
            <div className="mb-4">
              <div className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-sm bg-blue-500 inline-block" />
                Group
              </div>
              <div className="flex gap-2">
                {inputMode === 'rn'
                  ? <NumberField label="Room Nights" value={inputs.groupRN} onChange={v => setField('groupRN', v)} color="blue" disabled={isReadOnly} />
                  : <NumberField label="Occupancy %" value={groupPctDynamic} onChange={v => setField('groupPct', v)} suffix="%" color="blue" disabled={isReadOnly} />
                }
                <NumberField label="ADR" value={inputs.groupADR} onChange={v => setField('groupADR', v)} prefix="$" color="blue" disabled={isReadOnly} />
              </div>
              <div className="flex justify-between mt-1 px-0.5 text-xs text-slate-400">
                <span>{fmt(inputs.groupRN)} RN · {groupPctDynamic}%</span>
                <span className="font-semibold text-slate-600">{fmtDollar(groupRevenue)}</span>
              </div>
              {(budgetCtx || otbMonthly) && (
                <div className="mt-1 px-0.5 text-[10px] text-slate-400 flex gap-3">
                  {budgetCtx && budgetCtx.grpRN > 0 && <span>Budget: {fmt(budgetCtx.grpRN)} RN</span>}
                  {otbMonthly && otbMonthly.grpRN > 0 && <span>OTB: {fmt(otbMonthly.grpRN)} RN</span>}
                  {lyMonthly && lyMonthly.grpRN > 0 && <span>LY: {fmt(lyMonthly.grpRN)} RN</span>}
                </div>
              )}
            </div>

            {/* Transient */}
            <div className="mb-4">
              <div className="text-xs font-bold text-orange-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-sm bg-orange-500 inline-block" />
                Transient
              </div>
              <div className="flex gap-2">
                {inputMode === 'rn'
                  ? <NumberField label="Room Nights" value={inputs.transRN} onChange={v => setField('transRN', v)} color="orange" disabled={isReadOnly} />
                  : <NumberField label="Occupancy %" value={transPctDynamic} onChange={v => setField('transPct', v)} suffix="%" color="orange" disabled={isReadOnly} />
                }
                <NumberField label="ADR" value={inputs.transADR} onChange={v => setField('transADR', v)} prefix="$" color="orange" disabled={isReadOnly} />
              </div>
              <div className="flex justify-between mt-1 px-0.5 text-xs text-slate-400">
                <span>{fmt(inputs.transRN)} RN · {transPctDynamic}%</span>
                <span className="font-semibold text-slate-600">{fmtDollar(transRevenue)}</span>
              </div>
              {(budgetCtx || otbMonthly) && (
                <div className="mt-1 px-0.5 text-[10px] text-slate-400 flex gap-3">
                  {budgetCtx && budgetCtx.transRN > 0 && <span>Budget: {fmt(budgetCtx.transRN)} RN</span>}
                  {otbMonthly && otbMonthly.transRN > 0 && <span>OTB: {fmt(otbMonthly.transRN)} RN</span>}
                  {lyMonthly && lyMonthly.transRN > 0 && <span>LY: {fmt(lyMonthly.transRN)} RN</span>}
                </div>
              )}
            </div>

            {/* Composition bar */}
            <div className="mb-3">
              <div className="flex h-2 rounded-full overflow-hidden bg-slate-100">
                <div className="fv3-comp-group bg-blue-500 transition-all duration-200" style={{ width: `${Math.min(groupPctDynamic, 100)}%` }} />
                <div className="fv3-comp-trans bg-orange-500 transition-all duration-200" style={{ width: `${Math.min(transPctDynamic, 100 - groupPctDynamic)}%` }} />
              </div>
              <div className="flex justify-between mt-1 text-[10px] text-slate-400">
                <span><span className="font-semibold text-blue-500">{groupPctDynamic}%</span> group</span>
                <span><span className={`font-bold ${totalOccPct > 100 ? 'text-red-500' : 'text-slate-700'}`}>{totalOccPct}% total</span></span>
                <span><span className="font-semibold text-orange-500">{transPctDynamic}%</span> transient</span>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Stat Account mode: one row per mapped account */}
            <div className="space-y-3 max-h-[340px] overflow-y-auto pr-1">
              {statAccounts.map((sa, idx) => {
                const inp = statAccountInputs[sa.id] ?? { rn: 0, adr: 0 };
                const saRevenue = inp.rn * inp.adr;
                const saPct = totalAvail > 0 ? Math.round(inp.rn / totalAvail * 1000) / 10 : 0;
                const colorIdx = idx % SA_COLORS.length;
                return (
                  <div key={sa.id} className="mb-1">
                    <div className={`text-xs font-bold ${SA_TEXT[colorIdx]} uppercase tracking-wider mb-1.5 flex items-center gap-1.5`}>
                      <span className={`w-2 h-2 rounded-sm ${SA_BG[colorIdx]} inline-block`} />
                      {sa.accountName}
                      <span className="text-[10px] font-normal text-slate-400 normal-case">({sa.accountNumber})</span>
                      {sa.operation === 'subtract' && (
                        <span className="text-[9px] font-semibold text-red-400 bg-red-50 px-1 py-0.5 rounded">subtracts</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <NumberField label="Room Nights" value={inp.rn} onChange={v => onStatAccountInputChange(sa.id, 'rn', v)} color={SA_COLORS[colorIdx]} disabled={isReadOnly} />
                      <NumberField label="ADR" value={inp.adr} onChange={v => onStatAccountInputChange(sa.id, 'adr', v)} prefix="$" color={SA_COLORS[colorIdx]} disabled={isReadOnly} />
                    </div>
                    <div className="flex justify-between mt-1 px-0.5 text-xs text-slate-400">
                      <span>{fmt(inp.rn)} RN · {saPct}%</span>
                      <span className="font-semibold text-slate-600">{fmtDollar(saRevenue)}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Composition bar for stat accounts */}
            <div className="mb-3 mt-3">
              <div className="flex h-2 rounded-full overflow-hidden bg-slate-100">
                {statAccounts.map((sa, idx) => {
                  const inp = statAccountInputs[sa.id] ?? { rn: 0, adr: 0 };
                  const pct = totalAvail > 0 ? (inp.rn / totalAvail) * 100 : 0;
                  return <div key={sa.id} className={`${SA_BG[idx % SA_COLORS.length]} transition-all duration-200`} style={{ width: `${Math.min(pct, 100)}%` }} />;
                })}
              </div>
              <div className="flex justify-between mt-1 text-[10px] text-slate-400">
                <span>{statAccounts.length} segment{statAccounts.length !== 1 ? 's' : ''}</span>
                <span><span className={`font-bold ${totalOccPct > 100 ? 'text-red-500' : 'text-slate-700'}`}>{totalOccPct}% total</span></span>
              </div>
            </div>
          </>
        )}

        {totalOccPct > 100 && (
          <div className="p-2.5 rounded-lg bg-red-50 border border-red-200 text-xs font-semibold text-red-600 mb-3">
            Occupancy exceeds 100% — adjust room nights
          </div>
        )}
      </div>

      {/* Right: totals + LY */}
      <div className="space-y-3">
        <div className="p-3.5 rounded-lg bg-orange-50 border border-orange-200">
          <div className="text-xs font-bold text-orange-600 uppercase tracking-wider mb-2">
            {MONTH_NAMES[activeMonth - 1]} {year} Totals
          </div>
          {(() => {
            const rows: { l: string; v: string; bold?: boolean; clr?: string }[] = [
              { l: 'Total Revenue', v: fmtDollar(totalRevenue), bold: true, clr: 'text-orange-600' },
            ];
            if (forecastMode === 'stat_account') {
              for (const sa of statAccounts) {
                const inp = statAccountInputs[sa.id] ?? { rn: 0, adr: 0 };
                rows.push({ l: sa.accountName, v: fmtDollar(inp.rn * inp.adr) });
              }
            } else {
              rows.push({ l: 'Group Revenue', v: fmtDollar(groupRevenue) });
              rows.push({ l: 'Transient Revenue', v: fmtDollar(transRevenue) });
            }
            return rows;
          })().map((r, i) => (
            <div key={i} className="flex justify-between py-0.5 text-xs">
              <span className="text-slate-500">{r.l}</span>
              <span className={`${r.bold ? 'font-bold' : 'font-semibold'} ${r.clr ?? 'text-slate-800'}`}>{r.v}</span>
            </div>
          ))}
          {(() => {
            const dim = context?.daysInMonth ?? new Date(year, activeMonth, 0).getDate();
            const avgRooms = dim > 0 ? Math.round(totalAvail / dim * 10) / 10 : 0;
            return (
              <div className="mt-1.5 mb-1 p-2 rounded-md bg-slate-50 border border-slate-200">
                <div className="flex items-center gap-2 text-xs">
                  <div className="flex-1">
                    <div className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Avg Rooms</div>
                    <input
                      type="number"
                      value={avgRooms || ''}
                      placeholder="0"
                      disabled={isReadOnly}
                      onChange={e => {
                        const v = Number(e.target.value);
                        onAvailableRNChange(Math.round(v * dim));
                      }}
                      className="w-full text-sm font-bold text-slate-800 bg-transparent border-none outline-none disabled:cursor-not-allowed"
                    />
                  </div>
                  <span className="text-slate-300 font-bold">&times;</span>
                  <div className="flex-shrink-0 text-center">
                    <div className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Days</div>
                    <span className="text-sm font-bold text-slate-600">{dim}</span>
                  </div>
                  <span className="text-slate-300 font-bold">=</span>
                  <div className="flex-1">
                    <div className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Avail RN</div>
                    <input
                      type="number"
                      value={totalAvail || ''}
                      placeholder="0"
                      disabled={isReadOnly}
                      onChange={e => onAvailableRNChange(Number(e.target.value))}
                      className="w-full text-sm font-bold text-slate-800 bg-transparent border-none outline-none disabled:cursor-not-allowed"
                    />
                  </div>
                </div>
              </div>
            );
          })()}
          {[
            { l: 'Occupied RN', v: fmt(occRN) },
            { l: 'Total Occ %', v: `${totalOccPct}%`, bold: true, clr: totalOccPct > 100 ? 'text-red-500' : totalOccPct > 0 ? 'text-green-600' : 'text-slate-800' },
            { l: 'Blended ADR', v: fmtDollar(blendedADR) },
          ].map((r, i) => (
            <div key={i} className="flex justify-between py-0.5 text-xs">
              <span className="text-slate-500">{r.l}</span>
              <span className={`${r.bold ? 'font-bold' : 'font-semibold'} ${r.clr ?? 'text-slate-800'}`}>{r.v}</span>
            </div>
          ))}
        </div>

        {budgetRN > 0 && budgetCtx && (
          <div className="p-3.5 rounded-lg bg-blue-50 border border-blue-200">
            <div className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-2">
              Budget
            </div>
            {[
              { l: 'Available RN', v: fmt(totalAvail) },
              { l: 'Occupied RN', v: fmt(budgetRN) },
              { l: 'Occ %', v: totalAvail > 0 ? `${Math.round(budgetRN / totalAvail * 1000) / 10}%` : '—' },
              { l: 'ADR', v: budgetCtx.adr > 0 ? fmtDollar(budgetCtx.adr) : '—' },
              { l: 'Revenue', v: budgetCtx.revenue > 0 ? fmtDollar(budgetCtx.revenue) : '—' },
            ].map((r, i) => (
              <div key={i} className="flex justify-between py-0.5 text-xs">
                <span className="text-slate-500">{r.l}</span>
                <span className="font-semibold text-slate-800">{r.v}</span>
              </div>
            ))}
          </div>
        )}

        {otbRN > 0 && (
          <div className="p-3.5 rounded-lg bg-green-50 border border-green-200">
            <div className="text-xs font-bold text-green-600 uppercase tracking-wider mb-2">
              On the Books
            </div>
            {[
              { l: 'Available RN', v: fmt(totalAvail) },
              { l: 'Occupied RN', v: fmt(otbRN) },
              { l: 'Occ %', v: totalAvail > 0 ? `${Math.round(otbRN / totalAvail * 1000) / 10}%` : '—' },
              { l: 'ADR', v: otbADR > 0 ? fmtDollar(otbADR) : '—' },
              { l: 'Revenue', v: fmtDollar(otbRevenue) },
            ].map((r, i) => (
              <div key={i} className="flex justify-between py-0.5 text-xs">
                <span className="text-slate-500">{r.l}</span>
                <span className="font-semibold text-slate-800">{r.v}</span>
              </div>
            ))}
          </div>
        )}

        {lyMonthly && lyOcc !== null && lyADR !== null && lyRevenue !== null && (
          <div className="p-3.5 rounded-lg bg-slate-50 border border-slate-200">
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
              vs. Last Year
            </div>
            {[
              { l: 'LY Occ %', v: `${lyOcc}%`, delta: totalOccPct - lyOcc, pct: true },
              { l: 'LY ADR', v: fmtDollar(lyADR), delta: blendedADR - lyADR },
              { l: 'LY Revenue', v: fmtDollar(lyRevenue), delta: totalRevenue - lyRevenue },
            ].map((r, i) => (
              <div key={i} className="flex justify-between items-center py-0.5 text-xs">
                <span className="text-slate-500">{r.l}</span>
                <span className="flex gap-2 items-center">
                  <span className="text-slate-700">{r.v}</span>
                  <span className={`font-semibold ${r.delta >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                    {r.delta >= 0 ? '+' : ''}
                    {Math.abs(r.delta) > 100 ? fmtDollar(Math.round(r.delta)) : `${r.delta.toFixed(1)}${r.pct ? 'pp' : ''}`}
                  </span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── NumberField helper ────────────────────────────────────────

interface NumberFieldProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
  prefix?: string;
  suffix?: string;
  color?: string;
  disabled?: boolean;
}

const COLOR_MAP: Record<string, string> = {
  blue: 'text-blue-600', orange: 'text-orange-500', emerald: 'text-emerald-600',
  violet: 'text-violet-600', amber: 'text-amber-600', cyan: 'text-cyan-600',
  rose: 'text-rose-600', teal: 'text-teal-600',
};

function NumberField({ label, value, onChange, prefix, suffix, color = 'orange', disabled }: NumberFieldProps) {
  const colorClass = COLOR_MAP[color] ?? 'text-orange-500';
  return (
    <div className="flex-1 px-2.5 py-1.5 rounded-md border border-slate-200 bg-slate-50">
      <div className="text-[9px] font-semibold text-slate-400 uppercase tracking-widest mb-0.5">{label}</div>
      <div className="flex items-baseline gap-0.5">
        {prefix && <span className={`text-xs font-semibold ${colorClass}`}>{prefix}</span>}
        <input
          type="number"
          value={value || ''}
          onChange={e => onChange(Number(e.target.value))}
          disabled={disabled}
          placeholder="0"
          className={`w-full text-lg font-bold bg-transparent border-none outline-none ${colorClass} disabled:cursor-not-allowed`}
        />
        {suffix && <span className={`text-xs font-semibold ${colorClass}`}>{suffix}</span>}
      </div>
    </div>
  );
}

// ── Daily Tab ─────────────────────────────────────────────────

interface DailyTabProps {
  dailyData: Record<number, DayData>;
  setDailyData: React.Dispatch<React.SetStateAction<Record<number, DayData>>>;
  spreadMethod: string | null;
  applySpread: (method: string) => void;
  dailyRef: DailyRef;
  setDailyRef: (r: DailyRef) => void;
  daysInMonth: number;
  totalRooms: number;
  totalAvail: number;
  occRN: number;
  totalRevenue: number;
  dailyTotals: { gRN: number; tRN: number; totalRN: number; gRev: number; tRev: number; totalRev: number };
  context: RevenueContext | null;
  activeMonth: number;
  year: number;
  isReadOnly: boolean;
}

function DailyTab({
  dailyData, setDailyData, spreadMethod, applySpread,
  dailyRef, setDailyRef, daysInMonth, totalRooms, totalAvail,
  occRN, totalRevenue, dailyTotals, context, activeMonth, year, isReadOnly,
}: DailyTabProps) {
  const lyMap: Record<number, { grpRN: number; transRN: number; grpRevenue: number; transRevenue: number }> = {};
  context?.ly.daily.forEach(d => { lyMap[d.day] = d; });
  const otbMap: Record<number, { grpRN: number; transRN: number; grpRevenue: number; transRevenue: number }> = {};
  context?.otb.daily.forEach(d => { otbMap[d.day] = d; });

  const SPREADS = [
    { id: 'ly_pattern', label: 'LY Pattern' },
    { id: 'lm_trend', label: 'LM Trend' },
    { id: 'otb', label: 'On the Books' },
    { id: 'even', label: 'Even' },
    { id: 'weekend', label: 'Weekend Weighted' },
  ];

  const DAILY_FIELDS = ['groupRN', 'groupADR', 'transRN', 'transADR'] as const;

  const handleDailyKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, day: number, field: string) => {
    const colIdx = DAILY_FIELDS.indexOf(field as typeof DAILY_FIELDS[number]);
    if (colIdx === -1) return;

    let nextDay = day;
    let nextCol = colIdx;

    if (e.key === 'Enter') {
      e.preventDefault();
      nextDay = day < daysInMonth ? day + 1 : 1;
    } else if (e.key === 'Tab') {
      e.preventDefault();
      if (e.shiftKey) {
        if (colIdx > 0) { nextCol = colIdx - 1; }
        else { nextCol = DAILY_FIELDS.length - 1; nextDay = day > 1 ? day - 1 : daysInMonth; }
      } else {
        if (colIdx < DAILY_FIELDS.length - 1) { nextCol = colIdx + 1; }
        else { nextCol = 0; nextDay = day < daysInMonth ? day + 1 : 1; }
      }
    } else {
      return;
    }

    const next = e.currentTarget.ownerDocument.querySelector<HTMLInputElement>(`[data-daily-cell="${nextDay}-${DAILY_FIELDS[nextCol]}"]`);
    if (next) { next.focus(); next.select(); }
  };

  const rnVariance = dailyTotals.totalRN - occRN;
  const revVariance = dailyTotals.totalRev - totalRevenue;
  const hasSpread = dailyTotals.totalRN > 0;

  return (
    <div className="p-4">
      {/* Spread controls */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="text-xs font-semibold text-slate-500">Spread monthly totals:</span>
        {SPREADS.map(s => (
          <button
            key={s.id}
            onClick={() => !isReadOnly && applySpread(s.id)}
            disabled={isReadOnly}
            className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
              spreadMethod === s.id
                ? 'bg-orange-50 border border-orange-400 text-orange-600 font-bold'
                : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'
            } disabled:cursor-not-allowed`}
          >
            {s.label}
          </button>
        ))}
        <div className="flex-1" />
        <span className="text-xs text-slate-400">Reference:</span>
        {(['none', 'ly', 'otb'] as DailyRef[]).map(r => (
          <button
            key={r}
            onClick={() => setDailyRef(r)}
            className={`px-2 py-1 rounded text-[10px] font-semibold uppercase ${
              dailyRef === r ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
            }`}
          >
            {r === 'none' ? 'None' : r.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Variance summary */}
      {hasSpread && (
        <div className={`flex gap-4 mb-3 p-2.5 px-3 rounded-lg border text-xs ${
          rnVariance === 0 && revVariance === 0
            ? 'bg-green-50 border-green-200'
            : 'bg-amber-50 border-amber-200'
        }`}>
          {[
            { l: 'Daily Total RN', v: fmt(dailyTotals.totalRN) },
            { l: 'Daily Total Rev', v: fmtDollar(dailyTotals.totalRev) },
            { l: 'RN Variance', v: (rnVariance >= 0 ? '+' : '') + fmt(rnVariance), isDelta: true, raw: rnVariance },
            { l: 'Rev Variance', v: (revVariance >= 0 ? '+' : '') + fmtDollar(Math.round(revVariance)), isDelta: true, raw: revVariance },
          ].map((r, i) => (
            <div key={i} className="flex-1">
              <div className="text-[9px] text-slate-400 uppercase tracking-wider">{r.l}</div>
              <div className={`text-sm font-bold ${
                r.isDelta ? (r.raw === 0 ? 'text-green-600' : 'text-red-500') : 'text-slate-800'
              }`}>{r.v}</div>
            </div>
          ))}
        </div>
      )}

      {/* Daily table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-auto max-h-[calc(90vh-380px)]">
        <table className="w-full border-collapse text-xs">
          <thead className="sticky top-0 z-10">
            <tr className="bg-slate-50 border-b-2 border-slate-200">
              <th className="text-left px-3 py-2 text-[10px] text-slate-400 font-semibold uppercase w-20">Day</th>
              <th className="text-right px-2 py-2 text-[10px] text-blue-600 font-bold uppercase">Grp RN</th>
              <th className="text-right px-2 py-2 text-[10px] text-blue-600 font-bold uppercase">Grp ADR</th>
              <th className="text-right px-2 py-2 text-[10px] text-orange-500 font-bold uppercase">Trans RN</th>
              <th className="text-right px-2 py-2 text-[10px] text-orange-500 font-bold uppercase">Trans ADR</th>
              <th className="text-right px-2 py-2 text-[10px] text-slate-500 font-semibold uppercase border-l-2 border-slate-200">Total RN</th>
              <th className="text-right px-2 py-2 text-[10px] text-slate-500 font-semibold uppercase">Occ%</th>
              <th className="text-right px-2 py-2 text-[10px] text-slate-500 font-semibold uppercase">Revenue</th>
              {dailyRef === 'ly' && (
                <>
                  <th className="text-right px-2 py-2 text-[10px] text-slate-400 font-semibold uppercase border-l-2 border-slate-200">LY RN</th>
                  <th className="text-right px-2 py-2 text-[10px] text-slate-400 font-semibold uppercase">LY Rev</th>
                </>
              )}
              {dailyRef === 'otb' && (
                <>
                  <th className="text-right px-2 py-2 text-[10px] text-green-600 font-semibold uppercase border-l-2 border-slate-200">OTB RN</th>
                  <th className="text-right px-2 py-2 text-[10px] text-green-600 font-semibold uppercase">OTB Rev</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(d => {
              const dd = dailyData[d] ?? { groupRN: 0, transRN: 0, groupADR: 0, transADR: 0, edited: false };
              const dow = new Date(year, activeMonth - 1, d).getDay();
              const isWk = dow === 0 || dow === 5 || dow === 6;
              const totalRN = dd.groupRN + dd.transRN;
              const roomsPerNight = daysInMonth > 0 ? totalAvail / daysInMonth : 0;
              const dayOcc = roomsPerNight > 0 ? Math.round(totalRN / roomsPerNight * 1000) / 10 : 0;
              const dayRev = dd.groupRN * dd.groupADR + dd.transRN * dd.transADR;
              const lyDay = lyMap[d];
              const otbDay = otbMap[d];

              return (
                <tr
                  key={d}
                  className={`border-b border-slate-100 ${
                    dd.edited ? 'bg-amber-50' : isWk ? 'bg-slate-50' : 'bg-white'
                  }`}
                >
                  <td className="px-3 py-1.5 font-semibold">
                    <span className={isWk ? 'text-orange-500' : 'text-slate-700'}>{DAY_NAMES[dow]}</span>
                    <span className="text-slate-400 ml-1">{d}</span>
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <input
                      type="number"
                      value={dd.groupRN || ''}
                      placeholder="—"
                      disabled={isReadOnly}
                      data-daily-cell={`${d}-groupRN`}
                      onChange={e => setDailyData(p => ({ ...p, [d]: { ...p[d], groupRN: Number(e.target.value), edited: true } }))}
                      onKeyDown={e => handleDailyKeyDown(e, d, 'groupRN')}
                      onFocus={e => e.target.select()}
                      className="w-14 text-right text-blue-600 font-semibold bg-transparent border-none outline-none disabled:cursor-not-allowed"
                    />
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <input
                      type="number"
                      value={dd.groupADR || ''}
                      placeholder="—"
                      disabled={isReadOnly}
                      data-daily-cell={`${d}-groupADR`}
                      onChange={e => setDailyData(p => ({ ...p, [d]: { ...p[d], groupADR: Number(e.target.value), edited: true } }))}
                      onKeyDown={e => handleDailyKeyDown(e, d, 'groupADR')}
                      onFocus={e => e.target.select()}
                      className="w-16 text-right text-blue-500 bg-transparent border-none outline-none disabled:cursor-not-allowed"
                    />
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <input
                      type="number"
                      value={dd.transRN || ''}
                      placeholder="—"
                      disabled={isReadOnly}
                      data-daily-cell={`${d}-transRN`}
                      onChange={e => setDailyData(p => ({ ...p, [d]: { ...p[d], transRN: Number(e.target.value), edited: true } }))}
                      onKeyDown={e => handleDailyKeyDown(e, d, 'transRN')}
                      onFocus={e => e.target.select()}
                      className="w-14 text-right text-orange-500 font-semibold bg-transparent border-none outline-none disabled:cursor-not-allowed"
                    />
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <input
                      type="number"
                      value={dd.transADR || ''}
                      placeholder="—"
                      disabled={isReadOnly}
                      data-daily-cell={`${d}-transADR`}
                      onChange={e => setDailyData(p => ({ ...p, [d]: { ...p[d], transADR: Number(e.target.value), edited: true } }))}
                      onKeyDown={e => handleDailyKeyDown(e, d, 'transADR')}
                      onFocus={e => e.target.select()}
                      className="w-16 text-right text-orange-400 bg-transparent border-none outline-none disabled:cursor-not-allowed"
                    />
                  </td>
                  <td className="px-2 py-1.5 text-right font-semibold text-slate-700 border-l-2 border-slate-200">{totalRN || '—'}</td>
                  <td className={`px-2 py-1.5 text-right font-semibold ${dayOcc > 100 ? 'text-red-500' : dayOcc > 90 ? 'text-orange-500' : 'text-slate-600'}`}>
                    {dayOcc > 0 ? `${dayOcc}%` : '—'}
                  </td>
                  <td className="px-2 py-1.5 text-right font-semibold text-slate-800">
                    {dayRev > 0 ? fmtDollar(dayRev) : '—'}
                  </td>
                  {dailyRef === 'ly' && (
                    <>
                      <td className="px-2 py-1.5 text-right text-slate-400 border-l-2 border-slate-200">
                        {lyDay ? fmt(lyDay.grpRN + lyDay.transRN) : '—'}
                      </td>
                      <td className="px-2 py-1.5 text-right text-slate-400">
                        {lyDay ? fmtDollar(lyDay.grpRevenue + lyDay.transRevenue) : '—'}
                      </td>
                    </>
                  )}
                  {dailyRef === 'otb' && (
                    <>
                      <td className="px-2 py-1.5 text-right text-green-600 font-medium border-l-2 border-slate-200">
                        {otbDay ? fmt(otbDay.grpRN + otbDay.transRN) : '—'}
                      </td>
                      <td className="px-2 py-1.5 text-right text-green-600 font-medium">
                        {otbDay ? fmtDollar(otbDay.grpRevenue + otbDay.transRevenue) : '—'}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
            {/* Totals row */}
            <tr className="bg-slate-50 border-t-2 border-slate-300 font-bold">
              <td className="px-3 py-2 text-xs text-slate-700">TOTAL</td>
              <td className="px-2 py-2 text-right text-xs text-blue-600">{fmt(dailyTotals.gRN)}</td>
              <td className="px-2 py-2 text-right text-xs text-slate-400">—</td>
              <td className="px-2 py-2 text-right text-xs text-orange-500">{fmt(dailyTotals.tRN)}</td>
              <td className="px-2 py-2 text-right text-xs text-slate-400">—</td>
              <td className="px-2 py-2 text-right text-xs text-slate-800 border-l-2 border-slate-200">{fmt(dailyTotals.totalRN)}</td>
              <td className="px-2 py-2 text-right text-xs text-slate-700">
                {totalAvail > 0 ? `${Math.round(dailyTotals.totalRN / totalAvail * 1000) / 10}%` : '—'}
              </td>
              <td className="px-2 py-2 text-right text-xs text-orange-600">{fmtDollar(dailyTotals.totalRev)}</td>
              {dailyRef === 'ly' && (
                <>
                  <td className="px-2 py-2 text-right text-xs text-slate-400 border-l-2 border-slate-200">
                    {context ? fmt(context.ly.monthly.grpRN + context.ly.monthly.transRN) : '—'}
                  </td>
                  <td className="px-2 py-2 text-right text-xs text-slate-400">
                    {context ? fmtDollar(context.ly.monthly.totalRevenue) : '—'}
                  </td>
                </>
              )}
              {dailyRef === 'otb' && (
                <>
                  <td className="px-2 py-2 text-right text-xs text-green-600 border-l-2 border-slate-200">
                    {context ? fmt(context.otb.monthly.grpRN + context.otb.monthly.transRN) : '—'}
                  </td>
                  <td className="px-2 py-2 text-right text-xs text-green-600">
                    {context ? fmtDollar(context.otb.monthly.totalRevenue) : '—'}
                  </td>
                </>
              )}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Accounts Tab ──────────────────────────────────────────────

interface AccountsTabProps {
  accounts: RevenueContextAccount[];
  acctMethod: AcctMethod;
  setAcctMethod: (m: AcctMethod) => void;
  manualPcts: Record<number, number>;
  setManualPcts: React.Dispatch<React.SetStateAction<Record<number, number>>>;
  onManualPctChange: (chartOfAccountId: number, newPct: number) => void;
  totalRevenue: number;
  occRN: number;
  activeMonth: number;
  year: number;
  isReadOnly: boolean;
  forecastMode: ForecastMode;
  statAccounts: StatAccountMapping[];
  statAccountInputs: Record<number, StatAccountInput>;
  availableRoomNights: number;
  onRevenueAccountChange: (statAccountId: number, revenueChartOfAccountId: number | null) => void;
}



function AccountsTab({
  accounts, acctMethod, setAcctMethod, manualPcts, setManualPcts, onManualPctChange,
  totalRevenue, occRN, activeMonth, year, isReadOnly,
  forecastMode, statAccounts, statAccountInputs, availableRoomNights, onRevenueAccountChange,
}: AccountsTabProps) {

  // Stat mode renders StatAccountMappingPanel instead (see modal render).

  // ── Basic Mode: percentage distribution across all revenue accounts ──
  const METHODS: { id: AcctMethod; label: string }[] = [
    { id: 'ly_pct', label: 'Last Year % (GL)' },
    { id: 'budget_pct', label: 'Budget % (GL)' },
    { id: 'manual', label: 'Manual' },
  ];

  const totalRefAmt = accounts.reduce((s, a) => s + (acctMethod === 'ly_pct' ? a.lyAmt : a.budgetAmt), 0);

  return (
    <div className="p-5">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="text-xs text-slate-600">
          Distribute <strong className="text-orange-500">{fmtDollar(Math.round(totalRevenue))}</strong> across revenue accounts using:
        </span>
        {METHODS.map(m => (
          <button
            key={m.id}
            onClick={() => !isReadOnly && setAcctMethod(m.id)}
            disabled={isReadOnly}
            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
              acctMethod === m.id
                ? 'bg-orange-50 border border-orange-400 text-orange-600 font-bold'
                : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'
            } disabled:cursor-not-allowed`}
          >
            {m.label}
          </button>
        ))}
      </div>

      <p className="text-xs text-slate-500 mb-3 leading-relaxed">
        {acctMethod === 'ly_pct' && `Percentages from last year's GL data for ${MONTH_NAMES[activeMonth - 1]}. Each account's share of total rooms revenue is applied to this year's projection.`}
        {acctMethod === 'budget_pct' && `Percentages from the ${year} budget. Each account's budgeted share of rooms revenue is applied to the projected total.`}
        {acctMethod === 'manual' && 'Enter custom percentages for each account. They will be normalized to sum to 100% on apply.'}
      </p>

      {accounts.length === 0 ? (
        <div className="py-10 text-center text-sm text-slate-400">
          No revenue accounts found for this department.
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="bg-slate-50 border-b-2 border-slate-200">
                <th className="text-left px-3 py-2 text-[10px] text-slate-400 font-semibold uppercase">Account</th>
                <th className="text-right px-2 py-2 text-[10px] text-slate-400 font-semibold uppercase">
                  {acctMethod === 'ly_pct' ? 'LY %' : acctMethod === 'budget_pct' ? 'Budget %' : 'Manual %'}
                </th>
                <th className="text-right px-2 py-2 text-[10px] text-slate-400 font-semibold uppercase">
                  {acctMethod === 'ly_pct' ? 'LY Amount' : acctMethod === 'budget_pct' ? 'Budget Amt' : ''}
                </th>
                <th className="text-right px-3 py-2 text-[10px] text-orange-500 font-bold uppercase">Projected</th>
                <th className="text-right px-2 py-2 text-[10px] text-slate-400 font-semibold uppercase">Per Night</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((a) => {
                const pct = acctMethod === 'ly_pct' ? a.lyPct
                  : acctMethod === 'budget_pct' ? a.budgetPct
                  : (manualPcts[a.chartOfAccountId] ?? a.lyPct);
                const refAmt = acctMethod === 'ly_pct' ? a.lyAmt : acctMethod === 'budget_pct' ? a.budgetAmt : 0;
                const projected = Math.round(totalRevenue * pct / 100);
                const perNight = occRN > 0 ? (projected / occRN).toFixed(2) : '0.00';

                return (
                  <tr key={a.chartOfAccountId} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <div className="font-semibold text-slate-700">{a.accountName}</div>
                      <div className="text-[10px] text-slate-400">{a.accountNumber}</div>
                    </td>
                    <td className="px-2 py-2 text-right">
                      {acctMethod === 'manual' ? (
                        <input
                          type="number"
                          value={manualPcts[a.chartOfAccountId] ?? a.lyPct}
                          disabled={isReadOnly}
                          onChange={e => onManualPctChange(a.chartOfAccountId, Number(e.target.value))}
                          className="w-14 text-right text-xs font-semibold text-orange-500 border border-slate-200 rounded px-1.5 py-0.5 bg-slate-50 disabled:cursor-not-allowed"
                        />
                      ) : (
                        <span className="font-semibold text-slate-700">{pct.toFixed(1)}%</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right text-slate-400">
                      {refAmt > 0 ? fmtDollar(refAmt) : ''}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-orange-500 text-xs">
                      {fmtDollar(projected)}
                    </td>
                    <td className="px-2 py-2 text-right text-slate-500">
                      ${perNight}
                    </td>
                  </tr>
                );
              })}
              {/* Total row */}
              <tr className="bg-orange-50 border-t-2 border-orange-200">
                <td className="px-3 py-2 font-bold text-slate-900">Total Rooms Revenue</td>
                <td className="px-2 py-2 text-right font-bold text-slate-700">100%</td>
                <td className="px-2 py-2 text-right text-slate-500">
                  {acctMethod !== 'manual' && totalRefAmt > 0 ? fmtDollar(totalRefAmt) : ''}
                </td>
                <td className="px-3 py-2 text-right font-bold text-orange-500">
                  {fmtDollar(Math.round(totalRevenue))}
                </td>
                <td className="px-2 py-2 text-right font-semibold text-slate-600">
                  ${occRN > 0 ? (totalRevenue / occRN).toFixed(2) : '0.00'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Overview Tab ────────────────────────────────────────────────

interface OverviewTabProps {
  overviewData: Record<number, { groupRN: number; transRN: number; groupADR: number; transADR: number; avgRooms: number; days: number; statAccountInputs?: Record<number, StatAccountInput> }>;
  updateField: (month: number, field: string, val: number) => void;
  overviewLoaded: boolean;
  year: number;
  versionId: number;
  locationId: number;
  orgId: number;
  isBudget?: boolean;
  departmentName?: string;
  chartOfAccountDepartmentId?: number;
  onSeedMonths?: (updates: Record<number, Record<number, StatAccountInput>>) => void;
  isReadOnly: boolean;
  onMonthClick: (month: number) => void;
  onDailyClick: (month: number) => void;
  onAccountsClick: (month: number) => void;
  forecastMode: ForecastMode;
  statAccounts: StatAccountMapping[];
  onStatAccountFieldUpdate: (month: number, saId: number, field: 'rn' | 'adr', val: number) => void;
  onCalendarClick?: (month: number, sa: StatAccountMapping) => void;
  savedDailyPatch?: { month: number; saId: number; dailyRN: Record<number, number>; dailyADR: Record<number, number> } | null;
  onInlineDailyRNEdit?: (mo: number, sa: StatAccountMapping, dailyRN: Record<number, number>, dailyADR: Record<number, number>) => void;
}

interface DailyRefRow {
  otbRooms: number;
  otbRevenue: number;
  rmsRooms: number;
  rmsRevenue: number;
}
type DailyRefMap = Record<number, Record<number, DailyRefRow>>; // [month][day]

function OverviewTab({
  overviewData, updateField, overviewLoaded, year, versionId, locationId, orgId, isBudget = false, departmentName, chartOfAccountDepartmentId, onSeedMonths, isReadOnly,
  onMonthClick, onDailyClick, onAccountsClick,
  forecastMode, statAccounts, onStatAccountFieldUpdate, onCalendarClick,
  savedDailyPatch, onInlineDailyRNEdit,
}: OverviewTabProps) {
  const overviewRootRef = useRef<HTMLDivElement>(null);
  const [expandedMonths, setExpandedMonths] = useState<Record<number, boolean>>({});
  const scrollRef = useRef<HTMLDivElement>(null);
  // dailySaData[month][saId][day] = room nights for that day
  const [dailySaData, setDailySaData] = useState<Record<number, Record<number, Record<number, number>>>>({});
  // dailySaADR[month][saId][day] = ADR for that day
  const [dailySaADR, setDailySaADR] = useState<Record<number, Record<number, Record<number, number>>>>({});
  // Explicit lock state: `${mo}-${saId}` keys that have custom daily values locking the monthly input
  // Lock is set when daily values are loaded/entered; cleared when user explicitly unlocks
  const [lockedSaMonths, setLockedSaMonths] = useState<Set<string>>(new Set());
  // Keys the user has explicitly unlocked this session — won't be re-locked on expand
  // Must be a ref (not state) so async initDailyForMonth always reads the current value
  const manuallyUnlockedSaMonths = useRef<Set<string>>(new Set());
  const [unlockPrompt, setUnlockPrompt] = useState<{ mo: number; saId: number } | null>(null);

  const saMonthKey = (mo: number, saId: number) => `${mo}-${saId}`;
  const lockSaMonth = (mo: number, saId: number) => {
    const key = saMonthKey(mo, saId);
    setLockedSaMonths(prev => { const n = new Set(prev); n.add(key); return n; });
    // Re-locking after a daily edit clears the manual-unlock flag
    manuallyUnlockedSaMonths.current.delete(key);
  };
  const unlockSaMonth = (mo: number, saId: number) => {
    const key = saMonthKey(mo, saId);
    setLockedSaMonths(prev => { const n = new Set(prev); n.delete(key); return n; });
    manuallyUnlockedSaMonths.current.add(key);
  };

  // When a daily save completes (from DailyEntryModal), apply the updated per-day room nights + ADR
  useEffect(() => {
    if (!savedDailyPatch) return;
    const { month: patchMonth, saId, dailyRN, dailyADR } = savedDailyPatch;
    setDailySaData(prev => ({
      ...prev,
      [patchMonth]: { ...(prev[patchMonth] ?? {}), [saId]: dailyRN },
    }));
    if (dailyADR) {
      setDailySaADR(prev => ({
        ...prev,
        [patchMonth]: { ...(prev[patchMonth] ?? {}), [saId]: dailyADR },
      }));
    }
  }, [savedDailyPatch]);

  // OTB (on the books) + RMS (revenue management) reference data keyed by [month][day]
  const [dailyRefData, setDailyRefData] = useState<DailyRefMap>({});

  // ── Budget mode: historical actuals per stat account ─────────
  // saHistory[saId]['YYYY-M'] = { rn, revenue }
  const [saHistory, setSaHistory] = useState<Record<number, Record<string, { rn: number; revenue: number }>>>({});
  const [historyLoaded, setHistoryLoaded] = useState(false);
  // Second reference column basis: trailing-12 average (constant) or two years prior (per row)
  const [refBasis, setRefBasis] = useState<'t12' | 'py2'>('t12');
  const [rnPctStr, setRnPctStr] = useState('0');
  const [adrPctStr, setAdrPctStr] = useState('0');
  // Machine-filled cells (`${mo}-${saId}-${field}`) — cleared when the user types over them
  const [seededCells, setSeededCells] = useState<Set<string>>(new Set());
  // Expanded-month tab in budget mode: history panel or the daily grid
  const [expandedTab, setExpandedTab] = useState<Record<number, 'history' | 'daily'>>({});
  // DOW-aligned LY daily reference for the budget daily grid: [month][budgetDay] = { rn, adr }
  const [budgetDailyRef, setBudgetDailyRef] = useState<Record<number, Record<number, { rn: number; adr: number }>>>({});

  useEffect(() => {
    if (!isBudget) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getStatAccountHistory({ orgId, locationId, months: 42, departmentName, chartOfAccountDepartmentId });
        if (cancelled) return;
        const map: Record<number, Record<string, { rn: number; revenue: number }>> = {};
        for (const a of res.accounts) map[a.statAccountId] = a.months;
        setSaHistory(map);
      } catch {
        // Reference rail just shows dashes
      }
      if (!cancelled) setHistoryLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [isBudget, orgId, locationId, departmentName, chartOfAccountDepartmentId]);

  useEffect(() => {
    if (!overviewLoaded || isBudget) return;
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        Array.from({ length: 12 }, (_, i) => i + 1).map(async (mo) => {
          try {
            const ref = await getDailyReference({ locationId, year, month: mo });
            const perDay: Record<number, DailyRefRow> = {};
            for (const d of ref.otb.data as DailyReferenceOtb[]) {
              if (!perDay[d.day]) perDay[d.day] = { otbRooms: 0, otbRevenue: 0, rmsRooms: 0, rmsRevenue: 0 };
              perDay[d.day].otbRooms = d.otbRooms || 0;
              perDay[d.day].otbRevenue = d.otbRevenue || 0;
            }
            for (const d of ref.rms.data as DailyReferenceRms[]) {
              if (!perDay[d.day]) perDay[d.day] = { otbRooms: 0, otbRevenue: 0, rmsRooms: 0, rmsRevenue: 0 };
              perDay[d.day].rmsRooms = d.roomsSold || 0;
              perDay[d.day].rmsRevenue = d.revenue || 0;
            }
            return { mo, perDay };
          } catch {
            return { mo, perDay: {} };
          }
        })
      );
      if (cancelled) return;
      const next: DailyRefMap = {};
      for (const { mo, perDay } of results) next[mo] = perDay;
      setDailyRefData(next);
    })();
    return () => { cancelled = true; };
  }, [overviewLoaded, locationId, year]);

  const monthRefTotal = useCallback((mo: number): DailyRefRow => {
    const perDay = dailyRefData[mo] ?? {};
    const agg: DailyRefRow = { otbRooms: 0, otbRevenue: 0, rmsRooms: 0, rmsRevenue: 0 };
    for (const d of Object.values(perDay)) {
      agg.otbRooms += d.otbRooms;
      agg.otbRevenue += d.otbRevenue;
      agg.rmsRooms += d.rmsRooms;
      agg.rmsRevenue += d.rmsRevenue;
    }
    return agg;
  }, [dailyRefData]);

  const initDailyForMonth = async (mo: number) => {
    const r = overviewData[mo];
    if (!r) return;
    const saInp = r.statAccountInputs ?? {};
    const monthData: Record<number, Record<number, number>> = {};

    const monthADRData: Record<number, Record<number, number>> = {};
    for (const sa of statAccounts) {
      const total = saInp[sa.id]?.rn ?? 0;
      const monthlyADR = saInp[sa.id]?.adr ?? 0;
      let daily: Record<number, number> = {};
      let dailyADR: Record<number, number> = {};

      if (sa.revenueChartOfAccountId) {
        try {
          const result = await getDailyValues({ versionId, chartOfAccountId: sa.revenueChartOfAccountId, year, month: mo });
          const hasSaved = result.values.some(v => v.hasStatValues);
          if (hasSaved) {
            for (const v of result.values) {
              daily[v.day] = v.roomNights ?? 0;
              dailyADR[v.day] = v.adr ?? monthlyADR;
            }
            // Only lock if the user hasn't explicitly unlocked this session
            if (!manuallyUnlockedSaMonths.current.has(saMonthKey(mo, sa.id))) {
              lockSaMonth(mo, sa.id);
            }
          } else {
            const base = Math.floor(total / r.days);
            const rem = total - base * r.days;
            for (let d = 1; d <= r.days; d++) {
              daily[d] = base + (d <= rem ? 1 : 0);
              dailyADR[d] = monthlyADR;
            }
          }
        } catch {
          const base = Math.floor(total / r.days);
          const rem = total - base * r.days;
          for (let d = 1; d <= r.days; d++) {
            daily[d] = base + (d <= rem ? 1 : 0);
            dailyADR[d] = monthlyADR;
          }
        }
      } else {
        const base = Math.floor(total / r.days);
        const rem = total - base * r.days;
        for (let d = 1; d <= r.days; d++) {
          daily[d] = base + (d <= rem ? 1 : 0);
          dailyADR[d] = monthlyADR;
        }
      }

      monthData[sa.id] = daily;
      monthADRData[sa.id] = dailyADR;
    }
    setDailySaData(prev => ({ ...prev, [mo]: monthData }));
    setDailySaADR(prev => ({ ...prev, [mo]: monthADRData }));
  };

  // Fetch the reference month's per-day actuals straight from [Transaction]
  // (stat RN + resolved revenue — no PMS data) and DOW-align them onto the
  // budget month's days: Mon Mar 1 'YY reads against the same weekday LY.
  // Journals that post month-end summaries have no day grain; the reference
  // column shows dashes for those months rather than a borrowed pattern.
  const MIN_DAILY_GRAIN_DAYS = 8;
  const fetchBudgetDailyRef = async (mo: number, refYear: number) => {
    if (budgetDailyRef[mo]) return;
    try {
      const res = await getStatAccountDailyActuals({ orgId, locationId, year: refYear, month: mo, departmentName, chartOfAccountDepartmentId });
      const entries = res.days.filter(d => d.rn > 0 || d.revenue > 0);
      const rnDays = entries.filter(d => d.rn > 0).length;
      if (rnDays < MIN_DAILY_GRAIN_DAYS) {
        // Month-end-summary journal — no real daily detail in the GL
        setBudgetDailyRef(prev => ({ ...prev, [mo]: {} }));
        return;
      }
      const byDowNth: Record<string, { rn: number; revenue: number }> = {};
      const byDow: Record<number, { rn: number; revenue: number }[]> = {};
      for (const e of entries) {
        const dow = new Date(refYear, mo - 1, e.day).getDay();
        const nth = Math.ceil(e.day / 7);
        byDowNth[`${dow}_${nth}`] = e;
        if (!byDow[dow]) byDow[dow] = [];
        byDow[dow].push(e);
      }
      const dim = new Date(year, mo, 0).getDate();
      const perDay: Record<number, { rn: number; adr: number }> = {};
      for (let d = 1; d <= dim; d++) {
        const dow = new Date(year, mo - 1, d).getDay();
        const nth = Math.ceil(d / 7);
        const src = byDowNth[`${dow}_${nth}`] ?? (byDow[dow] && byDow[dow].length > 0 ? byDow[dow][byDow[dow].length - 1] : undefined);
        if (src) perDay[d] = { rn: src.rn, adr: src.rn > 0 ? src.revenue / src.rn : 0 };
      }
      setBudgetDailyRef(prev => ({ ...prev, [mo]: perDay }));
    } catch {
      // Daily reference column shows dashes
    }
  };

  const toggleExpand = (mo: number) => {
    const opening = !expandedMonths[mo];
    setExpandedMonths(prev => ({ ...prev, [mo]: opening }));
    if (opening) {
      initDailyForMonth(mo);
      // Scroll the month row into view after a brief render tick
      setTimeout(() => {
        const row = scrollRef.current?.querySelector<HTMLElement>(`[data-month-row="${mo}"]`);
        if (row && scrollRef.current) {
          const containerTop = scrollRef.current.getBoundingClientRect().top;
          const rowTop = row.getBoundingClientRect().top;
          const offset = rowTop - containerTop - 44; // 44px = thead height
          if (offset < 0) {
            scrollRef.current.scrollBy({ top: offset, behavior: 'smooth' });
          }
        }
      }, 50);
    }
  };

  const updateDailyRN = (mo: number, saId: number, day: number, val: number) => {
    const r = overviewData[mo];
    if (!r) return;
    const currentDays = dailySaData[mo]?.[saId] ?? {};
    const newDayData = { ...currentDays, [day]: val };
    setDailySaData(prev => ({
      ...prev,
      [mo]: { ...(prev[mo] ?? {}), [saId]: newDayData },
    }));
    // Recompute monthly total = sum of all days with this edit applied
    let newTotal = 0;
    for (let d = 1; d <= r.days; d++) newTotal += d === day ? val : (currentDays[d] ?? 0);
    onStatAccountFieldUpdate(mo, saId, 'rn', newTotal);
    // Lock this SA/month — custom daily values now exist
    lockSaMonth(mo, saId);
    // Persist the per-day distribution to forecastDailyValue for the linked revenue account
    const sa = statAccounts.find(s => s.id === saId);
    if (sa?.revenueChartOfAccountId && onInlineDailyRNEdit) {
      const currentADR = dailySaADR[mo]?.[saId] ?? {};
      onInlineDailyRNEdit(mo, sa, newDayData, currentADR);
    }
  };

  const updateDailyADR = (mo: number, saId: number, day: number, val: number) => {
    const r = overviewData[mo];
    if (!r) return;
    const currentADR = dailySaADR[mo]?.[saId] ?? {};
    const newADRData = { ...currentADR, [day]: val };
    setDailySaADR(prev => ({
      ...prev,
      [mo]: { ...(prev[mo] ?? {}), [saId]: newADRData },
    }));
    // Recompute weighted monthly ADR and update monthly total
    const currentRN = dailySaData[mo]?.[saId] ?? {};
    let totalRevenue = 0, totalRN = 0;
    for (let d = 1; d <= r.days; d++) {
      const rn = currentRN[d] ?? 0;
      const adr = d === day ? val : (currentADR[d] ?? 0);
      totalRevenue += rn * adr;
      totalRN += rn;
    }
    const weightedADR = totalRN > 0 ? totalRevenue / totalRN : val;
    onStatAccountFieldUpdate(mo, saId, 'adr', weightedADR);
    // Persist to forecastDailyValue
    const sa = statAccounts.find(s => s.id === saId);
    if (sa?.revenueChartOfAccountId && onInlineDailyRNEdit) {
      onInlineDailyRNEdit(mo, sa, currentRN, newADRData);
    }
  };

  // Apply a monthly stat edit — spread evenly and persist, overwriting daily values
  const applyMonthlyStatEdit = (mo: number, saId: number, field: 'rn' | 'adr', val: number) => {
    onStatAccountFieldUpdate(mo, saId, field, val);
    const r = overviewData[mo];
    if (!r) return;
    const sa = statAccounts.find(s => s.id === saId);
    if (field === 'rn') {
      const newDailyRN: Record<number, number> = {};
      const base = Math.floor(val / r.days);
      const rem = val - base * r.days;
      for (let d = 1; d <= r.days; d++) newDailyRN[d] = base + (d <= rem ? 1 : 0);
      setDailySaData(prev => ({ ...prev, [mo]: { ...(prev[mo] ?? {}), [saId]: newDailyRN } }));
      if (sa?.revenueChartOfAccountId && onInlineDailyRNEdit) {
        const currentADR = dailySaADR[mo]?.[saId] ?? {};
        onInlineDailyRNEdit(mo, sa, newDailyRN, currentADR);
      }
    }
    if (field === 'adr') {
      const newDailyADR: Record<number, number> = {};
      for (let d = 1; d <= r.days; d++) newDailyADR[d] = val;
      setDailySaADR(prev => ({ ...prev, [mo]: { ...(prev[mo] ?? {}), [saId]: newDailyADR } }));
      if (sa?.revenueChartOfAccountId && onInlineDailyRNEdit) {
        const currentRN = dailySaData[mo]?.[saId] ?? {};
        onInlineDailyRNEdit(mo, sa, currentRN, newDailyADR);
      }
    }
  };

  // Monthly edit — only blocked if explicitly locked (custom daily values)
  const handleMonthlyStatEdit = (mo: number, saId: number, field: 'rn' | 'adr', val: number) => {
    // A typed value is a hand edit — it stops being "seeded" and survives future re-seeds
    if (isBudget) {
      setSeededCells(prev => {
        if (!prev.has(`${mo}-${saId}-${field}`)) return prev;
        const n = new Set(prev);
        n.delete(`${mo}-${saId}-${field}`);
        return n;
      });
    }
    applyMonthlyStatEdit(mo, saId, field, val);
  };

  if (!overviewLoaded) {
    return (
      <>
        <div className="rn-sk-classic-only flex items-center justify-center h-64">
          <Loader2 size={28} className="animate-spin text-slate-400" />
        </div>
        <div className="rn-sk-inset-only"><RowsSkeleton rows={12} cols={8} label="Loading months" /></div>
      </>
    );
  }

  // Fill down: take first non-zero value in column and apply to all remaining months
  const fillDown = (field: string) => {
    let sourceVal = 0;
    for (let mo = 1; mo <= 12; mo++) {
      const r = overviewData[mo];
      const v = r?.[field as keyof typeof r] as number;
      if (v > 0) { sourceVal = v; break; }
    }
    if (sourceVal === 0) return;
    for (let mo = 1; mo <= 12; mo++) {
      const r = overviewData[mo];
      const current = r?.[field as keyof typeof r] as number;
      if (!current || current === 0) {
        updateField(mo, field, sourceVal);
      }
    }
  };

  const FIELDS = ['avgRooms', 'groupRN', 'groupADR', 'transRN', 'transADR'] as const;

  const handleGridKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, mo: number, field: string) => {
    const colIdx = FIELDS.indexOf(field as typeof FIELDS[number]);
    if (colIdx === -1) return;

    let nextMo = mo;
    let nextCol = colIdx;

    if (e.key === 'Enter') {
      e.preventDefault();
      nextMo = mo < 12 ? mo + 1 : 1;
    } else if (e.key === 'Tab') {
      e.preventDefault();
      if (e.shiftKey) {
        // Shift+Tab: move left, wrap to previous row
        if (colIdx > 0) {
          nextCol = colIdx - 1;
        } else {
          nextCol = FIELDS.length - 1;
          nextMo = mo > 1 ? mo - 1 : 12;
        }
      } else {
        // Tab: move right, wrap to next row
        if (colIdx < FIELDS.length - 1) {
          nextCol = colIdx + 1;
        } else {
          nextCol = 0;
          nextMo = mo < 12 ? mo + 1 : 1;
        }
      }
    } else {
      return; // don't interfere with other keys
    }

    const nextInput = e.currentTarget.ownerDocument.querySelector<HTMLInputElement>(
      `[data-overview-cell="${nextMo}-${FIELDS[nextCol]}"]`
    );
    if (nextInput) {
      nextInput.focus();
      nextInput.select();
    }
  };

  const isStatMode = forecastMode === 'stat_account' && statAccounts.length > 0;
  const SA_COLORS_OV = ['text-blue-600', 'text-orange-500', 'text-emerald-600', 'text-violet-600', 'text-amber-600', 'text-cyan-600', 'text-rose-600', 'text-teal-600'];

  // ── Budget mode: reference rail + seeding ────────────────────
  const isBudgetStat = isBudget && isStatMode;
  const nowRef = new Date();
  const curY = nowRef.getFullYear();
  const curM = nowRef.getMonth() + 1;
  const isCompleteMonth = (y: number, mo: number) => y < curY || (y === curY && mo < curM);

  const histMonthTotals = (y: number, mo: number) => {
    let rn = 0, revenue = 0;
    for (const sa of statAccounts) {
      const e = saHistory[sa.id]?.[`${y}-${mo}`];
      if (e) { rn += e.rn; revenue += e.revenue; }
    }
    return { rn, revenue };
  };

  // Latest COMPLETE instance of calendar month `mo` before the budget year — the
  // reference year varies per row (budgeting 2027 in Aug 2026: Jan–Jul → '26, Aug–Dec → '25).
  // Falls back to an older year when the latest complete instance has no data.
  const lyRefYearFor = (mo: number): number | null => {
    let firstComplete: number | null = null;
    for (let y = year - 1; y >= year - 4; y--) {
      if (!isCompleteMonth(y, mo)) continue;
      if (firstComplete === null) firstComplete = y;
      if (histMonthTotals(y, mo).rn > 0) return y;
    }
    return firstComplete;
  };

  const lyRefFor = (mo: number) => {
    const y = lyRefYearFor(mo);
    if (y == null) return null;
    const t = histMonthTotals(y, mo);
    return { year: y, rn: t.rn, revenue: t.revenue, adr: t.rn > 0 ? t.revenue / t.rn : 0 };
  };

  // Trailing 12 complete months, oldest first
  const t12 = (() => {
    // Anchor at the latest complete month that actually HAS data — GL posting
    // lags the calendar, so "last month" is often still empty. Scan back up to
    // 24 months; fall back to the calendar anchor when there's no data at all.
    let y = curY, m = curM - 1;
    if (m === 0) { m = 12; y--; }
    let scanY = y, scanM = m;
    for (let i = 0; i < 24; i++) {
      if (histMonthTotals(scanY, scanM).rn > 0) { y = scanY; m = scanM; break; }
      scanM--; if (scanM === 0) { scanM = 12; scanY--; }
    }
    let rn = 0, revenue = 0;
    const months: { y: number; m: number; rn: number; revenue: number }[] = [];
    for (let i = 0; i < 12; i++) {
      const t = histMonthTotals(y, m);
      months.unshift({ y, m, rn: t.rn, revenue: t.revenue });
      rn += t.rn; revenue += t.revenue;
      m--; if (m === 0) { m = 12; y--; }
    }
    return {
      months,
      avgRN: rn > 0 ? Math.round(rn / 12) : 0,
      adr: rn > 0 ? revenue / rn : 0,
      latest: months[months.length - 1],
    };
  })();

  const altRefFor = (mo: number): { rn: number; adr: number; year?: number } => {
    if (refBasis === 't12') return { rn: t12.avgRN, adr: t12.adr };
    // Two years prior: the complete instance before the LY reference instance
    const y = (lyRefYearFor(mo) ?? year - 1) - 1;
    const t = histMonthTotals(y, mo);
    return { rn: t.rn, adr: t.rn > 0 ? t.revenue / t.rn : 0, year: y };
  };

  const lyYearTotals = (() => {
    let rn = 0, revenue = 0;
    for (let mo = 1; mo <= 12; mo++) {
      const ref = lyRefFor(mo);
      if (ref) { rn += ref.rn; revenue += ref.revenue; }
    }
    return { rn, revenue, adr: rn > 0 ? revenue / rn : 0 };
  })();

  const altYearTotals = (() => {
    if (refBasis !== 'py2') return null; // T12 avg has no meaningful year total
    let rn = 0, revenue = 0;
    for (let mo = 1; mo <= 12; mo++) {
      const t = histMonthTotals((lyRefYearFor(mo) ?? year - 1) - 1, mo);
      rn += t.rn; revenue += t.revenue;
    }
    return { rn, adr: rn > 0 ? revenue / rn : 0 };
  })();

  // Seed plan: LY actuals × uplift into every seedable cell. Without overwrite
  // only empty cells seed. With overwrite every cell that has an LY basis is
  // recomputed, so the growth rates above can be re-dialed at any point — cell
  // provenance lives only in this session's `seededCells`, so gating overwrite
  // on it made the growth rates inert after a reload. Cells whose LY basis is
  // missing keep their value either way. Seeded room nights are capped so a
  // month never exceeds its available room nights.
  const computeSeedPlan = (overwrite: boolean) => {
    const rnPct = parseFloat(rnPctStr) || 0;
    const adrPct = parseFloat(adrPctStr) || 0;
    const updates: Record<number, Record<number, StatAccountInput>> = {};
    const seededKeys: string[] = [];
    // Fields holding a value this session didn't seed — what an overwrite puts
    // at risk, and the basis for the confirmation prompt.
    const replacedKeys: string[] = [];
    let projRN = 0, projRev = 0;

    for (let mo = 1; mo <= 12; mo++) {
      const r = overviewData[mo];
      if (!r) continue;
      const refYear = lyRefYearFor(mo);
      const avail = Math.round(r.avgRooms * r.days);
      const saInp = r.statAccountInputs ?? {};

      // Seed at FIELD level: an empty ADR seeds even when its RN partner already
      // holds a value (and vice versa) — a cell-pair can be half-filled when it
      // was seeded before its revenue mapping existed.
      const target: { saId: number; rn: number; adr: number; rnSeeded: boolean; adrSeeded: boolean }[] = [];
      let preservedRN = 0, preservedRev = 0;
      for (const sa of statAccounts) {
        const cur = saInp[sa.id] ?? { rn: 0, adr: 0 };
        const e = refYear != null ? saHistory[sa.id]?.[`${refYear}-${mo}`] : undefined;
        const histAdr = e && e.rn > 0 ? e.revenue / e.rn : 0;
        const rnSeeded = (!cur.rn || overwrite) && !!e && e.rn > 0;
        const adrSeeded = (!cur.adr || overwrite) && histAdr > 0;
        if (rnSeeded && cur.rn && !seededCells.has(`${mo}-${sa.id}-rn`)) replacedKeys.push(`${mo}-${sa.id}-rn`);
        if (adrSeeded && cur.adr && !seededCells.has(`${mo}-${sa.id}-adr`)) replacedKeys.push(`${mo}-${sa.id}-adr`);
        if (rnSeeded || adrSeeded) {
          target.push({
            saId: sa.id,
            rn: rnSeeded ? Math.max(0, Math.round(e!.rn * (1 + rnPct / 100))) : (cur.rn || 0),
            adr: adrSeeded ? Math.round(histAdr * (1 + adrPct / 100) * 100) / 100 : (cur.adr || 0),
            rnSeeded,
            adrSeeded,
          });
        } else {
          preservedRN += cur.rn || 0;
          preservedRev += (cur.rn || 0) * (cur.adr || 0);
        }
      }

      if (target.length > 0) {
        // Cap NEWLY seeded RN at what's left of available room nights;
        // kept RN values (in target or not) count against the budget.
        const keptInTargetRN = target.filter(t => !t.rnSeeded).reduce((s, t) => s + t.rn, 0);
        const seedBudget = avail > 0 ? Math.max(0, avail - preservedRN - keptInTargetRN) : Infinity;
        const seeded = target.filter(t => t.rnSeeded);
        const seedTotal = seeded.reduce((s, t) => s + t.rn, 0);
        if (seedBudget !== Infinity && seedTotal > seedBudget) {
          const scale = seedBudget / seedTotal;
          const floors = seeded.map(t => Math.floor(t.rn * scale));
          let rem = seedBudget - floors.reduce((s, v) => s + v, 0);
          seeded.forEach((t, i) => { t.rn = floors[i] + (rem > 0 ? 1 : 0); if (rem > 0) rem--; });
        }
        const moUpdates: Record<number, StatAccountInput> = {};
        for (const t of target) {
          moUpdates[t.saId] = { rn: t.rn, adr: t.adr };
          if (t.rnSeeded) seededKeys.push(`${mo}-${t.saId}-rn`);
          if (t.adrSeeded) seededKeys.push(`${mo}-${t.saId}-adr`);
          projRN += t.rn;
          projRev += t.rn * t.adr;
        }
        updates[mo] = moUpdates;
      }
      projRN += preservedRN;
      projRev += preservedRev;
    }
    return { updates, seededKeys, replacedKeys, projRN, projRev, monthCount: Object.keys(updates).length };
  };

  const runSeed = (overwrite: boolean, onlyMonth?: number) => {
    if (!onSeedMonths || isReadOnly) return;
    const plan = computeSeedPlan(overwrite);
    let updates = plan.updates;
    let seededKeys = plan.seededKeys;
    let replacedKeys = plan.replacedKeys;
    if (onlyMonth != null) {
      updates = updates[onlyMonth] ? { [onlyMonth]: updates[onlyMonth] } : {};
      seededKeys = seededKeys.filter(k => k.startsWith(`${onlyMonth}-`));
      replacedKeys = replacedKeys.filter(k => k.startsWith(`${onlyMonth}-`));
    }
    if (Object.keys(updates).length === 0) {
      toast('Nothing to seed — no last-year actuals for these stat accounts.', { icon: 'ℹ️' });
      return;
    }
    // Seeding saves straight through to the version, so an overwrite of values
    // this session didn't place is unrecoverable — confirm before replacing.
    if (replacedKeys.length > 0) {
      const n = replacedKeys.length;
      const scope = onlyMonth != null ? SHORT_MONTHS[onlyMonth - 1] : 'all 12 months';
      const ok = ownerWindowOf(overviewRootRef.current).confirm(
        `Re-seed ${scope} from last year's actuals × ${parseFloat(rnPctStr) || 0}% room nights / ${parseFloat(adrPctStr) || 0}% ADR?\n\n`
        + `This replaces ${n} value${n === 1 ? '' : 's'} already in the grid, including any you typed by hand. It can't be undone.`
      );
      if (!ok) return;
    }
    setSeededCells(prev => {
      const n = new Set(prev);
      for (const k of seededKeys) n.add(k);
      return n;
    });
    onSeedMonths(updates);
    const monthCount = Object.keys(updates).length;
    toast.success(`Seeded ${monthCount} month${monthCount === 1 ? '' : 's'} from actuals`);
  };

  // Editable RN column saIds (for keyboard navigation in daily grid)
  const rnSaIds = isStatMode ? statAccounts.map(sa => sa.id) : [];

  const handleDailyKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, mo: number, day: number, saId: number, field: 'rn' | 'adr') => {
    const days = overviewData[mo]?.days ?? new Date(year, mo, 0).getDate();
    // Columns ordered as [sa0-rn, sa0-adr, sa1-rn, sa1-adr, ...]
    const allSaIds = statAccounts.map(s => s.id);
    const totalCols = allSaIds.length * 2;
    const colIdx = allSaIds.indexOf(saId) * 2 + (field === 'adr' ? 1 : 0);
    let nextDay = day;
    let nextCol = colIdx;

    if (e.key === 'Enter') {
      e.preventDefault();
      nextDay = e.shiftKey ? day - 1 : day + 1;
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      nextDay = day + 1;
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      nextDay = day - 1;
    } else if (e.key === 'ArrowRight' && (e.target as HTMLInputElement).selectionStart === (e.target as HTMLInputElement).value.length) {
      e.preventDefault();
      nextCol = colIdx + 1;
      if (nextCol >= totalCols) { nextCol = 0; nextDay = day + 1; }
    } else if (e.key === 'ArrowLeft' && (e.target as HTMLInputElement).selectionStart === 0) {
      e.preventDefault();
      nextCol = colIdx - 1;
      if (nextCol < 0) { nextCol = totalCols - 1; nextDay = day - 1; }
    } else if (e.key === 'Tab') {
      e.preventDefault();
      if (e.shiftKey) {
        nextCol = colIdx - 1;
        if (nextCol < 0) { nextCol = totalCols - 1; nextDay = day - 1; }
      } else {
        nextCol = colIdx + 1;
        if (nextCol >= totalCols) { nextCol = 0; nextDay = day + 1; }
      }
    } else {
      return;
    }

    if (nextDay < 1 || nextDay > days) return;

    const nextSaId = allSaIds[Math.floor(nextCol / 2)];
    const nextField = nextCol % 2 === 0 ? 'rn' : 'adr';
    const nextInput = e.currentTarget.ownerDocument.querySelector<HTMLInputElement>(
      `[data-daily-cell="${mo}-${nextDay}-${nextSaId}-${nextField}"]`
    );
    if (nextInput) { nextInput.focus(); nextInput.select(); }
  };

  // Compute totals (mode-aware)
  let totalAvailRN = 0, totalOccRN = 0, totalRev = 0;
  const saTotals: Record<number, { rn: number; rev: number }> = {};
  if (isStatMode) statAccounts.forEach(sa => { saTotals[sa.id] = { rn: 0, rev: 0 }; });

  for (let mo = 1; mo <= 12; mo++) {
    const r = overviewData[mo];
    if (!r) continue;
    totalAvailRN += Math.round(r.avgRooms * r.days);
    if (isStatMode) {
      const saInp = r.statAccountInputs ?? {};
      for (const sa of statAccounts) {
        const inp = saInp[sa.id] ?? { rn: 0, adr: 0 };
        saTotals[sa.id].rn += inp.rn;
        saTotals[sa.id].rev += inp.rn * inp.adr;
        totalOccRN += inp.rn;
        totalRev += inp.rn * inp.adr;
      }
    } else {
      totalOccRN += r.groupRN + r.transRN;
      totalRev += r.groupRN * r.groupADR + r.transRN * r.transADR;
    }
  }
  const totalOccPct = totalAvailRN > 0 ? Math.round(totalOccRN / totalAvailRN * 1000) / 10 : 0;

  const inputCls = "w-full text-right text-xs font-semibold bg-transparent border-none outline-none disabled:cursor-not-allowed tabular-nums";

  // Build column definitions based on mode
  const basicCols = [
    { label: 'Grp RN', field: 'groupRN', cls: 'text-blue-600 font-bold', w: 'min-w-[5.5rem]', bl: true },
    { label: 'Grp ADR', field: 'groupADR', cls: 'text-blue-600 font-bold', w: 'min-w-[5rem]' },
    { label: 'Trans RN', field: 'transRN', cls: 'text-orange-500 font-bold', w: 'min-w-[5.5rem]', bl: true },
    { label: 'Trans ADR', field: 'transADR', cls: 'text-orange-500 font-bold', w: 'min-w-[5rem]' },
  ];
  // Fixed-width colored pill inputs for stat account mode — spinners hidden via CSS
  const noSpinner = "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none";
  const saRnInputCls = `w-[44px] text-center text-[11px] font-semibold tabular-nums bg-emerald-50 border border-emerald-200 rounded text-emerald-700 outline-none px-1 py-[2px] disabled:cursor-not-allowed focus:border-emerald-400 focus:ring-1 focus:ring-emerald-200 ${noSpinner}`;
  const saAdrInputCls = `w-[48px] text-center text-[11px] font-semibold tabular-nums bg-blue-50 border border-blue-200 rounded text-blue-600 outline-none px-1 py-[2px] disabled:cursor-not-allowed focus:border-blue-400 focus:ring-1 focus:ring-blue-200 ${noSpinner}`;
  const saDailyRnInputCls = `w-[44px] text-center text-[11px] font-semibold tabular-nums bg-slate-100 border border-slate-200 rounded text-emerald-700 outline-none px-1 py-[2px] disabled:cursor-not-allowed focus:border-slate-300 focus:ring-1 focus:ring-slate-200 ${noSpinner}`;
  const saDailyAdrInputCls = `w-[48px] text-center text-[11px] font-semibold tabular-nums bg-slate-100 border border-slate-200 rounded text-blue-600 outline-none px-1 py-[2px] disabled:cursor-not-allowed focus:border-slate-300 focus:ring-1 focus:ring-slate-200 ${noSpinner}`;

  const saCols = statAccounts.flatMap((sa, idx) => [
    { label: `${sa.accountName.length > 12 ? sa.accountName.slice(0, 12) + '…' : sa.accountName} RN`, saId: sa.id, sa, saField: 'rn' as const, cls: 'text-emerald-700', w: 'w-[58px] min-w-[58px]', bl: idx === 0 },
    { label: 'ADR', saId: sa.id, sa, saField: 'adr' as const, cls: 'text-blue-600', w: 'w-[66px] min-w-[66px]', bl: false },
  ]);

  // Frozen column left offsets: month(64px) + rooms(64px) + days(40px) + availRN(72px)
  const stickyBg = 'bg-white';
  const stickyBgAlt = 'bg-slate-50';

  const seedPctInputCls = `w-[52px] text-center text-[11px] font-bold tabular-nums bg-white border rounded outline-none px-1 py-[3px] ${noSpinner}`;

  return (
    <div ref={overviewRootRef} className="flex-1 min-h-0 flex flex-col">
      {/* Budget seed bar — LY actuals × uplift into empty cells */}
      {isBudgetStat && !isReadOnly && (() => {
        const preview = historyLoaded ? computeSeedPlan(false) : null;
        const previewAdr = preview && preview.projRN > 0 ? preview.projRev / preview.projRN : 0;
        return (
          <div className="flex items-center gap-3 flex-wrap px-4 py-2 border-t" style={{ background: 'var(--fv3-n-50, #FBF7F0)', borderColor: 'var(--fv3-n-100, #EFE5D2)' }}>
            <span className="text-[10px] font-extrabold tracking-[0.09em]" style={{ color: 'var(--fv3-n-500, #8A6D3B)' }}>SEED FROM LY ACTUALS</span>
            {historyLoaded && (t12.latest?.rn ?? 0) > 0 && (
              <span className="text-[10px] font-semibold rounded px-1.5 py-0.5 tabular-nums" style={{ background: 'var(--fv3-n-50, #F0E7D3)', color: 'var(--fv3-n-500, #8A6D3B)' }} title="Most recent complete month with GL data">
                Latest: {SHORT_MONTHS[t12.latest.m - 1]} ’{String(t12.latest.y).slice(2)} · {fmt(t12.latest.rn)} RN{t12.latest.revenue > 0 ? ` · $${(t12.latest.revenue / t12.latest.rn).toFixed(0)} ADR` : ''}
              </span>
            )}
            <label className="flex items-center gap-1.5 text-[11px] text-slate-500">
              <span className="font-semibold text-slate-600">Room nights</span>
              <input type="number" value={rnPctStr} disabled={!historyLoaded}
                onChange={e => setRnPctStr(e.target.value)}
                onFocus={e => e.target.select()}
                className={seedPctInputCls} style={{ borderColor: 'var(--fv3-n-200, #E2D9C6)', color: '#047857' }} />
              <span className="text-slate-400">%</span>
            </label>
            <label className="flex items-center gap-1.5 text-[11px] text-slate-500">
              <span className="font-semibold text-slate-600">ADR</span>
              <input type="number" value={adrPctStr} disabled={!historyLoaded}
                onChange={e => setAdrPctStr(e.target.value)}
                onFocus={e => e.target.select()}
                className={seedPctInputCls} style={{ borderColor: 'var(--fv3-n-200, #E2D9C6)', color: 'var(--fv3-n-600, #475569)' }} />
              <span className="text-slate-400">%</span>
            </label>
            {preview && preview.monthCount > 0 && (
              <span className="text-[11px] text-slate-500 tabular-nums">
                → <span className="font-bold text-slate-800">{fmt(preview.projRN)} RN · ${previewAdr.toFixed(0)} ADR · {fmtDollar(Math.round(preview.projRev))}</span>
              </span>
            )}
            <div className="ml-auto flex items-center gap-2">
              {!historyLoaded && <span className="text-[11px] text-slate-400 flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> Loading history…</span>}
              <button
                onClick={() => runSeed(false)}
                disabled={!historyLoaded || !preview || preview.monthCount === 0}
                className="px-3 py-1.5 text-[11px] font-bold rounded-lg border transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: 'var(--fv3-accent-wash, #FDEEDF)', borderColor: 'var(--fv3-accent-line, #F0C193)', color: 'var(--fv3-accent-ink, #B45309)' }}
              >
                {preview && preview.monthCount > 0 ? `Seed ${preview.monthCount} month${preview.monthCount === 1 ? '' : 's'}` : 'Seed empty cells'}
              </button>
              <button
                onClick={() => runSeed(true)}
                disabled={!historyLoaded}
                title="Recompute all 12 months from last year's actuals × the growth rates above — replaces values already in the grid"
                className="px-3 py-1.5 text-[11px] font-semibold rounded-lg border border-slate-200 text-slate-500 hover:text-slate-700 hover:bg-white transition-colors disabled:opacity-40"
              >
                Re-seed all
              </button>
            </div>
          </div>
        );
      })()}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-auto overscroll-contain border-t border-slate-200">
        <table className="border-collapse text-xs" style={{ minWidth: 'max-content', width: '100%' }}>
          <thead className="sticky top-0 z-20 bg-slate-50">
            <tr className="bg-slate-50 border-b-2 border-slate-200">
              <th className="text-left px-3 py-2 text-[10px] text-slate-400 font-semibold uppercase w-16 sticky left-0 z-30 bg-slate-50">Month</th>
              <th className="text-right px-2 py-1.5 text-[10px] text-slate-500 font-semibold uppercase w-20 sticky left-16 z-30 bg-slate-50">
                <div className="flex items-center justify-end gap-1">
                  <span>Rooms</span>
                  {!isReadOnly && <button onClick={() => fillDown('avgRooms')} title="Fill Rooms down" className="text-[9px] text-slate-300 hover:text-orange-500 transition-colors leading-none">↓</button>}
                </div>
              </th>
              <th className="text-right px-2 py-1.5 text-[10px] text-slate-400 font-semibold uppercase w-10 sticky left-[144px] z-30 bg-slate-50">Days</th>
              <th className="text-right px-2 py-1.5 text-[10px] text-slate-500 font-semibold uppercase w-[72px] sticky left-[184px] z-30 bg-slate-50" style={{ boxShadow: '4px 0 6px -2px rgba(0,0,0,0.08)' }}>Avail RN</th>
              {/* Reference columns: OTB/RMS when forecasting, historical actuals when budgeting */}
              {isBudgetStat ? (
                <>
                  <th colSpan={2} className="text-center px-1 py-2 text-[11px] font-semibold uppercase border-l" style={{ width: 128, minWidth: 128, background: 'var(--fv3-n-50, #F5EFE1)', borderColor: 'var(--fv3-n-100, #E9E2D2)', color: 'var(--fv3-n-500, #8A6D3B)' }}>
                    <span className="block text-[11px] font-semibold">LY Actual</span>
                    <span className="block text-[10px] font-normal" style={{ color: 'var(--fv3-n-400, #B49B6C)' }}>RN / ADR · year per row</span>
                  </th>
                  <th colSpan={2} className="text-center px-1 py-1.5 border-l" style={{ width: 120, minWidth: 120, background: 'var(--fv3-n-50, #F5EFE1)', borderColor: 'var(--fv3-n-100, #E9E2D2)' }}>
                    <select
                      value={refBasis}
                      onChange={e => setRefBasis(e.target.value as 't12' | 'py2')}
                      className="text-[10px] font-semibold uppercase bg-transparent outline-none cursor-pointer"
                      style={{ color: 'var(--fv3-n-500, #8A6D3B)' }}
                      title="Second reference basis"
                    >
                      <option value="t12">T12 Avg</option>
                      <option value="py2">2 Yrs Prior</option>
                    </select>
                    <span className="block text-[10px] font-normal normal-case" style={{ color: 'var(--fv3-n-400, #B49B6C)' }}>
                      {refBasis === 't12'
                        ? `RN / ADR · 12 mo thru ${t12.latest ? `${SHORT_MONTHS[t12.latest.m - 1]} ’${String(t12.latest.y).slice(2)}` : '—'}`
                        : 'RN / ADR · year per row'}
                    </span>
                  </th>
                </>
              ) : (
                <>
                  <th colSpan={2} className="text-center px-1 py-2 text-[11px] font-semibold uppercase bg-slate-50 border-l border-slate-200" style={{ width: 120, minWidth: 120, color: 'var(--fv3-n-600, #475569)' }}>
                    <span className="block text-[11px] font-semibold" style={{ color: 'var(--fv3-n-600, #475569)' }}>OTB</span>
                    <span className="block text-[10px] font-normal" style={{ color: 'var(--fv3-n-400, #94A3B8)' }}>Rooms / ADR</span>
                  </th>
                  <th colSpan={2} className="text-center px-1 py-2 text-[11px] font-semibold uppercase bg-slate-50 border-l border-slate-200" style={{ width: 120, minWidth: 120, color: 'var(--fv3-n-600, #475569)' }}>
                    <span className="block text-[11px] font-semibold" style={{ color: 'var(--fv3-n-600, #475569)' }}>RMS</span>
                    <span className="block text-[10px] font-normal" style={{ color: 'var(--fv3-n-400, #94A3B8)' }}>Rooms / ADR</span>
                  </th>
                </>
              )}
              {isStatMode ? statAccounts.flatMap((sa, idx) => [
                <th key={`sa-${sa.id}`} colSpan={2} style={{ width: 124, minWidth: 124, color: 'var(--fv3-n-600, #475569)' }} className={`text-center px-1 py-2 text-[11px] font-semibold uppercase ${idx === 0 ? 'border-l border-slate-200' : 'border-l border-slate-100'} bg-slate-50`}>
                  <span className="block text-[11px] font-semibold truncate" style={{ color: 'var(--fv3-n-600, #475569)' }}>{sa.accountName}</span>
                  <span className="block text-[10px] font-normal" style={{ color: 'var(--fv3-n-400, #94A3B8)' }}>RN / ADR</span>
                </th>
              ]) : basicCols.map((col, i) => (
                <th key={i} className={`text-right px-2 py-1.5 text-[10px] ${col.cls} font-semibold uppercase ${col.w} ${col.bl ? 'border-l border-slate-200' : ''}`}>
                  <div className="flex items-center justify-end gap-1">
                    <span>{col.label}</span>
                    {col.field && !isReadOnly && <button onClick={() => fillDown(col.field!)} title={`Fill ${col.label} down`} className="text-[9px] text-slate-300 hover:text-orange-500 transition-colors leading-none">↓</button>}
                  </div>
                </th>
              ))}
              <th className="text-right px-2 py-1.5 text-[10px] text-slate-500 font-semibold uppercase min-w-[4.5rem] border-l border-slate-200">Occ RN</th>
              <th className="text-right px-2 py-1.5 text-[10px] text-slate-500 font-semibold uppercase min-w-[3.5rem]">Occ%</th>
              <th className="text-right px-2 py-1.5 text-[10px] text-slate-700 font-bold uppercase min-w-[5.5rem] border-l border-slate-200">Revenue</th>
              <th className="w-12" />
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 12 }, (_, i) => i + 1).map(mo => {
              const r = overviewData[mo] ?? { groupRN: 0, transRN: 0, groupADR: 0, transADR: 0, avgRooms: 0, days: new Date(year, mo, 0).getDate() };
              const availRN = Math.round(r.avgRooms * r.days);
              const saInp = r.statAccountInputs ?? {};

              let occRN: number, revenue: number, hasData: boolean;
              if (isStatMode) {
                occRN = statAccounts.reduce((s, sa) => s + (saInp[sa.id]?.rn ?? 0), 0);
                revenue = statAccounts.reduce((s, sa) => { const inp = saInp[sa.id] ?? { rn: 0, adr: 0 }; return s + inp.rn * inp.adr; }, 0);
                hasData = occRN > 0;
              } else {
                occRN = r.groupRN + r.transRN;
                revenue = r.groupRN * r.groupADR + r.transRN * r.transADR;
                hasData = r.groupRN > 0 || r.transRN > 0;
              }
              const occPct = availRN > 0 ? Math.round(occRN / availRN * 1000) / 10 : 0;
              const isExpanded = !!expandedMonths[mo];

              return (
                <React.Fragment key={mo}>
                <tr data-month-row={mo} className={`border-b border-slate-100 ${isExpanded ? 'bg-amber-50/60 sticky top-[44px] z-[15]' : hasData ? 'bg-white' : 'bg-slate-50/30'} hover:bg-orange-50/20`}>
                  <td className={`px-1.5 py-1.5 font-semibold text-slate-700 sticky left-0 z-[16] ${isExpanded ? 'bg-amber-50/80' : hasData ? 'bg-white' : 'bg-slate-50/30'}`}>
                    <div className="flex items-center gap-0.5">
                      <button onClick={() => toggleExpand(mo)} className="p-0.5 text-slate-400 hover:text-slate-600 transition-transform" title={isExpanded ? 'Collapse daily' : 'Expand daily'}>
                        <ChevronRight size={12} className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                      </button>
                      <button onClick={() => onMonthClick(mo)} className="hover:text-orange-600 hover:underline">
                        {SHORT_MONTHS[mo - 1]}
                      </button>
                    </div>
                  </td>
                  <td className={`px-2 py-1.5 sticky left-16 z-[16] ${isExpanded ? 'bg-amber-50/80' : hasData ? 'bg-white' : 'bg-slate-50/30'}`}>
                    <input type="number" value={r.avgRooms || ''} placeholder="—" disabled={isReadOnly}
                      data-overview-cell={`${mo}-avgRooms`}
                      onChange={e => updateField(mo, 'avgRooms', Number(e.target.value))}
                      onKeyDown={e => handleGridKeyDown(e, mo, 'avgRooms')}
                      onFocus={e => e.target.select()}
                      className={`${inputCls} text-slate-700 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`} />
                  </td>
                  <td className={`px-2 py-1.5 text-right text-slate-400 font-medium sticky left-[144px] z-[16] ${isExpanded ? 'bg-amber-50/80' : hasData ? 'bg-white' : 'bg-slate-50/30'}`}>{r.days}</td>
                  <td className={`px-2 py-1.5 text-right font-semibold text-slate-600 sticky left-[184px] z-[16] ${isExpanded ? 'bg-amber-50/80' : hasData ? 'bg-white' : 'bg-slate-50/30'}`} style={{ boxShadow: '4px 0 6px -2px rgba(0,0,0,0.07)' }}>{availRN > 0 ? fmt(availRN) : '—'}</td>

                  {/* Reference cells: LY actual + alt basis when budgeting, OTB/RMS when forecasting */}
                  {isBudgetStat ? (() => {
                    const ref = lyRefFor(mo);
                    const alt = altRefFor(mo);
                    const altDim = refBasis === 't12' ? 0.62 : 1;
                    const cellSty: React.CSSProperties = { background: 'var(--fv3-n-50, #FAF6EE)' };
                    const cellCls = 'px-2 py-1.5 text-right text-[11px] tabular-nums';
                    return (
                      <>
                        <td className={`${cellCls} font-semibold border-l`} style={{ ...cellSty, width: 68, minWidth: 68, borderColor: 'var(--fv3-n-100, #E9E2D2)', color: 'var(--fv3-n-700, #6B5D42)' }}>
                          {ref && ref.rn > 0 ? (
                            <span className="inline-flex items-center gap-1 justify-end">
                              <span className="text-[9px] font-extrabold rounded px-0.5" style={{ background: 'var(--fv3-n-50, #F0E7D3)', color: 'var(--fv3-n-500, #8A6D3B)' }}>’{String(ref.year).slice(2)}</span>
                              {fmt(ref.rn)}
                            </span>
                          ) : <span style={{ color: 'var(--fv3-n-200, #D3C9B2)' }}>—</span>}
                        </td>
                        <td className={`${cellCls}`} style={{ ...cellSty, width: 52, minWidth: 52, color: 'var(--fv3-n-500, #8A7A5C)' }}>
                          {ref && ref.adr > 0 ? `$${ref.adr.toFixed(0)}` : <span style={{ color: 'var(--fv3-n-200, #D3C9B2)' }}>—</span>}
                        </td>
                        <td className={`${cellCls} border-l`} style={{ ...cellSty, width: 68, minWidth: 68, borderColor: 'var(--fv3-n-100, #E9E2D2)', color: 'var(--fv3-n-700, #6B5D42)', opacity: altDim }}>
                          {alt.rn > 0 ? (
                            refBasis === 'py2' && alt.year != null ? (
                              <span className="inline-flex items-center gap-1 justify-end">
                                <span className="text-[9px] font-extrabold rounded px-0.5" style={{ background: 'var(--fv3-n-50, #F0E7D3)', color: 'var(--fv3-n-500, #8A6D3B)' }}>’{String(alt.year).slice(2)}</span>
                                {fmt(alt.rn)}
                              </span>
                            ) : fmt(alt.rn)
                          ) : <span style={{ color: 'var(--fv3-n-200, #D3C9B2)' }}>—</span>}
                        </td>
                        <td className={`${cellCls}`} style={{ ...cellSty, width: 52, minWidth: 52, color: 'var(--fv3-n-500, #8A7A5C)', opacity: altDim }}>
                          {alt.adr > 0 ? `$${alt.adr.toFixed(0)}` : <span style={{ color: 'var(--fv3-n-200, #D3C9B2)' }}>—</span>}
                        </td>
                      </>
                    );
                  })() : (() => {
                    const agg = monthRefTotal(mo);
                    const otbAdr = agg.otbRooms > 0 ? agg.otbRevenue / agg.otbRooms : 0;
                    const rmsAdr = agg.rmsRooms > 0 ? agg.rmsRevenue / agg.rmsRooms : 0;
                    const cellCls = 'px-2 py-1.5 text-right text-[11px] tabular-nums';
                    return (
                      <>
                        <td className={`${cellCls} font-semibold text-slate-600 border-l border-slate-200`} style={{ width: 60, minWidth: 60 }}>
                          {agg.otbRooms > 0 ? fmt(agg.otbRooms) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className={`${cellCls} text-slate-500`} style={{ width: 60, minWidth: 60 }}>
                          {otbAdr > 0 ? `$${otbAdr.toFixed(0)}` : <span className="text-slate-300">—</span>}
                        </td>
                        <td className={`${cellCls} font-semibold text-slate-600 border-l border-slate-200`} style={{ width: 60, minWidth: 60 }}>
                          {agg.rmsRooms > 0 ? fmt(agg.rmsRooms) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className={`${cellCls} text-slate-500`} style={{ width: 60, minWidth: 60 }}>
                          {rmsAdr > 0 ? `$${rmsAdr.toFixed(0)}` : <span className="text-slate-300">—</span>}
                        </td>
                      </>
                    );
                  })()}

                  {isStatMode ? saCols.map((col, i) => {
                    const val = saInp[col.saId]?.[col.saField] ?? 0;
                    const isRnCol = col.saField === 'rn';
                    const locked = lockedSaMonths.has(saMonthKey(mo, col.saId));
                    const showCalendar = !isRnCol && !!(col.sa.revenueChartOfAccountId) && !isReadOnly && !!onCalendarClick;
                    const lockedCls = isRnCol
                      ? `w-[44px] text-center text-[11px] font-semibold tabular-nums bg-slate-100 border border-slate-300 rounded text-slate-500 outline-none px-1 py-[2px] cursor-pointer ${noSpinner}`
                      : `w-[48px] text-center text-[11px] font-semibold tabular-nums bg-slate-100 border border-slate-300 rounded text-slate-500 outline-none px-1 py-[2px] cursor-pointer ${noSpinner}`;
                    // Budget mode: empty cells carry a ghost of the LY actual; seeded cells get an orange dot
                    const ghostEntry = isBudgetStat ? (() => {
                      const gy = lyRefYearFor(mo);
                      return gy != null ? saHistory[col.saId]?.[`${gy}-${mo}`] : undefined;
                    })() : undefined;
                    const ghostVal = ghostEntry
                      ? (isRnCol ? ghostEntry.rn : (ghostEntry.rn > 0 ? Math.round(ghostEntry.revenue / ghostEntry.rn) : 0))
                      : 0;
                    const isSeeded = isBudgetStat && val > 0 && seededCells.has(`${mo}-${col.saId}-${col.saField}`);
                    const ghostStyle: React.CSSProperties | undefined =
                      isBudgetStat && !val && ghostVal > 0 ? { borderStyle: 'dashed' } : undefined;
                    return (
                      <td key={i} className={`px-1 py-1 ${col.w} ${col.bl ? 'border-l border-slate-200' : ''}`} style={{ position: 'relative' }}>
                        <div className="flex justify-center">
                          {locked ? (
                            <input
                              type="number" value={val || ''} readOnly
                              title="Custom daily values — click to unlock"
                              onFocus={() => setUnlockPrompt({ mo, saId: col.saId })}
                              className={lockedCls} />
                          ) : (
                            <span className="relative inline-flex">
                              <input type="number" value={val || ''}
                                placeholder={isBudgetStat && ghostVal > 0 ? String(ghostVal) : '—'}
                                title={isBudgetStat && !val && ghostVal > 0 ? 'Last year actual — seed or type to commit' : undefined}
                                disabled={isReadOnly}
                                onChange={e => handleMonthlyStatEdit(mo, col.saId, col.saField, Number(e.target.value))}
                                onFocus={e => e.target.select()}
                                style={ghostStyle}
                                className={isRnCol ? saRnInputCls : saAdrInputCls} />
                              {isSeeded && (
                                <span
                                  className="absolute rounded-full"
                                  title="Seeded from LY actuals"
                                  style={{ top: -2, right: -2, width: 6, height: 6, background: 'var(--fv3-accent, #F28C38)', border: '1px solid #fff' }} />
                              )}
                            </span>
                          )}
                        </div>
                        {showCalendar && (
                          <button
                            title={`Daily entry: ${col.sa.accountName} — ${SHORT_MONTHS[mo - 1]}`}
                            onClick={() => onCalendarClick!(mo, col.sa)}
                            style={{ position: 'absolute', right: 2, top: '50%', transform: 'translateY(-50%)' }}
                            className="w-[16px] h-[16px] flex items-center justify-center rounded border border-slate-200 bg-white text-slate-300 hover:text-[var(--fv3-accent-hover, #E8722A)] transition-colors"
                          >
                            <Calendar size={9} />
                          </button>
                        )}
                      </td>
                    );
                  }) : (
                    <>
                      <td className="px-2 py-1.5 border-l border-slate-200">
                        <input type="number" value={r.groupRN || ''} placeholder="—" disabled={isReadOnly}
                          data-overview-cell={`${mo}-groupRN`}
                          onChange={e => updateField(mo, 'groupRN', Number(e.target.value))}
                          onKeyDown={e => handleGridKeyDown(e, mo, 'groupRN')}
                          onFocus={e => e.target.select()}
                          className={`${inputCls} text-blue-600`} />
                      </td>
                      <td className="px-2 py-1.5">
                        <input type="number" value={r.groupADR || ''} placeholder="—" disabled={isReadOnly}
                          data-overview-cell={`${mo}-groupADR`}
                          onChange={e => updateField(mo, 'groupADR', Number(e.target.value))}
                          onKeyDown={e => handleGridKeyDown(e, mo, 'groupADR')}
                          onFocus={e => e.target.select()}
                          className={`${inputCls} text-blue-500`} />
                      </td>
                      <td className="px-2 py-1.5 border-l border-slate-200">
                        <input type="number" value={r.transRN || ''} placeholder="—" disabled={isReadOnly}
                          data-overview-cell={`${mo}-transRN`}
                          onChange={e => updateField(mo, 'transRN', Number(e.target.value))}
                          onKeyDown={e => handleGridKeyDown(e, mo, 'transRN')}
                          onFocus={e => e.target.select()}
                          className={`${inputCls} text-orange-500`} />
                      </td>
                      <td className="px-2 py-1.5">
                        <input type="number" value={r.transADR || ''} placeholder="—" disabled={isReadOnly}
                          data-overview-cell={`${mo}-transADR`}
                          onChange={e => updateField(mo, 'transADR', Number(e.target.value))}
                          onKeyDown={e => handleGridKeyDown(e, mo, 'transADR')}
                          onFocus={e => e.target.select()}
                          className={`${inputCls} text-orange-400`} />
                      </td>
                    </>
                  )}

                  <td className="px-2 py-1.5 text-right font-semibold text-slate-700 border-l border-slate-200">{occRN > 0 ? fmt(occRN) : '—'}</td>
                  <td className={`px-2 py-1.5 text-right font-semibold ${occPct > 100 ? 'text-red-500' : occPct > 0 ? 'text-slate-600' : 'text-slate-300'}`}>
                    {occPct > 0 ? `${occPct}%` : '—'}
                  </td>
                  <td className="px-2 py-1.5 text-right font-bold text-slate-800 border-l border-slate-200">
                    {revenue > 0 ? fmtDollar(revenue) : '—'}
                  </td>
                  <td className="w-2" />
                </tr>
                {/* Expanded daily rows — table-native, columns align with header */}
                {isExpanded && (() => {
                  const DOW_SHORT = ['Su','Mo','Tu','We','Th','Fr','Sa'];
                  const allDays = Array.from({ length: r.days }, (_, d) => {
                    const day = d + 1;
                    const dow = new Date(year, mo - 1, day).getDay();
                    return { day, dow };
                  });
                  const weeks: { day: number; dow: number }[][] = [];
                  let cur: { day: number; dow: number }[] = [];
                  allDays.forEach((d, i) => {
                    cur.push(d);
                    if (d.dow === 6 || i === allDays.length - 1) { weeks.push(cur); cur = []; }
                  });
                  const grandTotal = isStatMode
                    ? statAccounts.reduce((acc, sa) => { const inp = saInp[sa.id] ?? { rn: 0, adr: 0 }; return acc + inp.rn * inp.adr; }, 0)
                    : 0;
                  const expBg = 'var(--fv3-n-50, #F9FAFB)';
                  const nSaCols = isStatMode ? statAccounts.length : 4; // stat: 1 colSpan-2 per SA; basic: 4 individual cols
                  const rightCols = 4; // OccRN + Occ% + Revenue + spacer
                  // Budget mode: the expansion is tabbed — History (default) or the Daily grid
                  const expTab: 'history' | 'daily' = isBudgetStat ? (expandedTab[mo] ?? 'history') : 'daily';
                  const expRefYear = isBudgetStat ? lyRefYearFor(mo) : null;
                  return (
                    <>
                      {/* Orange top accent */}
                      <tr key={`${mo}-exp-top`}>
                        <td colSpan={999} style={{ padding: 0, height: 3, background: 'linear-gradient(90deg, var(--fv3-accent, #F28C38), var(--fv3-accent-hover, #E8722A))' }} />
                      </tr>
                      {/* Budget mode: History | Daily tab strip */}
                      {isBudgetStat && (
                        <tr key={`${mo}-exp-tabs`} style={{ background: 'var(--fv3-n-50, #FCF9F3)' }}>
                          <td colSpan={999} style={{ padding: '6px 10px 6px 24px', borderBottom: '1px solid var(--fv3-n-50, #F0EADB)', boxShadow: 'inset 3px 0 0 var(--fv3-accent, #F28C38)' }}>
                            <div className="flex items-center gap-3 flex-wrap">
                              <div className="inline-flex rounded-lg p-[2px]" style={{ background: 'var(--fv3-n-100, #F1EADA)' }}>
                                {(['history', 'daily'] as const).map(t => (
                                  <button
                                    key={t}
                                    onClick={() => {
                                      setExpandedTab(prev => ({ ...prev, [mo]: t }));
                                      if (t === 'daily' && expRefYear != null) fetchBudgetDailyRef(mo, expRefYear);
                                    }}
                                    className="px-3 py-1 rounded-md text-[11px] font-bold transition-colors"
                                    style={expTab === t
                                      ? { background: 'var(--fv3-n-0, #FFFFFF)', color: 'var(--fv3-n-900, #111827)', boxShadow: '0 1px 2px rgba(15,23,42,0.12)' }
                                      : { color: 'var(--fv3-n-500, #8A6D3B)' }}
                                  >
                                    {t === 'history' ? 'History' : 'Daily'}
                                  </button>
                                ))}
                              </div>
                              {expTab === 'daily' && expRefYear != null && (
                                <span className="text-[10px]" style={{ color: 'var(--fv3-n-400, #A99F8A)' }}>
                                  Reference: same weekday, {SHORT_MONTHS[mo - 1]} ’{String(expRefYear).slice(2)} · day edits roll up to the month
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                      {/* Budget mode: history panel */}
                      {isBudgetStat && expTab === 'history' && (() => {
                        const yrs: { y: number; rn: number; revenue: number; adr: number }[] = [];
                        if (expRefYear != null) {
                          for (let y = expRefYear - 2; y <= expRefYear; y++) {
                            const t = histMonthTotals(y, mo);
                            if (t.rn > 0) yrs.push({ y, rn: t.rn, revenue: t.revenue, adr: t.revenue / t.rn });
                          }
                        }
                        const histAvail = Math.round(r.avgRooms * r.days);
                        const maxRn = Math.max(1, ...t12.months.map(m2 => m2.rn));
                        const refMonthRev = expRefYear != null ? histMonthTotals(expRefYear, mo).revenue : 0;
                        const histTh = 'text-right pl-4 first:pl-0 first:text-left pb-1 text-[9px] font-bold uppercase tracking-wider text-slate-400';
                        return (
                          <tr key={`${mo}-exp-hist`}>
                            <td colSpan={999} style={{ background: 'var(--fv3-n-50, #FCF9F3)', padding: '14px 24px 16px', borderBottom: '1px solid var(--fv3-n-100, #EFE5D2)' }}>
                              <div className="flex flex-wrap gap-x-12 gap-y-5">
                                <div>
                                  <div className="text-[10px] font-extrabold tracking-[0.09em] uppercase mb-2" style={{ color: 'var(--fv3-n-500, #8A6D3B)' }}>{MONTH_NAMES[mo - 1]}, prior years</div>
                                  {yrs.length === 0 ? (
                                    <div className="text-[11px] text-slate-400">No history for this month yet.</div>
                                  ) : (
                                    <table className="text-[11px] tabular-nums">
                                      <thead>
                                        <tr>{['Year', 'RN', 'Occ', 'ADR', 'Revenue', 'YoY'].map(h => <th key={h} className={histTh}>{h}</th>)}</tr>
                                      </thead>
                                      <tbody>
                                        {yrs.map((yr2, i) => {
                                          const prev = i > 0 ? yrs[i - 1] : null;
                                          const yoy = prev && prev.revenue > 0 ? ((yr2.revenue - prev.revenue) / prev.revenue) * 100 : null;
                                          return (
                                            <tr key={yr2.y} className="border-t" style={{ borderColor: 'var(--fv3-n-50, #F0EADB)' }}>
                                              <td className="py-1 font-bold text-slate-600">{yr2.y}</td>
                                              <td className="py-1 pl-4 text-right text-slate-600">{fmt(yr2.rn)}</td>
                                              <td className="py-1 pl-4 text-right text-slate-500">{histAvail > 0 ? `${Math.round(yr2.rn / histAvail * 1000) / 10}%` : '—'}</td>
                                              <td className="py-1 pl-4 text-right text-slate-600">${yr2.adr.toFixed(0)}</td>
                                              <td className="py-1 pl-4 text-right font-semibold text-slate-700">{fmtDollar(Math.round(yr2.revenue))}</td>
                                              <td className="py-1 pl-4 text-right font-semibold" style={{ color: yoy == null ? 'var(--fv3-n-200, #CBD5E1)' : yoy >= 0 ? '#047857' : '#DC2626' }}>
                                                {yoy == null ? '—' : `${yoy >= 0 ? '+' : ''}${yoy.toFixed(1)}%`}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  )}
                                </div>
                                <div>
                                  <div className="text-[10px] font-extrabold tracking-[0.09em] uppercase mb-2" style={{ color: 'var(--fv3-n-500, #8A6D3B)' }}>Trailing 12 · room nights</div>
                                  <svg width={12 * 21 + 4} height={62} role="img" aria-label="Room nights by month, trailing twelve months">
                                    {t12.months.map((m2, i) => {
                                      const h = Math.max(2, Math.round((m2.rn / maxRn) * 42));
                                      const isLast = i === t12.months.length - 1;
                                      return <rect key={i} x={2 + i * 21} y={46 - h} width={15} height={h} rx={2} fill={isLast ? 'var(--fv3-chart-primary, var(--fv3-accent-hover, #E8722A))' : 'var(--fv3-chart-tertiary, #64748B)'} opacity={isLast ? 1 : 0.45} />;
                                    })}
                                    <line x1={0} y1={46.5} x2={12 * 21 + 2} y2={46.5} stroke="var(--fv3-n-100, #E5DED0)" strokeWidth={1} />
                                    {t12.months.map((m2, i) => (
                                      <text key={`l${i}`} x={2 + i * 21 + 7.5} y={58} textAnchor="middle" fontSize={8}
                                        fill={i === t12.months.length - 1 ? 'var(--fv3-chart-primary, #B45309)' : 'var(--fv3-n-300, #A3ADB9)'}
                                        fontWeight={i === t12.months.length - 1 ? 700 : 400}>
                                        {SHORT_MONTHS[m2.m - 1][0]}
                                      </text>
                                    ))}
                                  </svg>
                                  <div className="text-[10px] mt-1 tabular-nums" style={{ color: 'var(--fv3-n-400, #A99F8A)' }}>
                                    avg <span className="font-bold" style={{ color: 'var(--fv3-n-600, #475569)' }}>{fmt(t12.avgRN)} RN · ${t12.adr.toFixed(0)} ADR</span>
                                  </div>
                                </div>
                                <div>
                                  <div className="text-[10px] font-extrabold tracking-[0.09em] uppercase mb-2" style={{ color: 'var(--fv3-n-500, #8A6D3B)' }}>
                                    {expRefYear != null ? `${SHORT_MONTHS[mo - 1]} ’${String(expRefYear).slice(2)} by stat account` : 'By stat account'}
                                  </div>
                                  <table className="text-[11px] tabular-nums">
                                    <thead>
                                      <tr>{['Account', 'RN', 'ADR', 'Revenue', 'Share'].map(h => <th key={h} className={histTh}>{h}</th>)}</tr>
                                    </thead>
                                    <tbody>
                                      {statAccounts.map(sa => {
                                        const e = expRefYear != null ? saHistory[sa.id]?.[`${expRefYear}-${mo}`] : undefined;
                                        const saRn = e?.rn ?? 0;
                                        const saRev = e?.revenue ?? 0;
                                        const share = refMonthRev > 0 ? Math.round((saRev / refMonthRev) * 100) : 0;
                                        return (
                                          <tr key={sa.id} className="border-t" style={{ borderColor: 'var(--fv3-n-50, #F0EADB)' }}>
                                            <td className="py-1 font-semibold text-slate-600 pr-2 max-w-[130px] truncate">{sa.accountName}</td>
                                            <td className="py-1 pl-4 text-right text-slate-600">{saRn > 0 ? fmt(saRn) : '—'}</td>
                                            <td className="py-1 pl-4 text-right text-slate-500">{saRn > 0 ? `$${(saRev / saRn).toFixed(0)}` : '—'}</td>
                                            <td className="py-1 pl-4 text-right font-semibold text-slate-700">{saRev > 0 ? fmtDollar(Math.round(saRev)) : '—'}</td>
                                            <td className="py-1 pl-4 text-right text-slate-500">{share > 0 ? `${share}%` : '—'}</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                  {!isReadOnly && expRefYear != null && (
                                    <button
                                      onClick={() => runSeed(true, mo)}
                                      className="mt-2.5 px-3 py-1.5 text-[11px] font-bold rounded-lg border transition-colors"
                                      style={{ background: 'var(--fv3-accent-wash, #FDEEDF)', borderColor: 'var(--fv3-accent-line, #F0C193)', color: 'var(--fv3-accent-ink, #B45309)' }}
                                    >
                                      Seed {SHORT_MONTHS[mo - 1]} from ’{String(expRefYear).slice(2)}
                                    </button>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })()}
                      {(!isBudgetStat || expTab === 'daily') && (
                      <>
                      {/* Sub-header — aligns with outer thead */}
                      <tr key={`${mo}-exp-hdr`} style={{ background: expBg, position: 'sticky', top: 44, zIndex: 14 }}>
                        <td colSpan={3} style={{ position: 'sticky', left: 0, zIndex: 16, background: expBg, padding: '5px 8px 5px 24px', fontSize: 10, fontWeight: 600, color: 'var(--fv3-n-400, #8896A6)', textTransform: 'uppercase', letterSpacing: '0.07em', borderBottom: '1px solid var(--fv3-n-100, #E8ECF1)', boxShadow: 'inset 3px 0 0 var(--fv3-accent, #F28C38)', whiteSpace: 'nowrap' }}>
                          Day
                        </td>
                        <td style={{ position: 'sticky', left: 184, zIndex: 16, background: expBg, padding: '5px 8px', fontSize: 10, fontWeight: 600, color: 'var(--fv3-n-300, #B8C0CC)', textTransform: 'uppercase', letterSpacing: '0.07em', borderBottom: '1px solid var(--fv3-n-100, #E8ECF1)', boxShadow: '4px 0 6px -2px rgba(0,0,0,0.06)', whiteSpace: 'nowrap' }}>
                          Avail
                        </td>
                        {/* OTB + RMS sub-headers */}
                        <td style={{ padding: '5px 4px', fontSize: 10, fontWeight: 600, color: 'var(--fv3-n-400, #8896A6)', textTransform: 'uppercase', letterSpacing: '0.07em', textAlign: 'center', borderLeft: '1px solid var(--fv3-n-100, #E2E8F0)', borderBottom: '1px solid var(--fv3-n-100, #E8ECF1)' }}>RN</td>
                        <td style={{ padding: '5px 4px', fontSize: 10, fontWeight: 600, color: 'var(--fv3-n-400, #8896A6)', textTransform: 'uppercase', letterSpacing: '0.07em', textAlign: 'center', borderBottom: '1px solid var(--fv3-n-100, #E8ECF1)' }}>ADR</td>
                        <td style={{ padding: '5px 4px', fontSize: 10, fontWeight: 600, color: 'var(--fv3-n-400, #8896A6)', textTransform: 'uppercase', letterSpacing: '0.07em', textAlign: 'center', borderLeft: '1px solid var(--fv3-n-100, #E2E8F0)', borderBottom: '1px solid var(--fv3-n-100, #E8ECF1)' }}>RN</td>
                        <td style={{ padding: '5px 4px', fontSize: 10, fontWeight: 600, color: 'var(--fv3-n-400, #8896A6)', textTransform: 'uppercase', letterSpacing: '0.07em', textAlign: 'center', borderBottom: '1px solid var(--fv3-n-100, #E8ECF1)' }}>ADR</td>
                        {isStatMode ? saCols.map((col, ci) => (
                          <td key={ci} className={`${col.w} ${col.bl ? 'border-l border-slate-200' : ''}`} style={{ padding: '5px 4px', fontSize: 10, fontWeight: 600, color: 'var(--fv3-n-400, #8896A6)', textTransform: 'uppercase', letterSpacing: '0.07em', textAlign: 'center', borderBottom: '1px solid var(--fv3-n-100, #E8ECF1)' }}>
                            {col.saField === 'rn' ? 'RN' : 'ADR'}
                          </td>
                        )) : (
                          <>
                            <td colSpan={2} style={{ padding: '5px 8px', fontSize: 10, fontWeight: 600, color: 'var(--fv3-n-400, #8896A6)', textTransform: 'uppercase', letterSpacing: '0.07em', textAlign: 'center', borderLeft: '1px solid var(--fv3-n-100, #E2E8F0)', borderBottom: '1px solid var(--fv3-n-100, #E8ECF1)' }}>Group</td>
                            <td colSpan={2} style={{ padding: '5px 8px', fontSize: 10, fontWeight: 600, color: 'var(--fv3-n-400, #8896A6)', textTransform: 'uppercase', letterSpacing: '0.07em', textAlign: 'center', borderLeft: '1px solid var(--fv3-n-100, #E2E8F0)', borderBottom: '1px solid var(--fv3-n-100, #E8ECF1)' }}>Transient</td>
                          </>
                        )}
                        <td colSpan={rightCols} style={{ borderBottom: '1px solid var(--fv3-n-100, #E8ECF1)' }} />
                      </tr>
                      {/* Weeks */}
                      {weeks.map((week, wi) => (
                        <React.Fragment key={`${mo}-w${wi}`}>
                          <tr style={{ background: expBg }}>
                            <td colSpan={3} style={{ position: 'sticky', left: 0, zIndex: 5, background: expBg, padding: '4px 8px 3px 24px', fontSize: 10, fontWeight: 700, color: 'var(--fv3-accent-hover, #E8722A)', textTransform: 'uppercase', letterSpacing: '0.08em', boxShadow: 'inset 3px 0 0 var(--fv3-accent, #F28C38)', whiteSpace: 'nowrap' }}>
                              Week {wi + 1}
                            </td>
                            <td style={{ position: 'sticky', left: 184, zIndex: 5, background: expBg, boxShadow: '4px 0 6px -2px rgba(0,0,0,0.06)' }} />
                            <td colSpan={999} style={{ background: expBg }} />
                          </tr>
                          {week.map(({ day, dow }) => {
                            const isWknd = dow === 0 || dow === 6;
                            const dayBg = isWknd ? 'var(--fv3-n-50, #F0F2F5)' : (day % 2 === 0 ? 'var(--fv3-n-50, #FAFBFC)' : 'var(--fv3-n-0, #FFFFFF)');
                            // Budget mode: flag days spread/edited past physical capacity (rooms)
                            const dayTotalRN = isBudgetStat && isStatMode
                              ? statAccounts.reduce((s, sa) => s + (dailySaData[mo]?.[sa.id]?.[day] ?? 0), 0)
                              : 0;
                            const overCap = isBudgetStat && r.avgRooms > 0 && dayTotalRN > r.avgRooms;
                            return (
                              <tr key={`${mo}-d${day}`} style={{ background: dayBg, borderBottom: '1px solid var(--fv3-n-50, #F0F1F4)' }}>
                                {/* Day + DOW — sticky left-0, spans Month+Rooms+Days cols */}
                                <td colSpan={3} title={overCap ? `${dayTotalRN} room nights on ${r.avgRooms} rooms — over 100% occupancy` : undefined} style={{ position: 'sticky', left: 0, zIndex: 5, background: dayBg, padding: '3px 8px 3px 24px', whiteSpace: 'nowrap', boxShadow: 'inset 3px 0 0 var(--fv3-accent, #F28C38)' }}>
                                  <span style={{ fontSize: 12, fontWeight: overCap ? 800 : 600, color: overCap ? '#DC2626' : 'var(--fv3-n-600, #475569)', fontVariantNumeric: 'tabular-nums', display: 'inline-block', width: 22, textAlign: 'right' }}>{day}</span>
                                  <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.03em', color: isWknd ? 'var(--fv3-accent-hover, #E8722A)' : 'var(--fv3-n-400, #A0AEC0)', marginLeft: 5, display: 'inline-block', width: 18 }}>{DOW_SHORT[dow]}</span>
                                </td>
                                {/* Avail — sticky left-184, spans AvailRN col */}
                                <td style={{ position: 'sticky', left: 184, zIndex: 5, background: dayBg, padding: '3px 8px', textAlign: 'right', fontSize: 11, color: 'var(--fv3-n-200, #C8D0D8)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', boxShadow: '4px 0 6px -2px rgba(0,0,0,0.06)' }}>
                                  {r.avgRooms || ''}
                                </td>
                                {/* Daily reference: DOW-aligned LY actuals when budgeting, OTB/RMS when forecasting */}
                                {(() => {
                                  const cellSty: React.CSSProperties = { padding: '3px 6px', textAlign: 'center', fontSize: 11, fontVariantNumeric: 'tabular-nums', color: 'var(--fv3-n-500, #64748B)' };
                                  const empty: React.CSSProperties = { color: 'var(--fv3-n-200, #CBD5E1)' };
                                  if (isBudgetStat) {
                                    const bref = budgetDailyRef[mo]?.[day];
                                    const lyRN = bref?.rn ?? 0;
                                    const lyADR = bref?.adr ?? 0;
                                    const sepia: React.CSSProperties = { ...cellSty, background: 'var(--fv3-n-50, #FAF6EE)', color: 'var(--fv3-n-700, #6B5D42)' };
                                    return (
                                      <>
                                        <td style={{ ...sepia, borderLeft: '1px solid var(--fv3-n-100, #E9E2D2)', ...(lyRN > 0 ? {} : { color: 'var(--fv3-n-200, #D3C9B2)' }) }}>{lyRN > 0 ? fmt(lyRN) : '—'}</td>
                                        <td style={{ ...sepia, ...(lyADR > 0 ? {} : { color: 'var(--fv3-n-200, #D3C9B2)' }) }}>{lyADR > 0 ? `$${lyADR.toFixed(0)}` : '—'}</td>
                                        <td style={{ ...sepia, borderLeft: '1px solid var(--fv3-n-100, #E9E2D2)', color: 'var(--fv3-n-200, #D3C9B2)', opacity: 0.62 }}>—</td>
                                        <td style={{ ...sepia, color: 'var(--fv3-n-200, #D3C9B2)', opacity: 0.62 }}>—</td>
                                      </>
                                    );
                                  }
                                  const ref = dailyRefData[mo]?.[day];
                                  const otbRN = ref?.otbRooms ?? 0;
                                  const otbADR = otbRN > 0 ? (ref?.otbRevenue ?? 0) / otbRN : 0;
                                  const rmsRN = ref?.rmsRooms ?? 0;
                                  const rmsADR = rmsRN > 0 ? (ref?.rmsRevenue ?? 0) / rmsRN : 0;
                                  return (
                                    <>
                                      <td style={{ ...cellSty, borderLeft: '1px solid var(--fv3-n-100, #E2E8F0)', ...(otbRN > 0 ? {} : empty) }}>{otbRN > 0 ? fmt(otbRN) : '—'}</td>
                                      <td style={{ ...cellSty, ...(otbADR > 0 ? {} : empty) }}>{otbADR > 0 ? `$${otbADR.toFixed(0)}` : '—'}</td>
                                      <td style={{ ...cellSty, borderLeft: '1px solid var(--fv3-n-100, #E2E8F0)', ...(rmsRN > 0 ? {} : empty) }}>{rmsRN > 0 ? fmt(rmsRN) : '—'}</td>
                                      <td style={{ ...cellSty, ...(rmsADR > 0 ? {} : empty) }}>{rmsADR > 0 ? `$${rmsADR.toFixed(0)}` : '—'}</td>
                                    </>
                                  );
                                })()}
                                {/* Stat account cells — one <td> per saCols entry so columns align with monthly row */}
                                {isStatMode ? saCols.map((col, ci) => {
                                  const saId = col.saId;
                                  const isRn = col.saField === 'rn';
                                  const hasMonthData = !!(saInp[saId]?.rn || saInp[saId]?.adr);
                                  const rnVal = dailySaData[mo]?.[saId]?.[day] ?? 0;
                                  const adrVal = dailySaADR[mo]?.[saId]?.[day] ?? (saInp[saId]?.adr ?? 0);
                                  return (
                                    <td key={ci} className={`px-1 py-[3px] ${col.w} ${col.bl ? 'border-l border-slate-200' : ''}`}>
                                      <div className="flex justify-center">
                                        {!hasMonthData ? (
                                          <span className="text-[11px] text-slate-200">—</span>
                                        ) : isRn ? (
                                          <input type="number" value={rnVal || ''} placeholder="—" disabled={isReadOnly}
                                            data-daily-cell={`${mo}-${day}-${saId}-rn`}
                                            onChange={e => updateDailyRN(mo, saId, day, Number(e.target.value))}
                                            onKeyDown={e => handleDailyKeyDown(e, mo, day, saId, 'rn')}
                                            onFocus={e => e.target.select()}
                                            className={saDailyRnInputCls} />
                                        ) : (
                                          <input type="number" value={adrVal || ''} placeholder="—" disabled={isReadOnly}
                                            data-daily-cell={`${mo}-${day}-${saId}-adr`}
                                            onChange={e => updateDailyADR(mo, saId, day, Number(e.target.value))}
                                            onKeyDown={e => handleDailyKeyDown(e, mo, day, saId, 'adr')}
                                            onFocus={e => e.target.select()}
                                            className={saDailyAdrInputCls} />
                                        )}
                                      </div>
                                    </td>
                                  );
                                }) : (
                                  <>
                                    {(['groupRN', 'groupADR', 'transRN', 'transADR'] as const).map((f, fi) => {
                                      const moVal = r[f];
                                      const isRn = f === 'groupRN' || f === 'transRN';
                                      const dailyVal = isRn ? (moVal > 0 ? Math.round(moVal / r.days) : 0) : moVal;
                                      const clr = f.startsWith('group') ? 'var(--fv3-chart-secondary, #3B82F6)' : 'var(--fv3-chart-primary, var(--fv3-accent, #F97316))';
                                      return (
                                        <td key={fi} style={{ padding: '3px 8px', textAlign: 'center', fontSize: 12, fontWeight: 500, color: clr, opacity: 0.65, fontVariantNumeric: 'tabular-nums', borderLeft: fi === 0 || fi === 2 ? '1px solid var(--fv3-n-100, #E2E8F0)' : undefined }}>
                                          {dailyVal > 0 ? (isRn ? dailyVal : dailyVal.toFixed(0)) : ''}
                                        </td>
                                      );
                                    })}
                                  </>
                                )}
                                <td colSpan={rightCols} />
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      ))}
                      {/* Daily TOTAL row — sticky at bottom of expanded section */}
                      <tr key={`${mo}-daily-total`} style={{ background: 'var(--fv3-n-50, #F1F5F9)', borderTop: '2px solid var(--fv3-n-200, #CBD5E1)', borderBottom: '1px solid var(--fv3-n-200, #CBD5E1)', position: 'sticky', bottom: 0, zIndex: 12 }}>
                        <td colSpan={3} style={{ position: 'sticky', left: 0, zIndex: 5, background: 'var(--fv3-n-50, #F1F5F9)', padding: '4px 8px 4px 24px', whiteSpace: 'nowrap', boxShadow: 'inset 3px 0 0 var(--fv3-accent, #F28C38)' }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--fv3-n-600, #475569)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Total</span>
                        </td>
                        <td style={{ position: 'sticky', left: 184, zIndex: 5, background: 'var(--fv3-n-50, #F1F5F9)', padding: '4px 8px', textAlign: 'right', fontSize: 11, fontWeight: 600, color: 'var(--fv3-n-400, #94A3B8)', fontVariantNumeric: 'tabular-nums', boxShadow: '4px 0 6px -2px rgba(0,0,0,0.06)' }}>
                          {r.days * (r.avgRooms || 0) > 0 ? fmt(r.days * (r.avgRooms || 0)) : ''}
                        </td>
                        {/* Reference totals: LY actual when budgeting, OTB/RMS when forecasting */}
                        {(() => {
                          const totalSty: React.CSSProperties = { padding: '4px 6px', textAlign: 'center', fontSize: 11, fontWeight: 700, fontVariantNumeric: 'tabular-nums', background: 'var(--fv3-n-50, #F1F5F9)' };
                          if (isBudgetStat) {
                            const ref = lyRefFor(mo);
                            return (
                              <>
                                <td style={{ ...totalSty, color: ref && ref.rn > 0 ? 'var(--fv3-n-700, #6B5D42)' : 'var(--fv3-n-200, #CBD5E1)', borderLeft: '1px solid var(--fv3-n-100, #E2E8F0)' }}>{ref && ref.rn > 0 ? fmt(ref.rn) : '—'}</td>
                                <td style={{ ...totalSty, color: ref && ref.adr > 0 ? 'var(--fv3-n-500, #8A7A5C)' : 'var(--fv3-n-200, #CBD5E1)' }}>{ref && ref.adr > 0 ? `$${ref.adr.toFixed(0)}` : '—'}</td>
                                <td style={{ ...totalSty, color: 'var(--fv3-n-200, #CBD5E1)', borderLeft: '1px solid var(--fv3-n-100, #E2E8F0)' }}>—</td>
                                <td style={{ ...totalSty, color: 'var(--fv3-n-200, #CBD5E1)' }}>—</td>
                              </>
                            );
                          }
                          const agg = monthRefTotal(mo);
                          const otbAdr = agg.otbRooms > 0 ? agg.otbRevenue / agg.otbRooms : 0;
                          const rmsAdr = agg.rmsRooms > 0 ? agg.rmsRevenue / agg.rmsRooms : 0;
                          return (
                            <>
                              <td style={{ ...totalSty, color: agg.otbRooms > 0 ? 'var(--fv3-n-600, #475569)' : 'var(--fv3-n-200, #CBD5E1)', borderLeft: '1px solid var(--fv3-n-100, #E2E8F0)' }}>{agg.otbRooms > 0 ? fmt(agg.otbRooms) : '—'}</td>
                              <td style={{ ...totalSty, color: otbAdr > 0 ? 'var(--fv3-n-600, #475569)' : 'var(--fv3-n-200, #CBD5E1)' }}>{otbAdr > 0 ? `$${otbAdr.toFixed(0)}` : '—'}</td>
                              <td style={{ ...totalSty, color: agg.rmsRooms > 0 ? 'var(--fv3-n-600, #475569)' : 'var(--fv3-n-200, #CBD5E1)', borderLeft: '1px solid var(--fv3-n-100, #E2E8F0)' }}>{agg.rmsRooms > 0 ? fmt(agg.rmsRooms) : '—'}</td>
                              <td style={{ ...totalSty, color: rmsAdr > 0 ? 'var(--fv3-n-600, #475569)' : 'var(--fv3-n-200, #CBD5E1)' }}>{rmsAdr > 0 ? `$${rmsAdr.toFixed(0)}` : '—'}</td>
                            </>
                          );
                        })()}
                        {isStatMode ? saCols.map((col, ci) => {
                          const saId = col.saId;
                          const isRnCol = col.saField === 'rn';
                          const monthlyInp = saInp[saId] ?? { rn: 0, adr: 0 };
                          const dailyRNMap = dailySaData[mo]?.[saId] ?? {};
                          const dailyADRMap = dailySaADR[mo]?.[saId] ?? {};
                          const dailyRNTotal = Object.values(dailyRNMap).reduce((s, v) => s + (v || 0), 0);
                          const dailyRevTotal = Object.keys(dailyRNMap).reduce((s, d) => {
                            const dn = Number(d);
                            return s + (dailyRNMap[dn] || 0) * (dailyADRMap[dn] || 0);
                          }, 0);
                          // Prefer daily-computed totals; fall back to monthly inputs
                          const displayRN = dailyRNTotal > 0 ? dailyRNTotal : monthlyInp.rn;
                          const displayADR = dailyRNTotal > 0
                            ? (dailyRNTotal > 0 ? dailyRevTotal / dailyRNTotal : 0)
                            : monthlyInp.adr;
                          return (
                            <td key={ci} className={`px-1 py-[4px] ${col.w} ${col.bl ? 'border-l border-slate-200' : ''}`} style={{ textAlign: 'center' }}>
                              {isRnCol ? (
                                <span style={{ fontSize: 11, fontWeight: 700, color: displayRN > 0 ? '#1A8754' : 'var(--fv3-n-200, #CBD5E1)', fontVariantNumeric: 'tabular-nums' }}>
                                  {displayRN > 0 ? fmt(displayRN) : '—'}
                                </span>
                              ) : (
                                <span style={{ fontSize: 11, fontWeight: 700, color: displayADR > 0 ? 'var(--fv3-chart-secondary, #2563EB)' : 'var(--fv3-n-200, #CBD5E1)', fontVariantNumeric: 'tabular-nums' }}>
                                  {displayADR > 0 ? `$${displayADR.toFixed(2)}` : '—'}
                                </span>
                              )}
                            </td>
                          );
                        }) : null}
                        <td colSpan={rightCols} />
                      </tr>
                      {/* Totals cards — aligned to columns */}
                      {isStatMode && (
                        <>
                          {/* Label row */}
                          <tr key={`${mo}-exp-totals-hdr`} style={{ background: 'var(--fv3-n-50, #F8FAFC)', borderTop: '2px solid var(--fv3-n-100, #E2E8F0)' }}>
                            <td colSpan={3} style={{ position: 'sticky', left: 0, zIndex: 5, background: 'var(--fv3-n-50, #F8FAFC)', padding: '8px 12px 4px 24px', boxShadow: 'inset 3px 0 0 var(--fv3-accent, #F28C38)' }}>
                            </td><td style={{ position: 'sticky', left: 184, zIndex: 5, background: 'var(--fv3-n-50, #F8FAFC)', boxShadow: '4px 0 6px -2px rgba(0,0,0,0.06)' }}>
                              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--fv3-n-900, #0F172A)' }}>Forecast Total</span>
                                <span style={{ fontSize: 11, color: 'var(--fv3-n-400, #94A3B8)' }}>{SHORT_MONTHS[mo - 1]} {year}</span>
                              </div>
                            </td>
                            <td colSpan={4} style={{ background: 'var(--fv3-n-50, #F8FAFC)', borderLeft: '1px solid var(--fv3-n-100, #E2E8F0)' }} />
                            {statAccounts.map((sa, si) => {
                              const inp = saInp[sa.id] ?? { rn: 0, adr: 0 };
                              const rev = inp.rn * inp.adr;
                              const pct = grandTotal > 0 ? Math.round((rev / grandTotal) * 100) : 0;
                              const isEmpty = !inp.rn && !inp.adr;
                              return (
                                <td key={sa.id} colSpan={2} style={{ padding: '8px 8px 4px', background: 'var(--fv3-n-50, #F8FAFC)', borderLeft: si === 0 ? '1px solid var(--fv3-n-100, #E2E8F0)' : '1px solid var(--fv3-n-50, #F1F5F9)', verticalAlign: 'top' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--fv3-n-600, #475569)' }}>{sa.accountName.split(' ')[0]}</span>
                                    {!isEmpty && <span style={{ fontSize: 9, fontWeight: 500, color: 'var(--fv3-n-400, #94A3B8)', background: 'var(--fv3-n-100, #EAECF0)', borderRadius: 3, padding: '1px 4px' }}>{pct}%</span>}
                                  </div>
                                </td>
                              );
                            })}
                            <td colSpan={rightCols} style={{ background: 'var(--fv3-n-50, #F8FAFC)' }}>
                              <div style={{ textAlign: 'right', paddingRight: 12, fontSize: 14, fontWeight: 700, color: 'var(--fv3-n-900, #0F172A)', fontVariantNumeric: 'tabular-nums' }}>{fmtDollar(grandTotal)}</div>
                            </td>
                          </tr>
                          {/* Card content row */}
                          <tr key={`${mo}-exp-totals-body`} style={{ background: 'var(--fv3-n-50, #F8FAFC)' }}>
                            <td colSpan={3} style={{ position: 'sticky', left: 0, zIndex: 5, background: 'var(--fv3-n-50, #F8FAFC)', padding: '0 0 10px 0', boxShadow: 'inset 3px 0 0 var(--fv3-accent, #F28C38)' }} />
                            <td style={{ position: 'sticky', left: 184, zIndex: 5, background: 'var(--fv3-n-50, #F8FAFC)', padding: '0 0 10px 0', boxShadow: '4px 0 6px -2px rgba(0,0,0,0.06)' }} />
                            <td colSpan={4} style={{ background: 'var(--fv3-n-50, #F8FAFC)', borderLeft: '1px solid var(--fv3-n-100, #E2E8F0)' }} />
                            {statAccounts.map((sa, si) => {
                              const inp = saInp[sa.id] ?? { rn: 0, adr: 0 };
                              const rev = Math.round(inp.rn * inp.adr * 100) / 100;
                              const pct = grandTotal > 0 ? Math.round((rev / grandTotal) * 100) : 0;
                              const isEmpty = !inp.rn && !inp.adr;
                              return (
                                <td key={sa.id} colSpan={2} style={{ padding: '0 8px 10px', background: 'var(--fv3-n-50, #F8FAFC)', borderLeft: si === 0 ? '1px solid var(--fv3-n-100, #E2E8F0)' : '1px solid var(--fv3-n-50, #F1F5F9)', verticalAlign: 'top' }}>
                                  <div style={{ background: 'var(--fv3-n-0, #FFFFFF)', borderRadius: 7, padding: '8px 10px', border: '1px solid var(--fv3-n-100, #EEF0F4)', minHeight: 72, display: 'flex', flexDirection: 'column', justifyContent: isEmpty ? 'center' : 'flex-start' }}>
                                    {isEmpty ? (
                                      <span style={{ fontSize: 13, color: 'var(--fv3-n-200, #D4D4D4)', textAlign: 'center' }}>—</span>
                                    ) : (
                                      <>
                                        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--fv3-n-900, #0F172A)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' }}>{fmtDollar(rev)}</div>
                                        <div style={{ display: 'flex', gap: 8, marginTop: 3 }}>
                                          <span style={{ fontSize: 11, color: 'var(--fv3-n-500, #64748B)' }}><span style={{ color: '#1A8754', fontWeight: 600 }}>{fmt(inp.rn)}</span> RN</span>
                                          <span style={{ fontSize: 11, color: 'var(--fv3-n-500, #64748B)' }}><span style={{ color: 'var(--fv3-accent-ink, #D97706)', fontWeight: 600 }}>{inp.adr > 0 ? `$${inp.adr.toFixed(2)}` : '—'}</span> ADR</span>
                                        </div>
                                        <div style={{ marginTop: 'auto', paddingTop: 5, height: 3, borderRadius: 2, background: 'var(--fv3-n-50, #F1F5F9)', overflow: 'hidden' }}>
                                          <div style={{ height: '100%', borderRadius: 2, background: pct > 30 ? 'var(--fv3-accent, #F28C38)' : pct > 10 ? 'var(--fv3-chart-secondary, #FDBA74)' : 'var(--fv3-chart-tertiary, #FDE68A)', width: `${Math.max(pct, 2)}%`, transition: 'width 0.3s' }} />
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </td>
                              );
                            })}
                            <td colSpan={rightCols} style={{ background: 'var(--fv3-n-50, #F8FAFC)' }} />
                          </tr>
                        </>
                      )}
                      </>
                      )}
                      {/* Orange bottom accent */}
                      <tr key={`${mo}-exp-bot`}>
                        <td colSpan={999} style={{ padding: 0, height: 3, background: 'linear-gradient(90deg, var(--fv3-accent, #F28C38), var(--fv3-accent-hover, #E8722A))' }} />
                      </tr>
                    </>
                  );
                })()}
                </React.Fragment>
              );
            })}
            {/* Totals row */}
            <tr className="fv3-foot-row bg-slate-50 border-t-2 border-slate-200 font-bold sticky bottom-0 z-[20]">
              <td className="px-3 py-2 text-slate-700 sticky left-0 z-[21] bg-slate-50">TOTAL</td>
              <td className="px-2 py-2 sticky left-16 z-[21] bg-slate-50" />
              <td className="px-2 py-2 sticky left-[144px] z-[21] bg-slate-50" />
              <td className="px-2 py-2 text-right text-slate-600 sticky left-[184px] z-[21] bg-slate-50" style={{ boxShadow: '4px 0 6px -2px rgba(0,0,0,0.07)' }}>{totalAvailRN > 0 ? fmt(totalAvailRN) : '—'}</td>
              {/* Reference year totals: LY actual when budgeting, OTB/RMS when forecasting */}
              {isBudgetStat ? (
                <>
                  <td className="px-2 py-2 text-center text-xs font-bold tabular-nums border-l" style={{ background: 'var(--fv3-n-50, #F5EFE1)', borderColor: 'var(--fv3-n-100, #E9E2D2)', color: lyYearTotals.rn > 0 ? 'var(--fv3-n-700, #6B5D42)' : 'var(--fv3-n-200, #CBD5E1)' }}>{lyYearTotals.rn > 0 ? fmt(lyYearTotals.rn) : '—'}</td>
                  <td className="px-2 py-2 text-center text-xs font-bold tabular-nums" style={{ background: 'var(--fv3-n-50, #F5EFE1)', color: lyYearTotals.adr > 0 ? 'var(--fv3-n-500, #8A7A5C)' : 'var(--fv3-n-200, #CBD5E1)' }}>{lyYearTotals.adr > 0 ? `$${lyYearTotals.adr.toFixed(0)}` : '—'}</td>
                  <td className="px-2 py-2 text-center text-xs font-bold tabular-nums border-l" style={{ background: 'var(--fv3-n-50, #F5EFE1)', borderColor: 'var(--fv3-n-100, #E9E2D2)', color: altYearTotals && altYearTotals.rn > 0 ? 'var(--fv3-n-700, #6B5D42)' : 'var(--fv3-n-200, #CBD5E1)' }}>{altYearTotals && altYearTotals.rn > 0 ? fmt(altYearTotals.rn) : '—'}</td>
                  <td className="px-2 py-2 text-center text-xs font-bold tabular-nums" style={{ background: 'var(--fv3-n-50, #F5EFE1)', color: altYearTotals && altYearTotals.adr > 0 ? 'var(--fv3-n-500, #8A7A5C)' : 'var(--fv3-n-200, #CBD5E1)' }}>{altYearTotals && altYearTotals.adr > 0 ? `$${altYearTotals.adr.toFixed(0)}` : '—'}</td>
                </>
              ) : (() => {
                const yr: DailyRefRow = { otbRooms: 0, otbRevenue: 0, rmsRooms: 0, rmsRevenue: 0 };
                for (let mo = 1; mo <= 12; mo++) {
                  const agg = monthRefTotal(mo);
                  yr.otbRooms += agg.otbRooms;
                  yr.otbRevenue += agg.otbRevenue;
                  yr.rmsRooms += agg.rmsRooms;
                  yr.rmsRevenue += agg.rmsRevenue;
                }
                const otbAdr = yr.otbRooms > 0 ? yr.otbRevenue / yr.otbRooms : 0;
                const rmsAdr = yr.rmsRooms > 0 ? yr.rmsRevenue / yr.rmsRooms : 0;
                return (
                  <>
                    <td className="px-2 py-2 text-center text-xs font-bold text-slate-700 tabular-nums border-l border-slate-200">{yr.otbRooms > 0 ? fmt(yr.otbRooms) : '—'}</td>
                    <td className="px-2 py-2 text-center text-xs font-bold text-slate-600 tabular-nums">{otbAdr > 0 ? `$${otbAdr.toFixed(0)}` : '—'}</td>
                    <td className="px-2 py-2 text-center text-xs font-bold text-slate-700 tabular-nums border-l border-slate-200">{yr.rmsRooms > 0 ? fmt(yr.rmsRooms) : '—'}</td>
                    <td className="px-2 py-2 text-center text-xs font-bold text-slate-600 tabular-nums">{rmsAdr > 0 ? `$${rmsAdr.toFixed(0)}` : '—'}</td>
                  </>
                );
              })()}
              {isStatMode ? saCols.map((col, i) => {
                const isRn = col.saField === 'rn';
                const saId = col.saId;
                const totalRN = saTotals[saId]?.rn ?? 0;
                const totalRev = Object.values(overviewData).reduce((s, r) => {
                  const inp = r.statAccountInputs?.[saId] ?? { rn: 0, adr: 0 };
                  return s + inp.rn * inp.adr;
                }, 0);
                const wADR = totalRN > 0 ? totalRev / totalRN : 0;
                // Fallback: average of non-zero monthly ADRs when no RN entered
                const avgADR = wADR > 0 ? wADR : (() => {
                  const adrs = Object.values(overviewData).map(r => r.statAccountInputs?.[saId]?.adr ?? 0).filter(a => a > 0);
                  return adrs.length > 0 ? adrs.reduce((s, a) => s + a, 0) / adrs.length : 0;
                })();
                return (
                  <td key={i} className={`px-1 py-2 text-center ${col.cls} ${col.bl ? 'border-l border-slate-200' : ''}`}>
                    {isRn
                      ? <span className="text-xs font-bold text-emerald-700 tabular-nums">{totalRN > 0 ? fmt(totalRN) : '—'}</span>
                      : <span className="text-xs font-bold text-blue-600 tabular-nums">{avgADR > 0 ? `$${avgADR.toFixed(2)}` : '—'}</span>
                    }
                  </td>
                );
              }) : (
                <>
                  <td className="px-2 py-2 text-right text-blue-600 border-l border-slate-200">{fmt(totalOccRN > 0 ? Object.values(overviewData).reduce((s, r) => s + r.groupRN, 0) : 0)}</td>
                  <td className="px-2 py-2" />
                  <td className="px-2 py-2 text-right text-orange-500 border-l border-slate-200">{fmt(totalOccRN > 0 ? Object.values(overviewData).reduce((s, r) => s + r.transRN, 0) : 0)}</td>
                  <td className="px-2 py-2" />
                </>
              )}
              <td className="px-2 py-2 text-right text-slate-800 border-l border-slate-200">{fmt(totalOccRN)}</td>
              <td className="px-2 py-2 text-right text-slate-700">{totalOccPct > 0 ? `${totalOccPct}%` : '—'}</td>
              <td className="px-2 py-2 text-right text-orange-600 border-l border-slate-200">{fmtDollar(totalRev)}</td>
              <td className="px-2 py-2" />
            </tr>
          </tbody>
        </table>
      </div>

      {/* Unlock prompt — shown when user clicks the lock badge */}
      {unlockPrompt && (() => {
        const sa = statAccounts.find(s => s.id === unlockPrompt.saId);
        const saName = sa?.accountName ?? 'this account';
        const mo = unlockPrompt.mo;
        return (
          <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/40 backdrop-blur-[2px]">
            <div className="bg-white rounded-xl shadow-2xl w-[380px] p-6 border border-slate-200">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg bg-blue-50 border border-blue-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span style={{ fontSize: 16 }}>🔒</span>
                </div>
                <div>
                  <div className="text-sm font-bold text-slate-900">Unlock Monthly Total?</div>
                  <div className="text-sm text-slate-500 mt-1 leading-snug">
                    <span className="font-semibold text-slate-700">{saName}</span> — {SHORT_MONTHS[mo - 1]} {year} has custom daily values.
                    Unlocking lets you edit the monthly total, which will spread it evenly across all days.
                  </div>
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setUnlockPrompt(null)}
                  className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Keep Daily Values
                </button>
                <button
                  onClick={() => {
                    unlockSaMonth(unlockPrompt.mo, unlockPrompt.saId);
                    setUnlockPrompt(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                  style={{ background: 'linear-gradient(135deg, var(--fv3-accent, #F28C38), var(--fv3-accent-hover, #E8722A))' }}
                >
                  Unlock & Edit Monthly
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
